import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate, downloadFile } from '../shared/api';
import { Breadcrumb } from '../shared/Breadcrumb';
import { DateInput } from '../shared/DateInput';
import { readUrlParam, useUrlSync, CopyLinkButton } from '../shared/urlState';
import { GoldCmPlannerPanel } from './goldCmPlannerPanel';
import { CashflowScheduleTab } from './cashflowSchedule';

const CGM_FALLBACK_SEGMENTS = ['poslovnice', 'web_registered', 'web_guest', 'slovenija', 'bugarska'] as const;

const GRAMS_PER_TROY_OZ = 31.1034768;

type CgmRule = {
    pct_on_spot: number;
    eur_on_spot: number;
    exceptions?: Array<{
        valid_from: string;
        valid_to?: string | null;
        pct_on_spot?: number;
        eur_on_spot?: number;
    }>;
};

/** Prodajno pravilo po kanalu + otkup (spread ili % ispod prodaje). */
type CgmSegmentRule = CgmRule & {
    buyback_spread_eur?: number;
    buyback_pct_below_sale?: number;
};

type PurchaseInputs = {
    premium_pct_on_metal: number;
    premium_eur: number;
    dependent_costs_eur: number;
};

type ProductPricingRow = {
    purchase: PurchaseInputs;
    segments: Record<string, CgmSegmentRule>;
};

type CgmPayload = {
    products: Record<string, ProductPricingRow>;
};

type PurchaseComputed = {
    metal_value_eur: number;
    premium_pct_on_metal: number;
    premium_from_pct_eur: number;
    premium_eur: number;
    dependent_costs_eur: number;
    total_purchase_cost_eur: number;
    nabavna_cijena_eur: number;
};

type IndexRow = {
    product: {
        id: number;
        slug: string;
        sku: string | null;
        name_hr: string;
        fine_weight_g: number | null;
        cgm_category?: string | null;
        cgm_warehouse?: string | null;
        cgm_delivery_type?: string | null;
        cgm_mint?: string | null;
        cgm_supplier?: string | null;
        current_sale_price_eur?: number | null;
        premium_poslovnica_eur?: number;
        premium_slovenija_eur?: number;
        premium_bugarska_eur?: number;
        poslovnica_price_eur?: number | null;
        slovenija_price_eur?: number | null;
        bugarska_price_eur?: number | null;
    };
    rules_by_segment: Record<string, CgmRule>;
    computed_sale_eur_by_segment: Record<string, number | null>;
    computed_buyback_eur_by_segment?: Record<string, number | null>;
    purchase_computed?: PurchaseComputed | null;
};

function defaultPurchase(): PurchaseInputs {
    return { premium_pct_on_metal: 0, premium_eur: 0, dependent_costs_eur: 0 };
}

function emptyRule(): CgmSegmentRule {
    return { pct_on_spot: 0, eur_on_spot: 0, exceptions: [] };
}

function cloneSegmentRule(r: CgmSegmentRule): CgmSegmentRule {
    return {
        pct_on_spot: r.pct_on_spot,
        eur_on_spot: r.eur_on_spot,
        exceptions: (r.exceptions ?? []).map((e) => ({ ...e })),
        buyback_spread_eur: r.buyback_spread_eur,
        buyback_pct_below_sale: r.buyback_pct_below_sale,
    };
}

/**
 * Heuristika za premium_eur (production cost) per komad, po fine_weight_g.
 * Mora ostati u sync s app/Http/Controllers/Api/GoldCmPlannerController::premiumEstimateEur().
 */
function premiumEstimateEur(fineG: number | null | undefined): number {
    const g = fineG ?? 0;
    if (g <= 0) return 0;
    if (g <= 1.5) return 12;
    if (g <= 3) return 15;
    if (g <= 7.5) return 20;
    if (g <= 12) return 28;
    if (g <= 22) return 40;
    if (g <= 33) return 65;
    if (g <= 55) return 70;
    if (g <= 110) return 110;
    if (g <= 260) return 200;
    if (g <= 510) return 320;
    return 500;
}

function computePurchaseLadder(spot: number, fineG: number | null | undefined, p: PurchaseInputs): PurchaseComputed | null {
    if (fineG === null || fineG === undefined || fineG <= 0) {
        return null;
    }
    const oz = fineG / GRAMS_PER_TROY_OZ;
    const metal = spot * oz;
    const premiumFromPct = metal * (p.premium_pct_on_metal / 100);
    const total = metal + premiumFromPct + p.premium_eur + p.dependent_costs_eur;

    return {
        metal_value_eur: Math.round(metal * 10000) / 10000,
        premium_pct_on_metal: p.premium_pct_on_metal,
        premium_from_pct_eur: Math.round(premiumFromPct * 10000) / 10000,
        premium_eur: Math.round(p.premium_eur * 10000) / 10000,
        dependent_costs_eur: Math.round(p.dependent_costs_eur * 10000) / 10000,
        total_purchase_cost_eur: Math.round(total * 10000) / 10000,
        nabavna_cijena_eur: Math.round(total * 10000) / 10000,
    };
}

function computeSaleEur(spot: number, fineG: number | null | undefined, rule: CgmRule): number | null {
    if (fineG === null || fineG === undefined || fineG <= 0) {
        return null;
    }
    const oz = fineG / GRAMS_PER_TROY_OZ;
    const metal = spot * oz;

    return metal * (1 + rule.pct_on_spot / 100) + rule.eur_on_spot;
}

function computeBuybackEur(saleEur: number | null, seg: CgmSegmentRule): number | null {
    if (saleEur === null || saleEur <= 0) {
        return null;
    }
    if (seg.buyback_spread_eur !== undefined && seg.buyback_spread_eur !== null && !Number.isNaN(seg.buyback_spread_eur)) {
        return Math.max(0, Math.round((saleEur - seg.buyback_spread_eur) * 10000) / 10000);
    }
    const p = seg.buyback_pct_below_sale;
    if (p !== undefined && p !== null && !Number.isNaN(p) && p >= 0 && p <= 100) {
        return Math.round(saleEur * (1 - p / 100) * 10000) / 10000;
    }

    return null;
}

function buildPayloadFromRows(
    rows: IndexRow[],
    revPayload: { products?: Record<string, unknown> } | null | undefined,
    segmentOrder: string[],
): CgmPayload {
    const products: CgmPayload['products'] = {};
    for (const row of rows) {
        const id = String(row.product.id);
        const segs: Record<string, CgmSegmentRule> = {};
        for (const seg of segmentOrder) {
            const base = (row.rules_by_segment[seg] as CgmSegmentRule | undefined) ?? emptyRule();
            segs[seg] = cloneSegmentRule(base);
        }
        products[id] = { purchase: defaultPurchase(), segments: segs };
    }
    if (!revPayload?.products) {
        return { products };
    }
    for (const [pid, raw] of Object.entries(revPayload.products)) {
        const r = raw as Record<string, unknown>;
        const purchaseIn = r.purchase;
        if (purchaseIn && typeof purchaseIn === 'object' && purchaseIn !== null) {
            const pi = purchaseIn as Record<string, unknown>;
            products[pid] = {
                ...products[pid],
                purchase: {
                    premium_pct_on_metal: Number(pi.premium_pct_on_metal ?? pi.nabavni_premium_pct_on_metal ?? 0),
                    premium_eur: Number(pi.premium_eur ?? pi.nabavni_premium_eur ?? 0),
                    dependent_costs_eur: Number(pi.dependent_costs_eur ?? pi.zavisni_troskovi_nabave_eur ?? 0),
                },
            };
        }
        if (r.segments && typeof r.segments === 'object' && r.segments !== null) {
            const merged: Record<string, CgmSegmentRule> = { ...(products[pid]?.segments ?? {}) };
            for (const [sk, sv] of Object.entries(r.segments as Record<string, CgmSegmentRule>)) {
                merged[sk] = cloneSegmentRule(sv);
            }
            products[pid] = { ...products[pid], segments: merged };
        } else {
            const flat = r as unknown as CgmRule;
            const segs: Record<string, CgmSegmentRule> = {};
            for (const seg of segmentOrder) {
                segs[seg] = cloneSegmentRule({
                    pct_on_spot: Number(flat.pct_on_spot ?? 0),
                    eur_on_spot: Number(flat.eur_on_spot ?? 0),
                    exceptions: flat.exceptions ?? [],
                });
            }
            products[pid] = { ...products[pid], segments: segs };
        }
    }

    return { products };
}

function segmentLabel(seg: string, locale: 'hr' | 'en'): string {
    const map: Record<string, { hr: string; en: string }> = {
        poslovnice: { hr: 'Poslovnice', en: 'Branches' },
        web_registered: { hr: 'WEB registrirani', en: 'WEB registered' },
        web_guest: { hr: 'WEB gost', en: 'WEB guest' },
        slovenija: { hr: 'Slovenija', en: 'Slovenia' },
        bugarska: { hr: 'Bugarska', en: 'Bulgaria' },
    };

    return map[seg]?.[locale] ?? seg;
}

function draftSegmentRuleFromState(
    productId: number,
    allRows: IndexRow[],
    payload: CgmPayload,
    activeSegment: string,
): CgmSegmentRule {
    const key = String(productId);
    const row = allRows.find((x) => x.product.id === productId);
    const cur = payload.products[key]?.segments?.[activeSegment];
    if (cur) {
        return cur;
    }

    return cloneSegmentRule((row?.rules_by_segment?.[activeSegment] as CgmSegmentRule | undefined) ?? emptyRule());
}

function sortRowsBySalePriceAsc(
    list: IndexRow[],
    args: {
        allRows: IndexRow[];
        payload: CgmPayload;
        activeSegment: string;
        spotPx: number | undefined;
        locale: 'hr' | 'en';
    },
): IndexRow[] {
    const { allRows, payload, activeSegment, spotPx, locale } = args;

    const saleForSort = (r: IndexRow): number => {
        const draft = draftSegmentRuleFromState(r.product.id, allRows, payload, activeSegment);
        const sale = spotPx !== undefined ? computeSaleEur(spotPx, r.product.fine_weight_g, draft) : null;
        if (sale === null || Number.isNaN(sale)) {
            return Number.POSITIVE_INFINITY;
        }

        return sale;
    };

    if (spotPx === undefined) {
        return [...list].sort((a, b) => a.product.name_hr.localeCompare(b.product.name_hr, locale === 'en' ? 'en-GB' : 'hr-HR'));
    }

    return [...list].sort((a, b) => {
        const da = saleForSort(a);
        const db = saleForSort(b);
        if (da !== db) {
            return da - db;
        }

        return a.product.name_hr.localeCompare(b.product.name_hr, locale === 'en' ? 'en-GB' : 'hr-HR');
    });
}

function formatEurAmount(value: number, locale: 'hr' | 'en'): string {
    return new Intl.NumberFormat(locale === 'en' ? 'en-GB' : 'hr-HR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
}

function formatEurMoneyCell(value: number | null, locale: 'hr' | 'en'): string {
    if (value === null || Number.isNaN(value)) {
        return '—';
    }

    return formatEurAmount(value, locale);
}

const moneyCellStyle: React.CSSProperties = {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
};

/** Per-produkt per-skladište min stock editor (LAGER pozicije) — inline u Cijene tab purchase panel. */
type MinStockRow = { id: number; product_id: number; warehouse_code: string; warehouse_name: string | null; min_qty: number; notes: string | null; updated_at: string };
type MinStockResp = { rows: MinStockRow[]; warehouses: Array<{ code: string; name: string }> };

function MinStockEditor({ productId, token, locale }: { productId: number; token: string; locale: 'hr' | 'en' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = React.useState<MinStockResp | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [adding, setAdding] = React.useState(false);
    const [draftWh, setDraftWh] = React.useState('');
    const [draftQty, setDraftQty] = React.useState('');
    const [draftNotes, setDraftNotes] = React.useState('');

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const r = await fetch(`/api/admin/product-min-stock?product_id=${productId}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            setData(await r.json());
        } catch (e) {
            console.error(e);
        } finally { setLoading(false); }
    }, [productId, token]);

    React.useEffect(() => { void load(); }, [load]);

    async function save() {
        if (!draftWh || !draftQty) { alert(t('Odaberi skladište i unesi količinu.', 'Pick warehouse and amount.')); return; }
        try {
            const r = await fetch('/api/admin/product-min-stock', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ product_id: productId, warehouse_code: draftWh, min_qty: parseFloat(draftQty), notes: draftNotes || null }),
            });
            if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
            setAdding(false); setDraftWh(''); setDraftQty(''); setDraftNotes('');
            await load();
        } catch (e) {
            alert(e instanceof Error ? e.message : 'API');
        }
    }

    async function updateQty(id: number, qty: number) {
        try {
            await fetch(`/api/admin/product-min-stock/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ min_qty: qty }) });
            await load();
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
    }

    async function remove(id: number) {
        if (!confirm(t('Obrisati ovu poziciju?', 'Delete this position?'))) return;
        try {
            await fetch(`/api/admin/product-min-stock/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
            await load();
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
    }

    if (loading || !data) return <p className="muted" style={{ marginTop: 8, fontSize: '0.78rem' }}>{t('Učitavam…', 'Loading…')}</p>;

    return (
        <div style={{ marginTop: 10 }}>
            <p className="muted" style={{ fontSize: '0.74rem', margin: '0 0 8px' }}>
                {t('Definira minimalni broj komada koje uvijek mora biti u skladištu. Plan isporuka uračunava popunjavanje lagera kao implicitne narudžbe.', 'Defines minimum quantity that must always be at this warehouse. Delivery plan treats lager refills as implicit orders.')}
            </p>
            {data.rows.length === 0 ? (
                <p className="muted" style={{ fontSize: '0.78rem', fontStyle: 'italic', marginBottom: 8 }}>{t('Nema definiranih lager pozicija.', 'No lager positions defined.')}</p>
            ) : (
                <table style={{ width: '100%', fontSize: '0.8rem', marginBottom: 8 }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.2)' }}>
                            <th style={{ textAlign: 'left', padding: '4px 8px' }}>{t('Skladište', 'Warehouse')}</th>
                            <th style={{ textAlign: 'right', padding: '4px 8px' }}>{t('Min. kol.', 'Min qty')}</th>
                            <th style={{ textAlign: 'left', padding: '4px 8px' }}>{t('Napomena', 'Notes')}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.rows.map((r) => (
                            <tr key={r.id} style={{ borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.1)' }}>
                                <td style={{ padding: '4px 8px' }}><strong>{r.warehouse_code}</strong> {r.warehouse_name ? <span className="muted" style={{ fontSize: '0.74rem' }}>· {r.warehouse_name}</span> : null}</td>
                                <td style={{ textAlign: 'right', padding: '4px 8px' }}>
                                    <input type="number" defaultValue={r.min_qty} step="1" min="0" onBlur={(e) => { const v = parseFloat(e.target.value); if (Number.isFinite(v) && v !== r.min_qty) void updateQty(r.id, v); }} style={{ width: 80, padding: '2px 6px', textAlign: 'right' }} />
                                </td>
                                <td className="muted" style={{ padding: '4px 8px', fontSize: '0.74rem' }}>{r.notes ?? '—'}</td>
                                <td><button type="button" className="secondary compact" style={{ fontSize: '0.7rem', padding: '2px 6px', color: '#b14040' }} onClick={() => void remove(r.id)}>🗑</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {adding ? (
                <div style={{ background: 'rgba(255,251,235,0.6)', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 6, padding: '8px 10px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <select value={draftWh} onChange={(e) => setDraftWh(e.target.value)} style={{ padding: '4px 8px', fontSize: '0.8rem' }}>
                        <option value="">— {t('Skladište', 'Warehouse')} —</option>
                        {data.warehouses.filter((w) => !data.rows.find((r) => r.warehouse_code === w.code)).map((w) => (
                            <option key={w.code} value={w.code}>{w.code} · {w.name}</option>
                        ))}
                    </select>
                    <input type="number" value={draftQty} onChange={(e) => setDraftQty(e.target.value)} placeholder={t('Min kol.', 'Min qty')} style={{ width: 90, padding: '4px 8px', fontSize: '0.8rem' }} />
                    <input type="text" value={draftNotes} onChange={(e) => setDraftNotes(e.target.value)} placeholder={t('Napomena (opcionalno)', 'Notes (optional)')} style={{ flex: 1, minWidth: 120, padding: '4px 8px', fontSize: '0.8rem' }} />
                    <button type="button" className="compact" onClick={() => void save()} style={{ fontSize: '0.78rem' }}>💾 {t('Spremi', 'Save')}</button>
                    <button type="button" className="secondary compact" onClick={() => { setAdding(false); setDraftWh(''); setDraftQty(''); setDraftNotes(''); }} style={{ fontSize: '0.78rem' }}>✗</button>
                </div>
            ) : (
                <button type="button" className="secondary compact" onClick={() => setAdding(true)} style={{ fontSize: '0.78rem' }}>+ {t('Dodaj lager poziciju', 'Add lager position')}</button>
            )}
        </div>
    );
}

/** Placeholder taba za buduće sub-modul. Linka na 40.5 roadmap. */
function PlaceholderTab({ locale, icon, title, description, etapaLabel }: { locale: 'hr' | 'en'; icon: string; title: string; description: string; etapaLabel: string }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    return (
        <div style={{
            padding: '40px 30px', textAlign: 'center',
            background: 'linear-gradient(135deg, #fffbeb 0%, rgba(254,243,199,0.4) 100%)',
            border: '2px dashed rgba(var(--ad-soft-rgb),0.4)', borderRadius: 14,
        }}>
            <div style={{ fontSize: '3rem', marginBottom: 10 }}>{icon}</div>
            <h3 style={{ margin: '0 0 8px', color: 'var(--ad-ink)' }}>{title}</h3>
            <p className="muted" style={{ maxWidth: 540, margin: '0 auto 12px', fontSize: '0.88rem', lineHeight: 1.5 }}>{description}</p>
            <p style={{ fontSize: '0.78rem', color: 'var(--ad-acc-3)', fontWeight: 600 }}>
                ⏳ {t('Planirano za:', 'Planned in:')} <strong>{etapaLabel}</strong>
            </p>
            <p style={{ fontSize: '0.74rem', color: 'rgba(0,0,0,0.5)', marginTop: 8 }}>
                {t('Detalji i status u', 'Details and status in')} <strong>m.40.5 Roadmap</strong>.
            </p>
        </div>
    );
}

function PravilaPlaceholderTab({ locale, token }: { locale: 'hr' | 'en'; token: string }) {
    return <PravilaMirrorTab locale={locale} token={token} />;
}

type DeliveryType = { id: number; name: string; days_min: number | null; days_max: number | null; sort_order: number };

function PravilaMirrorTab({ locale, token }: { locale: 'hr' | 'en'; token: string }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = React.useState<DeliveryType[] | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        let cancelled = false;
        async function run() {
            try {
                const r = await fetch('/api/admin/product-lookups/delivery_types', { headers: { Authorization: `Bearer ${token}` } });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const data = await r.json();
                if (!cancelled) setRows(data.rows as DeliveryType[]);
            } catch (e) {
                if (!cancelled) setError(e instanceof Error ? e.message : 'API');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        void run();
        return () => { cancelled = true; };
    }, [token]);

    if (loading) return <p className="muted">{t('Učitavam…', 'Loading…')}</p>;
    if (error) return <p className="error">{error}</p>;

    return (
        <section>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <h3 style={{ margin: 0, color: 'var(--ad-ink)' }}>📐 {t('Pravila isporuke — mirror m.110.2', 'Delivery rules — mirror m.110.2')}</h3>
                    <p className="muted" style={{ margin: '4px 0 0', fontSize: '0.82rem' }}>
                        🔗 {t('Source of truth: m.110.2 Rokovi isporuke (Administracija → Podaci → 110 Proizvodi). Ovdje je pregled — edit u 110.2.', 'Source of truth: m.110.2 Delivery types (Administration → Data → 110 Products). Read-only mirror here — edit in 110.2.')}
                    </p>
                </div>
                <p className="muted" style={{ fontSize: '0.78rem', fontStyle: 'italic' }}>
                    💡 {t('Sljedeće: Stock Allocation + Hedging thresholds u zasebnim tablicama.', 'Next: Stock Allocation + Hedging thresholds in separate tables.')}
                </p>
            </div>

            <div className="table-wrap">
                <table className="admin-user-table" style={{ fontSize: '0.86rem' }}>
                    <thead>
                        <tr>
                            <th>{t('Rok isporuke', 'Delivery type')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Min dana', 'Min days')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Max dana', 'Max days')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Sort', 'Sort')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows && rows.length > 0 ? rows.map((r) => (
                            <tr key={r.id}>
                                <td><strong>{r.name}</strong></td>
                                <td style={{ textAlign: 'right' }}>{r.days_min ?? '—'}</td>
                                <td style={{ textAlign: 'right' }}>{r.days_max ?? '—'}</td>
                                <td style={{ textAlign: 'right' }} className="muted">{r.sort_order}</td>
                            </tr>
                        )) : (
                            <tr><td colSpan={4} className="muted" style={{ textAlign: 'center', padding: '1.5rem', fontStyle: 'italic' }}>{t('Nema definiranih rokova isporuke. Otvori m.110.2 (Administracija → Podaci → 110 Proizvodi → 110.2 Rokovi isporuke) i unesi.', 'No delivery types yet. Open m.110.2 to add.')}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div style={{ marginTop: 14, background: 'linear-gradient(135deg, rgba(30,64,175,0.06) 0%, rgba(30,64,175,0.02) 100%)', border: '1px solid rgba(30,64,175,0.25)', borderRadius: 8, padding: '10px 14px' }}>
                <p style={{ margin: 0, fontSize: '0.82rem' }}>
                    ℹ️ {t(
                        'Ovi rokovi se koriste kao SLA u m.40.4 Plan isporuka (waterfall allocation): kratki rokovi (npr. POKLON 3d) prisiljavaju L0/L1 lokalnu zalihu; dugi rokovi (npr. INVESTICIJSKI 30d) dozvoljavaju L3 supplier order.',
                        'These deadlines drive SLA in m.40.4 Delivery plan (waterfall): short windows force L0/L1 local stock; long windows allow L3 supplier orders.',
                    )}
                </p>
            </div>
        </section>
    );
}


/**
 * PlannerFiltersPanel — A-G filteri (cjelovitost / starost / layer / payment / kategorija / poslovnica / vrijednost) + preset dropdown.
 */
function PlannerFiltersPanel({ filters, setFilters, presets, activePresetName, onLoadPreset, onSavePreset, onDeletePreset, locale, onExportCsv, availableMints = [] }: {
    filters: PlannerFilters;
    setFilters: (f: PlannerFilters) => void;
    presets: Array<{ id: number; name: string; filters: any; is_default: boolean }>;
    activePresetName: string;
    onLoadPreset: (name: string) => void;
    onSavePreset: (name: string, isDefault: boolean) => Promise<void>;
    onDeletePreset: (id: number, name: string) => Promise<void>;
    locale: 'hr' | 'en';
    onExportCsv?: () => void;
    availableMints?: string[];
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    // STAGED filteri — local state, primjenjuje se na parent tek na "Primijeni filter" klik.
    const [staged, setStaged] = React.useState<PlannerFilters>(filters);
    // Kad parent filters promijeni (npr. preset load), sinkroniziraj staged.
    React.useEffect(() => { setStaged(filters); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [JSON.stringify(filters)]);
    const dirty = JSON.stringify(staged) !== JSON.stringify(filters);

    const set = <K extends keyof PlannerFilters>(k: K, v: PlannerFilters[K]) => setStaged({ ...staged, [k]: v });
    const toggleLayer = (l: 'L0'|'L1'|'L2'|'L3') => {
        const cur = staged.allowed_layers ?? ['L0','L1','L2','L3'];
        set('allowed_layers', cur.includes(l) ? cur.filter((x) => x !== l) as any : [...cur, l] as any);
    };
    const layers = staged.allowed_layers ?? ['L0','L1','L2','L3'];

    return (
        <div style={{ background: '#fff8e7', border: '1.5px solid var(--ad-acc)', borderRadius: 8, padding: '14px 18px', marginBottom: 14 }}>
            {/* Preset dropdown */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: '0.84rem', color: 'var(--ad-ink)' }}>📋 {t('Preset', 'Preset')}:</strong>
                <select value={activePresetName} onChange={(e) => e.target.value && onLoadPreset(e.target.value)} style={{ padding: '4px 8px', fontSize: '0.82rem' }}>
                    <option value="">— {t('Manual filteri', 'Manual filters')} —</option>
                    {presets.map((p) => (
                        <option key={p.id} value={p.name}>{p.is_default ? '★ ' : ''}{p.name}</option>
                    ))}
                </select>
                <button type="button" onClick={() => {
                    const name = prompt(t('Naziv preseta:', 'Preset name:'), activePresetName);
                    if (!name) return;
                    const def = confirm(t('Postaviti kao DEFAULT (auto-load pri otvaranju)?', 'Set as DEFAULT (auto-load on open)?'));
                    void onSavePreset(name, def);
                }} className="secondary compact" style={{ fontSize: '0.74rem' }}>💾 {t('Spremi', 'Save')}</button>
                {activePresetName ? (
                    <button type="button" onClick={() => {
                        const p = presets.find((x) => x.name === activePresetName);
                        if (p) void onDeletePreset(p.id, p.name);
                    }} className="secondary compact" style={{ fontSize: '0.74rem', color: '#b14040' }}>🗑</button>
                ) : null}
                <button type="button" onClick={() => { const reset = { allowed_layers: ['L0','L1','L2','L3'] as ('L0'|'L1'|'L2'|'L3')[] }; setStaged(reset); setFilters(reset); }} className="secondary compact" style={{ fontSize: '0.74rem', marginLeft: 'auto' }}>↺ {t('Reset', 'Reset')}</button>
                {onExportCsv ? (
                    <button type="button" onClick={onExportCsv} className="secondary compact" style={{ fontSize: '0.74rem', background: '#1f7a36', color: '#fff', borderColor: '#1f7a36' }}>
                        📥 {t('Export XLSX (trenutni rezultati)', 'Export XLSX (current results)')}
                    </button>
                ) : null}
                <button type="button" onClick={() => setFilters(staged)} disabled={! dirty}
                    style={{
                        fontSize: '0.78rem',
                        background: dirty ? 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))' : '#ccc',
                        color: '#fff',
                        padding: '5px 12px',
                        borderRadius: 6,
                        border: 'none',
                        cursor: dirty ? 'pointer' : 'default',
                        fontWeight: 700,
                    }}>
                    ✓ {t('Primijeni filter', 'Apply filter')}{dirty ? ' ●' : ''}
                </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
                {/* A — Cjelovitost */}
                <fieldset style={{ border: '1px solid #d9c98a', borderRadius: 6, padding: '8px 12px' }}>
                    <legend style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--ad-ink)', padding: '0 6px' }}>A · {t('Cjelovitost', 'Completeness')}</legend>
                    <label style={{ fontSize: '0.82rem', display: 'block', marginBottom: 4 }}>
                        <input type="checkbox" checked={!!staged.only_full_orders} onChange={(e) => set('only_full_orders', e.target.checked)} /> {t('Samo cijele narudžbe (100% L0+L1+L2)', 'Only full orders (100% L0+L1+L2)')}
                    </label>
                    <label style={{ fontSize: '0.82rem', display: 'block' }}>
                        {t('Min pokrivenost %', 'Min coverage %')}:
                        <input type="number" min="0" max="100" step="5" value={staged.min_coverage_pct ?? ''} onChange={(e) => set('min_coverage_pct', e.target.value === '' ? undefined : parseFloat(e.target.value))} placeholder="0" style={{ width: 60, marginLeft: 6, padding: '2px 4px' }} />
                    </label>
                </fieldset>

                {/* B — Starost / SLA */}
                <fieldset style={{ border: '1px solid #d9c98a', borderRadius: 6, padding: '8px 12px' }}>
                    <legend style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--ad-ink)', padding: '0 6px' }}>B · {t('Starost / SLA', 'Age / SLA')}</legend>
                    <label style={{ fontSize: '0.82rem', display: 'block', marginBottom: 4 }}>
                        {t('Min staro (dana)', 'Min age (days)')}:
                        <input type="number" min="0" step="1" value={staged.min_age_days ?? ''} onChange={(e) => set('min_age_days', e.target.value === '' ? undefined : parseInt(e.target.value))} placeholder="0" style={{ width: 60, marginLeft: 6, padding: '2px 4px' }} />
                    </label>
                    <label style={{ fontSize: '0.82rem', display: 'block', marginBottom: 4 }}>
                        {t('Max staro (dana)', 'Max age (days)')}:
                        <input type="number" min="0" step="1" value={staged.max_age_days ?? ''} onChange={(e) => set('max_age_days', e.target.value === '' ? undefined : parseInt(e.target.value))} placeholder="∞" style={{ width: 60, marginLeft: 6, padding: '2px 4px' }} />
                    </label>
                    <label style={{ fontSize: '0.82rem', display: 'block', marginBottom: 2 }}>
                        <input type="checkbox" checked={!!staged.sla_breach_only} onChange={(e) => set('sla_breach_only', e.target.checked)} /> ⚠ {t('Samo SLA breach', 'SLA breach only')}
                    </label>
                    <label style={{ fontSize: '0.82rem', display: 'block' }}>
                        <input type="checkbox" checked={!!staged.deliver_this_week} onChange={(e) => set('deliver_this_week', e.target.checked)} /> {t('Dispatchable ovaj tjedan (SLA ≤7d)', 'Dispatchable this week (SLA ≤7d)')}
                    </label>
                </fieldset>

                {/* C — Layer */}
                <fieldset style={{ border: '1px solid #d9c98a', borderRadius: 6, padding: '8px 12px' }}>
                    <legend style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--ad-ink)', padding: '0 6px' }}>C · {t('Layer pravila', 'Layer rules')}</legend>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {(['L0','L1','L2','L3'] as const).map((l) => {
                            const on = layers.includes(l);
                            const bg = { L0: '#1f7a36', L1: '#1e40af', L2: 'var(--ad-acc-3)', L3: '#b14040' }[l];
                            return (
                                <button key={l} type="button" onClick={() => toggleLayer(l)} style={{ background: on ? bg : '#eee', color: on ? '#fff' : '#888', padding: '4px 10px', borderRadius: 4, border: 'none', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
                                    {on ? '✓ ' : ''}{l}
                                </button>
                            );
                        })}
                    </div>
                    <label style={{ fontSize: '0.78rem', display: 'block', marginTop: 6 }}>
                        {t('Min RUC % (narudžba)', 'Min GM % (order)')}:
                        <input type="number" step="0.5" value={staged.min_ruc_pct ?? ''} onChange={(e) => set('min_ruc_pct', e.target.value === '' ? undefined : parseFloat(e.target.value))} placeholder="—" style={{ width: 60, marginLeft: 6, padding: '2px 4px' }} />
                    </label>
                </fieldset>

                {/* D — Plaćanje */}
                <fieldset style={{ border: '1px solid #d9c98a', borderRadius: 6, padding: '8px 12px' }}>
                    <legend style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--ad-ink)', padding: '0 6px' }}>D · {t('Plaćanje', 'Payment')}</legend>
                    <label style={{ fontSize: '0.82rem', display: 'block', marginBottom: 4 }}>
                        <input type="checkbox" checked={!!staged.only_paid_full} onChange={(e) => set('only_paid_full', e.target.checked)} /> 💰 {t('Samo paid_full', 'Paid in full only')}
                    </label>
                    <label style={{ fontSize: '0.82rem', display: 'block' }}>
                        {t('Min avans %', 'Min advance %')}:
                        <input type="number" min="0" max="100" step="5" value={staged.min_advance_pct ?? ''} onChange={(e) => set('min_advance_pct', e.target.value === '' ? undefined : parseFloat(e.target.value))} placeholder="0" style={{ width: 60, marginLeft: 6, padding: '2px 4px' }} />
                    </label>
                </fieldset>

                {/* E — Kategorija */}
                <fieldset style={{ border: '1px solid #d9c98a', borderRadius: 6, padding: '8px 12px' }}>
                    <legend style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--ad-ink)', padding: '0 6px' }}>E · {t('Kategorija', 'Category')}</legend>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {['KOLEKCIONARSKI','POKLON','INVESTICIJSKI MALI','INVESTICIJSKI VELIKI','INVESTICIJSKI EGZOTIKA'].map((c) => {
                            const sel = staged.categories?.includes(c) ?? false;
                            return (
                                <button key={c} type="button" onClick={() => {
                                    const cur = staged.categories ?? [];
                                    set('categories', sel ? cur.filter((x) => x !== c) : [...cur, c]);
                                }} style={{ background: sel ? 'var(--ad-acc)' : '#fff', color: sel ? '#fff' : '#666', padding: '2px 6px', borderRadius: 3, border: '1px solid #d9c98a', fontSize: '0.7rem', cursor: 'pointer' }}>
                                    {sel ? '✓' : ''}{c.replace('INVESTICIJSKI ', 'INV ')}
                                </button>
                            );
                        })}
                    </div>
                    <label style={{ fontSize: '0.78rem', display: 'block', marginTop: 6 }}>
                        <input type="checkbox" checked={!!staged.exclude_egzotika} onChange={(e) => set('exclude_egzotika', e.target.checked)} /> {t('Isključi EGZOTIKU', 'Exclude EGZOTIKA')}
                    </label>
                </fieldset>

                {/* F — Poslovnica / region */}
                <fieldset style={{ border: '1px solid #d9c98a', borderRadius: 6, padding: '8px 12px' }}>
                    <legend style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--ad-ink)', padding: '0 6px' }}>F · {t('Poslovnica / region', 'Branch / region')}</legend>
                    <label style={{ fontSize: '0.78rem', display: 'block' }}>
                        {t('Poslovnice (CSV)', 'Branches (CSV)')}:
                        <input type="text" value={(staged.branches ?? []).join(',')} onChange={(e) => set('branches', e.target.value ? e.target.value.split(',').map((x) => x.trim()).filter(Boolean) : undefined)} placeholder="064,041,068" style={{ width: 160, marginLeft: 4, padding: '2px 4px' }} />
                    </label>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                        {['Centar','Sjever','Istok','Jug'].map((r) => {
                            const sel = staged.regions?.includes(r) ?? false;
                            return (
                                <button key={r} type="button" onClick={() => {
                                    const cur = staged.regions ?? [];
                                    set('regions', sel ? cur.filter((x) => x !== r) : [...cur, r]);
                                }} style={{ background: sel ? 'var(--ad-acc)' : '#fff', color: sel ? '#fff' : '#666', padding: '2px 8px', borderRadius: 3, border: '1px solid #d9c98a', fontSize: '0.74rem', cursor: 'pointer' }}>
                                    {sel ? '✓ ' : ''}{r}
                                </button>
                            );
                        })}
                    </div>
                </fieldset>

                {/* G — Vrijednost */}
                <fieldset style={{ border: '1px solid #d9c98a', borderRadius: 6, padding: '8px 12px' }}>
                    <legend style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--ad-ink)', padding: '0 6px' }}>G · {t('Vrijednost', 'Value')}</legend>
                    <label style={{ fontSize: '0.82rem', display: 'block', marginBottom: 4 }}>
                        {t('Min EUR narudžbe', 'Min EUR order')}:
                        <input type="number" min="0" step="100" value={staged.min_eur ?? ''} onChange={(e) => set('min_eur', e.target.value === '' ? undefined : parseFloat(e.target.value))} placeholder="0" style={{ width: 90, marginLeft: 4, padding: '2px 4px' }} />
                    </label>
                    <label style={{ fontSize: '0.82rem', display: 'block' }}>
                        {t('Max EUR narudžbe', 'Max EUR order')}:
                        <input type="number" min="0" step="100" value={staged.max_eur ?? ''} onChange={(e) => set('max_eur', e.target.value === '' ? undefined : parseFloat(e.target.value))} placeholder="∞" style={{ width: 90, marginLeft: 4, padding: '2px 4px' }} />
                    </label>
                    <label style={{ fontSize: '0.82rem', display: 'block', marginTop: 6, borderTop: '1px dashed #d9c98a', paddingTop: 6 }} title={t('Najmanji iznos jedne pošiljke (Plan preskladištenja) — manja preskladi­štenja ispod ovog praga se ne prikazuju u PDF-u.', 'Smallest shipment value (Restocking plan) — smaller transfers hidden from PDF.')}>
                        📦 {t('Min € preskladištenje', 'Min € transfer')}:
                        <input type="number" min="0" step="100" value={staged.min_transfer_eur ?? ''} onChange={(e) => set('min_transfer_eur', e.target.value === '' ? undefined : parseFloat(e.target.value))} placeholder="0" style={{ width: 90, marginLeft: 4, padding: '2px 4px' }} />
                    </label>
                </fieldset>

                {/* H — Strategija (dodatni filteri za Plan / Generate) */}
                <fieldset style={{ border: '1px solid #d9c98a', borderRadius: 6, padding: '8px 12px' }}>
                    <legend style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--ad-ink)', padding: '0 6px' }}>H · {t('Strategija', 'Strategy')}</legend>
                    <label style={{ fontSize: '0.82rem', display: 'block', marginBottom: 4 }} title={t('Sortira kandidate da unutar budžeta zatvori MAX broj narudžbi (jeftinije prvo) umjesto FIFO.', 'Sort candidates to close MAX orders within budget (cheapest first) instead of FIFO.')}>
                        <input type="checkbox" checked={!!staged.prioritize_max_orders} onChange={(e) => set('prioritize_max_orders', e.target.checked)} /> ⭐ {t('Prioritetni — zatvori max broj narudžbi unutar budžeta', 'Priority — close max orders within budget')}
                    </label>
                    {staged.prioritize_max_orders ? (
                        <label style={{ fontSize: '0.82rem', display: 'block', marginBottom: 4, marginLeft: 20 }} title={t('Ukupni budžet za zatvaranje narudžbi (estimirano nabavni trošak). Server bira najjeftinije narudžbe redom dok ne ostanemo bez para.', 'Total budget to close orders (estimated cost). Server picks cheapest orders until budget runs out.')}>
                            💰 {t('Budžet', 'Budget')} €:
                            <input type="number" min="0" step="500" value={staged.budget_eur ?? ''} onChange={(e) => set('budget_eur', e.target.value === '' ? undefined : parseFloat(e.target.value))} placeholder="50000" style={{ width: 110, marginLeft: 6, padding: '2px 4px' }} />
                        </label>
                    ) : null}

                    {/* Kovnice + budžet po kovnici — naruči SAMO od odabranih, do zadanog EUR iznosa (FIFO). */}
                    {availableMints.length > 0 ? (
                        <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px dashed rgba(var(--ad-soft-rgb),0.3)' }}>
                            <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ad-ink)', fontWeight: 700, marginBottom: 2 }} title={t('Označi kovnice od kojih želiš naručiti; upiši EUR limit po kovnici (prazno = neograničeno). Server reže nabavu te kovnice do iznosa, najstarije narudžbe prve.', 'Check mints to order from; set EUR limit per mint (empty = unlimited). Server caps that mint’s procurement, oldest orders first.')}>
                                🏭 {t('Kovnice + budžet po kovnici', 'Mints + budget per mint')}
                            </div>
                            <div style={{ fontSize: '0.68rem', color: '#888', marginBottom: 4 }}>{t('Bez ijedne označene = sve kovnice. EUR prazno = neograničeno.', 'None checked = all mints. Empty EUR = unlimited.')}</div>
                            <div style={{ maxHeight: 170, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {availableMints.map((m) => {
                                    const active = (staged.mints ?? []).includes(m);
                                    return (
                                        <label key={m} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem' }}>
                                            <input type="checkbox" checked={active} onChange={(e) => {
                                                const cur = staged.mints ?? [];
                                                const nextMints = e.target.checked ? [...cur, m] : cur.filter((x) => x !== m);
                                                set('mints', nextMints.length ? nextMints : undefined);
                                                if (!e.target.checked) {
                                                    const nb = { ...(staged.mint_budgets ?? {}) };
                                                    delete nb[m];
                                                    set('mint_budgets', Object.keys(nb).length ? nb : undefined);
                                                }
                                            }} />
                                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m}</span>
                                            <input type="number" min="0" step="500" disabled={!active}
                                                value={staged.mint_budgets?.[m] ?? ''}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    const nb = { ...(staged.mint_budgets ?? {}) };
                                                    if (v === '') delete nb[m]; else nb[m] = parseFloat(v);
                                                    set('mint_budgets', Object.keys(nb).length ? nb : undefined);
                                                }}
                                                placeholder="∞" title={t('EUR limit za ovu kovnicu', 'EUR limit for this mint')}
                                                style={{ width: 84, padding: '2px 4px', opacity: active ? 1 : 0.4 }} /> €
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}
                    <label style={{ fontSize: '0.82rem', display: 'block' }} title={t('Prikazuje samo narudžbe koje sadrže BOTH Au i Ag', 'Show only orders containing BOTH Au and Ag')}>
                        <input type="checkbox" checked={!!staged.mixed_only} onChange={(e) => set('mixed_only', e.target.checked)} /> 🥇🥈 {t('Samo miješane narudžbe (Au+Ag)', 'Mixed only (Au+Ag)')}
                    </label>
                    <label style={{ fontSize: '0.82rem', display: 'block', marginTop: 4 }} title={t('Samo narudžbe koje imaju "dogovoren_datum" oznaku <= ovaj datum (postavljeno u m.40.3). Prazno = svi.', 'Only orders with "dogovoren_datum" annotation <= this date (set in m.40.3). Empty = all.')}>
                        📅 {t('Isporučiti do', 'Deliver by')}:
                        <input type="text" placeholder="dd.mm.gggg" value={staged.delivery_by_date ? (() => { const [y,m,d] = staged.delivery_by_date.split('-'); return `${d}.${m}.${y}`; })() : ''} onChange={(e) => {
                            const v = e.target.value.trim();
                            if (v === '') { set('delivery_by_date', undefined); return; }
                            const m = v.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
                            if (m) {
                                const iso = `${m[3]}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}`;
                                set('delivery_by_date', iso);
                            } else {
                                set('delivery_by_date', v); // partial, allow user to keep typing
                            }
                        }} style={{ width: 110, marginLeft: 6, padding: '2px 4px' }} />
                    </label>

                    {/* ── Oznake iz m.40.3 (default ON; isključi da plan ignorira) ── */}
                    <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px dashed rgba(var(--ad-soft-rgb),0.3)' }}>
                        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ad-ink)', fontWeight: 700, marginBottom: 3 }}>{t('Oznake iz m.40.3', 'm.40.3 flags')}</div>
                        <label style={{ fontSize: '0.82rem', display: 'block' }} title={t('Izuzmi "smješteno" narudžbe iz preskladištenja (već fizički rezervirane).', 'Exclude "in stock" orders from restocking.')}>
                            <input type="checkbox" checked={staged.respect_imam_ju !== false} onChange={(e) => set('respect_imam_ju', e.target.checked)} /> ✋ {t('Izuzmi "Smješteno"', 'Exclude "In stock"')}
                        </label>
                        <label style={{ fontSize: '0.82rem', display: 'block' }} title={t('Hitne narudžbe prioritetne u planu.', 'Urgent orders prioritized.')}>
                            <input type="checkbox" checked={staged.prioritize_hitno !== false} onChange={(e) => set('prioritize_hitno', e.target.checked)} /> 🚨 {t('Prioritiziraj "Hitno"', 'Prioritize "Urgent"')}
                        </label>
                        <label style={{ fontSize: '0.82rem', display: 'block' }} title={t('Poštuj dogovoreni datum isporuke u redoslijedu.', 'Respect agreed delivery date in ordering.')}>
                            <input type="checkbox" checked={staged.respect_agreed_date !== false} onChange={(e) => set('respect_agreed_date', e.target.checked)} /> 📅 {t('Poštuj "Dogovoren datum"', 'Respect "Agreed date"')}
                        </label>
                        <div style={{ fontSize: '0.78rem', marginTop: 4 }}>🚚 {t('Način isporuke', 'Delivery method')}:</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {([['u_poslovnici', t('U poslovnici', 'At branch')], ['vozac', t('Vozač', 'Driver')], ['gls_posta', 'GLS'], ['centralno', t('Centralno', 'Central')]] as const).map(([val, lbl]) => {
                                const cur = staged.nacin_isporuke ?? [];
                                return (
                                    <label key={val} style={{ fontSize: '0.8rem' }}>
                                        <input type="checkbox" checked={cur.includes(val)} onChange={(e) => set('nacin_isporuke', (e.target.checked ? [...cur, val] : cur.filter((x) => x !== val)) as any)} /> {lbl}
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                </fieldset>
            </div>

            {staged.order_numbers && staged.order_numbers.length > 0 ? (
                <div style={{ marginTop: 10, padding: '6px 10px', background: 'rgba(30,64,175,0.08)', borderRadius: 4, fontSize: '0.78rem' }}>
                    📌 {t('Eksplicitni odabir narudžbi', 'Explicit order selection')}: <strong>{staged.order_numbers.length}</strong> · ostali filteri se primjenjuju nakon
                    <button type="button" onClick={() => set('order_numbers', undefined)} className="secondary compact" style={{ marginLeft: 8, fontSize: '0.72rem' }}>✕ {t('Očisti', 'Clear')}</button>
                </div>
            ) : null}
        </div>
    );
}

/**
 * RUC Drift Report (Priority A) — top-10 negativnih + per-layer + per-supplier breakdown.
 * Pokazuje koliko nam stvarni cost (actual_cost_eur) odstupa od expected (CGM premium estimate).
 */
function RucDriftTab({ locale, token }: { locale: 'hr' | 'en'; token: string }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [period, setPeriod] = React.useState<number>(30);
    const [data, setData] = React.useState<any>(null);
    const [pending, setPending] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [actualEdits, setActualEdits] = React.useState<Record<number, string>>({});

    const load = React.useCallback(async () => {
        setLoading(true); setError('');
        try {
            const [r, p] = await Promise.all([
                fetch(`/api/gold-cm-planner/delivery-plan/ruc-drift?days=${period}`, { headers: { Authorization: `Bearer ${token}` } }),
                fetch(`/api/gold-cm-planner/delivery-plan/pending-actuals`, { headers: { Authorization: `Bearer ${token}` } }),
            ]);
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            setData(await r.json());
            if (p.ok) {
                const pd = await p.json();
                setPending(pd.rows ?? []);
            }
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setLoading(false);
        }
    }, [period, token]);
    React.useEffect(() => { void load(); }, [load]);

    async function saveActual(id: number) {
        const v = parseFloat(actualEdits[id] ?? '');
        if (!isFinite(v) || v < 0) { alert(t('Unesi pozitivan iznos', 'Enter positive amount')); return; }
        const r = await fetch(`/api/gold-cm-planner/delivery-allocations/${id}/actuals`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ actual_cost_eur: v }),
        });
        if (r.ok) {
            await load();
        } else {
            alert(t('Greška', 'Error'));
        }
    }

    if (loading && !data) return <p className="muted">{t('Učitavam…', 'Loading…')}</p>;
    if (error) return <p className="error">{error}</p>;
    if (!data) return null;

    const total = data.total || {};
    const fmt = (n: number | null | undefined, dp = 2) => n == null ? '—' : Number(n).toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { minimumFractionDigits: dp, maximumFractionDigits: dp });

    return (
        <section>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <h3 style={{ margin: 0, color: 'var(--ad-ink)' }}>💰 {t('RUC Drift Report', 'RUC Drift Report')}</h3>
                    <p className="muted" style={{ margin: '4px 0 0', fontSize: '0.82rem' }}>
                        {t('Razlika između očekivane RUC (pri Live Offer accept) i stvarne RUC (nakon operater unese actual cost).',
                          'Diff between expected RUC (at Live Offer accept) and realized RUC (after operator enters actual cost).')}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                    {[30, 90, 180].map((p) => (
                        <button key={p} type="button" onClick={() => setPeriod(p)} className={period === p ? '' : 'secondary compact'} style={{ fontSize: '0.78rem' }}>
                            {p}d
                        </button>
                    ))}
                    <button type="button" onClick={() => void load()} className="secondary compact" style={{ fontSize: '0.78rem' }}>🔁</button>
                </div>
            </div>

            <p className="muted" style={{ fontSize: '0.72rem', marginBottom: 12 }}>
                ℹ️ {t('Cutoff', 'Cutoff')}: {data.cutoff_date} · {total.cnt ?? 0} {t('alokacija sa actuals', 'allocations with actuals')}
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 16 }}>
                <KpiCard label={t('Σ Expected', 'Σ Expected')} value={fmt(total.expected, 0) + ' €'} accent="#1e40af" />
                <KpiCard label={t('Σ Realized', 'Σ Realized')} value={fmt(total.realized, 0) + ' €'} accent="#1f7a36" />
                <KpiCard label={t('Σ Drift', 'Σ Drift')} value={(Number(total.drift) >= 0 ? '+' : '') + fmt(total.drift, 0) + ' €'} accent={Number(total.drift) >= 0 ? '#1f7a36' : '#b14040'} highlight={Number(total.drift) < 0} />
                <KpiCard label={t('Negativnih / SLA breach', 'Negative / SLA breach')} value={`${total.negative_count ?? 0} / ${total.sla_breach_count ?? 0}`} accent="#b14040" />
            </div>

            {pending.length > 0 ? (
                <>
                    <h4 style={{ margin: '14px 0 6px', fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#1e40af' }}>
                        ✏ {t('Pending actuals — unesi stvarni cost (nakon isporuke)', 'Pending actuals — enter actual cost (after delivery)')}
                        <span style={{ fontSize: '0.7rem', color: '#b14040', marginLeft: 8 }}>· {pending.length} {t('alokacija čeka unos', 'awaiting entry')}</span>
                    </h4>
                    <div className="table-wrap" style={{ marginBottom: 16, maxHeight: 320, overflowY: 'auto' }}>
                        <table className="admin-user-table" style={{ fontSize: '0.78rem' }}>
                            <thead style={{ background: 'rgba(30,64,175,0.05)' }}>
                                <tr>
                                    <th>{t('Plan datum', 'Plan date')}</th>
                                    <th>{t('Narudžba', 'Order')}</th>
                                    <th>{t('Artikl', 'Item')}</th>
                                    <th>Layer</th>
                                    <th style={{ textAlign: 'right' }}>{t('Kol.', 'Qty')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Expected unit cost', 'Expected unit cost')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Actual TOTAL €', 'Actual TOTAL €')}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {pending.map((a: any) => (
                                    <tr key={a.id}>
                                        <td className="muted" style={{ fontSize: '0.72rem' }}>{a.plan_date}</td>
                                        <td style={{ fontSize: '0.72rem' }}>{a.order_number}</td>
                                        <td>{a.item_name?.slice(0, 40)}</td>
                                        <td><strong>{a.source_layer}</strong></td>
                                        <td style={{ textAlign: 'right' }}>{a.qty}</td>
                                        <td style={{ textAlign: 'right' }} className="muted">{Number(a.source_unit_cost_eur).toFixed(2)} €</td>
                                        <td style={{ textAlign: 'right' }}>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={actualEdits[a.id] ?? ''}
                                                placeholder={(Number(a.source_unit_cost_eur) * Number(a.qty)).toFixed(2)}
                                                onChange={(e) => setActualEdits({ ...actualEdits, [a.id]: e.target.value })}
                                                style={{ width: 100, padding: '3px 6px', textAlign: 'right', fontSize: '0.78rem' }}
                                            />
                                        </td>
                                        <td>
                                            <button type="button" className="compact" disabled={!actualEdits[a.id]} onClick={() => void saveActual(a.id)} style={{ fontSize: '0.74rem' }}>
                                                ✓ {t('Spremi', 'Save')}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : null}

            <h4 style={{ margin: '14px 0 6px', fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>{t('Po layeru (L0/L1/L2/L3)', 'By layer')}</h4>
            <div className="table-wrap" style={{ marginBottom: 14 }}>
                <table className="admin-user-table" style={{ fontSize: '0.84rem' }}>
                    <thead>
                        <tr>
                            <th>Layer</th>
                            <th style={{ textAlign: 'right' }}>{t('Cnt', 'Cnt')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Expected', 'Expected')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Realized', 'Realized')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Drift €', 'Drift €')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Drift %', 'Drift %')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(data.per_layer ?? []).map((r: any) => (
                            <tr key={r.source_layer}>
                                <td><strong>{r.source_layer}</strong></td>
                                <td style={{ textAlign: 'right' }}>{r.cnt}</td>
                                <td style={{ textAlign: 'right' }}>{fmt(r.expected, 0)} €</td>
                                <td style={{ textAlign: 'right' }}>{fmt(r.realized, 0)} €</td>
                                <td style={{ textAlign: 'right', color: Number(r.drift) >= 0 ? '#1f7a36' : '#b14040', fontWeight: 700 }}>{fmt(r.drift, 0)} €</td>
                                <td style={{ textAlign: 'right', color: Number(r.drift_pct) >= 0 ? '#1f7a36' : '#b14040' }}>{Number(r.drift_pct).toFixed(1)}%</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <h4 style={{ margin: '14px 0 6px', fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>{t('Po dobavljaču (L3)', 'By supplier (L3)')}</h4>
            <div className="table-wrap" style={{ marginBottom: 14 }}>
                <table className="admin-user-table" style={{ fontSize: '0.84rem' }}>
                    <thead>
                        <tr>
                            <th>{t('Dobavljač', 'Supplier')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Cnt', 'Cnt')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Expected', 'Expected')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Realized', 'Realized')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Drift €', 'Drift €')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(data.per_supplier ?? []).map((r: any) => (
                            <tr key={r.source_supplier_name}>
                                <td>{r.source_supplier_name}</td>
                                <td style={{ textAlign: 'right' }}>{r.cnt}</td>
                                <td style={{ textAlign: 'right' }}>{fmt(r.expected, 0)} €</td>
                                <td style={{ textAlign: 'right' }}>{fmt(r.realized, 0)} €</td>
                                <td style={{ textAlign: 'right', color: Number(r.drift) >= 0 ? '#1f7a36' : '#b14040', fontWeight: 700 }}>{fmt(r.drift, 0)} €</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <h4 style={{ margin: '14px 0 6px', fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#b14040' }}>⚠ {t('TOP-10 najgore (action list)', 'TOP-10 worst (action list)')}</h4>
            <div className="table-wrap">
                <table className="admin-user-table" style={{ fontSize: '0.78rem' }}>
                    <thead>
                        <tr>
                            <th>{t('Plan', 'Plan')}</th>
                            <th>{t('Narudžba', 'Order')}</th>
                            <th>{t('Partner', 'Partner')}</th>
                            <th>{t('Artikl', 'Item')}</th>
                            <th>Layer</th>
                            <th>{t('Dobavljač', 'Supplier')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Expected', 'Expected')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Realized', 'Realized')}</th>
                            <th style={{ textAlign: 'right' }}>Drift</th>
                            <th>SLA</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(data.worst_10 ?? []).map((r: any) => (
                            <tr key={r.id}>
                                <td className="muted" style={{ fontSize: '0.72rem' }}>{r.plan_date}</td>
                                <td style={{ fontSize: '0.72rem' }}>{r.order_number}</td>
                                <td>{r.partner_name?.slice(0, 25)}</td>
                                <td>{r.item_name?.slice(0, 30)}</td>
                                <td><strong>{r.source_layer}</strong></td>
                                <td style={{ fontSize: '0.72rem' }}>{r.source_supplier_name ?? '—'}</td>
                                <td style={{ textAlign: 'right' }}>{fmt(r.expected_ruc_eur, 0)} €</td>
                                <td style={{ textAlign: 'right' }}>{fmt(r.realized_ruc_eur, 0)} €</td>
                                <td style={{ textAlign: 'right', color: '#b14040', fontWeight: 700 }}>{fmt(r.ruc_drift_eur, 0)} €</td>
                                <td>{r.sla_breach ? <span style={{ background: 'rgba(177,64,64,0.15)', color: '#b14040', padding: '2px 6px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700 }}>BREACH</span> : <span className="muted" style={{ fontSize: '0.7rem' }}>OK</span>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}


/**
 * Flat xls-like Cijene tablica per aktivni segment (Poslovnica / Web reg / Web gost / Slovenija / Bugarska).
 * Pokazuje samo polja relevantna za tab. Inline-edit za nabavni premium, zavisni troškovi, premium na spot, spread.
 */
function CijeneFlatTable({ rows, payload, activeSegment, spotPerOz, locale, onUpdatePurchase, onUpdateSegment, token, onProductPremiumChanged }: {
    rows: IndexRow[];
    payload: CgmPayload;
    activeSegment: string;
    spotPerOz: number | undefined;
    locale: 'hr' | 'en';
    onUpdatePurchase: (productId: number, patch: Partial<PurchaseInputs>) => void;
    onUpdateSegment: (productId: number, patch: Partial<CgmSegmentRule>) => void;
    token: string;
    onProductPremiumChanged?: () => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const spotPerG = spotPerOz ? spotPerOz / GRAMS_PER_TROY_OZ : 0;

    // Segment label za header (npr. "WEB registrirani", "Poslovnica")
    const segLabel = segmentLabel(activeSegment, locale);
    const isWeb = activeSegment.startsWith('web_') || activeSegment === 'slovenija' || activeSegment === 'bugarska';
    const isPoslovnica = activeSegment === 'poslovnice';
    // Tržišta s premium-on-web modelom (po user spec): MPC = web_registered_cijena + premium_<segment>_eur
    const isMarketPremium = activeSegment === 'poslovnice' || activeSegment === 'slovenija' || activeSegment === 'bugarska';
    const premiumFieldName: 'premium_poslovnica_eur' | 'premium_slovenija_eur' | 'premium_bugarska_eur' | null =
        activeSegment === 'poslovnice' ? 'premium_poslovnica_eur'
        : activeSegment === 'slovenija' ? 'premium_slovenija_eur'
        : activeSegment === 'bugarska' ? 'premium_bugarska_eur'
        : null;

    // Lokalni draft za inline edit premium polja (debounced save)
    const [premiumDrafts, setPremiumDrafts] = React.useState<Record<number, string>>({});
    const flushTimers = React.useRef<Record<number, number>>({});
    const savePremium = React.useCallback(async (productId: number, value: number) => {
        if (! premiumFieldName) return;
        try {
            await mutate(`/admin/products/${productId}`, token, 'PATCH', { [premiumFieldName]: value });
            // NE pozivamo onProductPremiumChanged() — to bi triggeralo load() koji re-fetcha sve
            // produkte i resetira state (gubi se scroll pozicija, search, sort, focus). Display je
            // već live preko premiumDrafts; jedina razlika je da se nakon page reload-a vrijednost
            // dohvaća iz baze (sad ima novu vrijednost).
        } catch (e) {
            // tiho — toast bi bio prijateljskiji, ali ne želimo padove
            console.error('savePremium fail', e);
        }
    }, [premiumFieldName, token]);

    // Filtri
    const [search, setSearch] = React.useState('');
    const [catFilter, setCatFilter] = React.useState<string>('');
    const [mintFilter, setMintFilter] = React.useState<string>('');
    const [weightFilter, setWeightFilter] = React.useState<string>('');
    const distinctCats = Array.from(new Set(rows.map((r) => r.product.cgm_category).filter(Boolean))).sort();
    const distinctMints = Array.from(new Set(rows.map((r) => r.product.cgm_mint).filter(Boolean))).sort();
    const distinctWeights = Array.from(new Set(rows.map((r) => r.product.fine_weight_g).filter((g): g is number => g != null && g > 0))).sort((a, b) => a - b);

    // Sort state (klik na header column = sort key)
    type SortKey = 'name' | 'category' | 'delivery' | 'mint' | 'supplier' | 'g' | 'premium' | 'dep_costs' | 'nc' | 'spread' | 'margin' | 'mpc' | 'margin_to_spot' | 'margin_to_spot_pct' | 'otkupna' | 'ruc_eur' | 'ruc_pct';
    const [sortKey, setSortKey] = React.useState<SortKey>('category');
    const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('asc');
    const toggleSort = (key: SortKey) => {
        if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        else { setSortKey(key); setSortDir('asc'); }
    };
    const sortArrow = (key: SortKey) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    const filtered = rows.filter((r) => {
        if (catFilter && r.product.cgm_category !== catFilter) return false;
        if (mintFilter && (r.product.cgm_mint ?? '') !== mintFilter) return false;
        if (weightFilter && String(r.product.fine_weight_g ?? '') !== weightFilter) return false;
        if (search) {
            const s = search.toLowerCase();
            const hay = `${r.product.name_hr} ${r.product.cgm_supplier ?? ''} ${r.product.cgm_mint ?? ''}`.toLowerCase();
            if (!hay.includes(s)) return false;
        }
        return true;
    });

    // Sort dinamičan po sortKey (klik na header)
    function getPurchTmp(productId: number): PurchaseInputs {
        return payload.products[String(productId)]?.purchase ?? defaultPurchase();
    }
    function getSegTmp(productId: number): CgmSegmentRule {
        const row = rows.find((x) => x.product.id === productId);
        return payload.products[String(productId)]?.segments?.[activeSegment] ?? cloneSegmentRule((row?.rules_by_segment?.[activeSegment] as CgmSegmentRule | undefined) ?? emptyRule());
    }
    function sortValue(r: IndexRow): string | number {
        const p = r.product;
        const purch = getPurchTmp(p.id);
        const seg = getSegTmp(p.id);
        const fineG = p.fine_weight_g ?? 0;
        const metalEur = fineG * spotPerG;
        const nc = metalEur + purch.premium_eur + purch.dependent_costs_eur;
        // MPC formula (web/web_guest): MPC = NC + web marža (eur_on_spot) + opcionalno % na spot
        // (vlasnikova specifikacija 2026-06-03: nabavni premium + zavisni troškovi se UKLJUČUJU u MPC,
        //  prije ovog fix-a bili su izvan MPC → frontend je pokazivao MPC niži od NC za neke segmente)
        const mpc = isMarketPremium
            ? (p.current_sale_price_eur != null ? Number(p.current_sale_price_eur) : nc + seg.eur_on_spot + metalEur * (seg.pct_on_spot / 100)) + (premiumFieldName ? Number(p[premiumFieldName] ?? 0) : 0)
            : nc + seg.eur_on_spot + metalEur * (seg.pct_on_spot / 100);
        const otkupna = seg.buyback_spread_eur != null ? Math.max(0, mpc - seg.buyback_spread_eur) : (seg.buyback_pct_below_sale != null ? mpc * (1 - seg.buyback_pct_below_sale / 100) : -Infinity);
        const ruc = mpc - nc;
        const rucPct = mpc > 0 ? (ruc / mpc) * 100 : 0;
        switch (sortKey) {
            case 'name': return p.name_hr ?? '';
            case 'category': return p.cgm_category ?? '';
            case 'delivery': return p.cgm_delivery_type ?? '';
            case 'mint': return p.cgm_mint ?? '';
            case 'supplier': return p.cgm_supplier ?? '';
            case 'g': return fineG;
            case 'premium': return purch.premium_eur;
            case 'dep_costs': return purch.dependent_costs_eur;
            case 'nc': return nc;
            case 'spread': return seg.buyback_spread_eur ?? 0;
            case 'margin': return seg.eur_on_spot;
            case 'mpc': return mpc;
            case 'margin_to_spot': return mpc - metalEur;
            case 'margin_to_spot_pct': return metalEur > 0 ? ((mpc - metalEur) / metalEur) * 100 : 0;
            case 'otkupna': return otkupna;
            case 'ruc_eur': return ruc;
            case 'ruc_pct': return rucPct;
        }
    }
    filtered.sort((a, b) => {
        const av = sortValue(a);
        const bv = sortValue(b);
        let cmp: number;
        if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
        else cmp = String(av).localeCompare(String(bv));
        return sortDir === 'asc' ? cmp : -cmp;
    });

    function fmt(n: number | null | undefined, dp = 2): string {
        if (n == null || !isFinite(n)) return '—';
        return n.toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { minimumFractionDigits: dp, maximumFractionDigits: dp });
    }

    function pct(n: number | null | undefined): string {
        if (n == null || !isFinite(n)) return '—';
        return n.toFixed(2) + '%';
    }

    function getPurch(productId: number): PurchaseInputs {
        const key = String(productId);
        return payload.products[key]?.purchase ?? defaultPurchase();
    }
    function getSeg(productId: number): CgmSegmentRule {
        const key = String(productId);
        const row = rows.find((x) => x.product.id === productId);
        return payload.products[key]?.segments?.[activeSegment] ?? cloneSegmentRule((row?.rules_by_segment?.[activeSegment] as CgmSegmentRule | undefined) ?? emptyRule());
    }

    return (
        <div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
                <input
                    placeholder={t('Pretraga (naziv, dobavljač, kovnica)…', 'Search (name, supplier, mint)…')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ padding: '6px 10px', minWidth: 280, fontSize: '0.85rem' }}
                />
                <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} style={{ padding: '6px 10px', fontSize: '0.85rem' }}>
                    <option value="">{t('— Sve kategorije —', '— All categories —')}</option>
                    {distinctCats.map((c) => <option key={c ?? ''} value={c ?? ''}>{c}</option>)}
                </select>
                <select value={mintFilter} onChange={(e) => setMintFilter(e.target.value)} style={{ padding: '6px 10px', fontSize: '0.85rem' }}>
                    <option value="">{t('— Sve kovnice —', '— All mints —')}</option>
                    {distinctMints.map((m) => <option key={m ?? ''} value={m ?? ''}>{m}</option>)}
                </select>
                <select value={weightFilter} onChange={(e) => setWeightFilter(e.target.value)} style={{ padding: '6px 10px', fontSize: '0.85rem' }}>
                    <option value="">{t('— Sve težine —', '— All weights —')}</option>
                    {distinctWeights.map((g) => <option key={g} value={String(g)}>{g} g</option>)}
                </select>
                {(mintFilter || weightFilter || catFilter || search) ? (
                    <button type="button" className="secondary compact" onClick={() => { setMintFilter(''); setWeightFilter(''); setCatFilter(''); setSearch(''); }} style={{ fontSize: '0.78rem' }}>✕ {t('Očisti filtere', 'Clear filters')}</button>
                ) : null}
                <span className="muted" style={{ fontSize: '0.78rem', marginLeft: 'auto' }}>
                    {t('Prikazano', 'Showing')} {filtered.length} / {rows.length} · {t('Tab', 'Tab')}: <strong>{segLabel}</strong> · {t('SPOT', 'SPOT')}: <strong>{fmt(spotPerG, 4)} €/g</strong>
                </span>
            </div>

            <div className="table-wrap" style={{ overflowX: 'auto', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 6 }}>
                <table className="table-like" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                    <thead style={{ background: 'rgba(var(--ad-acc-rgb),0.08)', position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr style={{ borderBottom: '2px solid var(--ad-acc)' }}>
                            <th onClick={() => toggleSort('name')} style={{ padding: '6px 8px', textAlign: 'left', minWidth: 220, cursor: 'pointer', userSelect: 'none' }}>{t('Proizvod', 'Product')}{sortArrow('name')}</th>
                            <th onClick={() => toggleSort('category')} style={{ padding: '6px 8px', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}>{t('Kategorija', 'Category')}{sortArrow('category')}</th>
                            <th onClick={() => toggleSort('delivery')} style={{ padding: '6px 8px', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}>{t('Rok isporuke', 'Delivery')}{sortArrow('delivery')}</th>
                            <th onClick={() => toggleSort('mint')} style={{ padding: '6px 8px', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}>{t('Kovnica', 'Mint')}{sortArrow('mint')}</th>
                            <th onClick={() => toggleSort('supplier')} style={{ padding: '6px 8px', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}>{t('Dobavljač', 'Supplier')}{sortArrow('supplier')}</th>
                            <th onClick={() => toggleSort('g')} style={{ padding: '6px 8px', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}>{t('g', 'g')}{sortArrow('g')}</th>
                            <th onClick={() => toggleSort('premium')} style={{ padding: '6px 8px', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}>{t('Nabavni premium €', 'Acq. premium €')}{sortArrow('premium')}</th>
                            <th onClick={() => toggleSort('dep_costs')} style={{ padding: '6px 8px', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}>{t('Zav. troškovi €', 'Dep. costs €')}{sortArrow('dep_costs')}</th>
                            <th onClick={() => toggleSort('nc')} style={{ padding: '6px 8px', textAlign: 'right', cursor: 'pointer', userSelect: 'none', background: 'rgba(31,122,54,0.1)' }}>NC{sortArrow('nc')}</th>
                            {isMarketPremium ? (
                                <>
                                    <th style={{ padding: '6px 8px', textAlign: 'right', background: 'rgba(0,0,0,0.04)' }} title={t('Web registrirani prodajna cijena — osnovica (current_sale_price_eur na bi_products)', 'Web registered sale price (base)')}>{t('Web osnovica €', 'Web base €')}</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'right', background: 'rgba(var(--ad-acc-rgb),0.12)' }} title={t('Premium za '+segLabel+' iznad web cijene (editable)', 'Premium for '+segLabel+' above web price (editable)')}>{t('Premium '+segLabel+' €', 'Premium '+segLabel+' €')}</th>
                                </>
                            ) : (
                                <>
                                    <th onClick={() => toggleSort('spread')} style={{ padding: '6px 8px', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}>{t('Spread', 'Spread')} {segLabel} €{sortArrow('spread')}</th>
                                    <th onClick={() => toggleSort('margin')} style={{ padding: '6px 8px', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}>{t('Web marža €', 'Web margin €')}{sortArrow('margin')}</th>
                                </>
                            )}
                            <th onClick={() => toggleSort('mpc')} style={{ padding: '6px 8px', textAlign: 'right', cursor: 'pointer', userSelect: 'none', background: 'rgba(30,64,175,0.08)' }}>MPC {segLabel}{sortArrow('mpc')}</th>
                            <th onClick={() => toggleSort('margin_to_spot')} style={{ padding: '6px 8px', textAlign: 'right', cursor: 'pointer', userSelect: 'none', background: 'rgba(138,104,23,0.10)' }} title={t('MPC poslovnice − spot vrijednost metala (€)', 'MPC − spot metal value (€)')}>{t('Marža do spota €', 'Margin to spot €')}{sortArrow('margin_to_spot')}</th>
                            <th onClick={() => toggleSort('margin_to_spot_pct')} style={{ padding: '6px 8px', textAlign: 'right', cursor: 'pointer', userSelect: 'none', background: 'rgba(138,104,23,0.10)' }} title={t('(MPC − spot) / spot × 100', '(MPC − spot) / spot × 100')}>{t('Marža %', 'Margin %')}{sortArrow('margin_to_spot_pct')}</th>
                            <th onClick={() => toggleSort('otkupna')} style={{ padding: '6px 8px', textAlign: 'right', cursor: 'pointer', userSelect: 'none', background: 'rgba(177,64,64,0.06)' }}>{t('Otkupna', 'Buyback')}{sortArrow('otkupna')}</th>
                            <th onClick={() => toggleSort('ruc_eur')} style={{ padding: '6px 8px', textAlign: 'right', cursor: 'pointer', userSelect: 'none', background: 'rgba(31,122,54,0.12)' }}>RUC €{sortArrow('ruc_eur')}</th>
                            <th onClick={() => toggleSort('ruc_pct')} style={{ padding: '6px 8px', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}>RUC %{sortArrow('ruc_pct')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((r) => {
                            const p = r.product;
                            const purch = getPurch(p.id);
                            const seg = getSeg(p.id);
                            const fineG = p.fine_weight_g ?? 0;
                            const metalEur = fineG * spotPerG;
                            const nc = metalEur + purch.premium_eur + purch.dependent_costs_eur;
                            // Cijena računanje (vlasnikova formula 2026-06-03):
                            //   - Web/web_guest: MPC = NC + web marža (= nc + eur_on_spot + opcionalno % na spot)
                            //     gdje NC = fine*spot + nabavni premium + zavisni troškovi
                            //   - Poslovnice/SI/BG: webBase (current_sale_price_eur ili fallback NC+marža) + premium_<segment>_eur
                            const webBase = p.current_sale_price_eur != null ? Number(p.current_sale_price_eur) : (nc + seg.eur_on_spot + metalEur * (seg.pct_on_spot / 100));
                            const productPremiumLive = premiumFieldName ? Number(p[premiumFieldName] ?? 0) : 0;
                            // Draft premium (dok korisnik tipka) — koristi se za LIVE izračun MPC/otkupna/RUC
                            // umjesto productPremiumLive (koji odgovara DB vrijednosti i ažurira se tek nakon
                            // save-a). Bez ovog: korisnik tipka 4.50 ali MPC i dalje pokazuje staru vrijednost.
                            const effectivePremium = premiumDrafts[p.id] !== undefined ? (parseFloat(premiumDrafts[p.id]) || 0) : productPremiumLive;
                            const mpc = isMarketPremium
                                ? webBase + effectivePremium
                                : nc + seg.eur_on_spot + metalEur * (seg.pct_on_spot / 100);
                            const otkupna = seg.buyback_spread_eur != null ? Math.max(0, mpc - seg.buyback_spread_eur) : (seg.buyback_pct_below_sale != null ? mpc * (1 - seg.buyback_pct_below_sale / 100) : null);
                            const ruc = mpc - nc;
                            const rucPct = mpc > 0 ? (ruc / mpc) * 100 : 0;

                            return (
                                <tr key={p.id} style={{ borderBottom: '1px solid #eee5d0' }}>
                                    <td style={{ padding: '4px 8px' }}>
                                        <div style={{ fontWeight: 600 }}>{p.name_hr.slice(0, 60)}</div>
                                        {p.sku ? <div className="muted" style={{ fontSize: '0.7rem' }}>{p.sku}</div> : null}
                                    </td>
                                    <td style={{ padding: '4px 8px', fontSize: '0.72rem' }}>{p.cgm_category ?? '—'}</td>
                                    <td style={{ padding: '4px 8px', fontSize: '0.72rem' }}>{p.cgm_delivery_type ?? '—'}</td>
                                    <td style={{ padding: '4px 8px', fontSize: '0.72rem' }}>{p.cgm_mint ?? '—'}</td>
                                    <td style={{ padding: '4px 8px', fontSize: '0.72rem' }}>{p.cgm_supplier ?? '—'}</td>
                                    <td style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fineG}</td>
                                    <td style={{ padding: '2px 4px', textAlign: 'right' }}>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={purch.premium_eur || ''}
                                            onChange={(e) => onUpdatePurchase(p.id, { premium_eur: parseFloat(e.target.value) || 0 })}
                                            style={{ width: 80, padding: '2px 4px', textAlign: 'right', fontSize: '0.78rem', border: '1px solid #ddd', borderRadius: 3 }}
                                        />
                                    </td>
                                    <td style={{ padding: '2px 4px', textAlign: 'right' }}>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={purch.dependent_costs_eur || ''}
                                            onChange={(e) => onUpdatePurchase(p.id, { dependent_costs_eur: parseFloat(e.target.value) || 0 })}
                                            style={{ width: 70, padding: '2px 4px', textAlign: 'right', fontSize: '0.78rem', border: '1px solid #ddd', borderRadius: 3 }}
                                        />
                                    </td>
                                    <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 700, background: 'rgba(31,122,54,0.06)', fontVariantNumeric: 'tabular-nums' }}>{fmt(nc)}</td>
                                    {isMarketPremium ? (
                                        <>
                                            <td style={{ padding: '4px 8px', textAlign: 'right', background: 'rgba(0,0,0,0.03)', fontVariantNumeric: 'tabular-nums' }} title={t('Web osnovica iz current_sale_price_eur (uredi u m.110)', 'Web base from current_sale_price_eur (edit in m.110)')}>{fmt(webBase)}</td>
                                            <td style={{ padding: '2px 4px', textAlign: 'right', background: 'rgba(var(--ad-acc-rgb),0.08)' }}>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={premiumDrafts[p.id] !== undefined ? premiumDrafts[p.id] : String(productPremiumLive || 0)}
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        setPremiumDrafts((prev) => ({ ...prev, [p.id]: v }));
                                                        if (flushTimers.current[p.id]) window.clearTimeout(flushTimers.current[p.id]);
                                                        flushTimers.current[p.id] = window.setTimeout(() => {
                                                            void savePremium(p.id, parseFloat(v) || 0);
                                                            delete flushTimers.current[p.id];
                                                        }, 700);
                                                    }}
                                                    onBlur={() => {
                                                        if (flushTimers.current[p.id]) { window.clearTimeout(flushTimers.current[p.id]); delete flushTimers.current[p.id]; }
                                                        const v = premiumDrafts[p.id];
                                                        if (v !== undefined) void savePremium(p.id, parseFloat(v) || 0);
                                                    }}
                                                    style={{ width: 80, padding: '2px 4px', textAlign: 'right', fontSize: '0.78rem', border: '1px solid #ddd', borderRadius: 3 }}
                                                    title={t('Premium iznad web cijene za '+segLabel+' — sprema se u bi_products', 'Premium above web price (saves to bi_products)')}
                                                />
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td style={{ padding: '2px 4px', textAlign: 'right' }}>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={seg.buyback_spread_eur ?? ''}
                                                    onChange={(e) => onUpdateSegment(p.id, { buyback_spread_eur: e.target.value === '' ? undefined : parseFloat(e.target.value) || 0 })}
                                                    style={{ width: 70, padding: '2px 4px', textAlign: 'right', fontSize: '0.78rem', border: '1px solid #ddd', borderRadius: 3 }}
                                                    title={t('Buyback spread € (MPC - Otkupna)', 'Buyback spread € (MPC - Buyback)')}
                                                />
                                            </td>
                                            <td style={{ padding: '2px 4px', textAlign: 'right' }}>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={seg.eur_on_spot || ''}
                                                    onChange={(e) => onUpdateSegment(p.id, { eur_on_spot: parseFloat(e.target.value) || 0 })}
                                                    style={{ width: 70, padding: '2px 4px', textAlign: 'right', fontSize: '0.78rem', border: '1px solid #ddd', borderRadius: 3 }}
                                                    title={t('Marža (€/komad) iznad SPOT-a', 'Margin (€/piece) above SPOT')}
                                                />
                                            </td>
                                        </>
                                    )}
                                    <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 700, background: 'rgba(30,64,175,0.06)', fontVariantNumeric: 'tabular-nums' }}>{fmt(mpc)}</td>
                                    {/* Marža do spota — MPC − vrijednost metala po spotu (€ i %). Pokazuje koliko “iznad goldspota” držimo cijenu. */}
                                    <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600, background: 'rgba(138,104,23,0.06)', fontVariantNumeric: 'tabular-nums', color: 'var(--ad-ink)' }}>{fmt(mpc - metalEur)}</td>
                                    <td style={{ padding: '4px 8px', textAlign: 'right', background: 'rgba(138,104,23,0.06)', fontVariantNumeric: 'tabular-nums', color: 'var(--ad-ink)' }}>{metalEur > 0 ? pct(((mpc - metalEur) / metalEur) * 100) : '—'}</td>
                                    <td style={{ padding: '4px 8px', textAlign: 'right', background: 'rgba(177,64,64,0.04)', fontVariantNumeric: 'tabular-nums' }}>{otkupna != null ? fmt(otkupna) : '—'}</td>
                                    <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 700, color: ruc >= 0 ? '#1f7a36' : '#b14040', background: 'rgba(31,122,54,0.08)', fontVariantNumeric: 'tabular-nums' }}>{fmt(ruc)}</td>
                                    <td style={{ padding: '4px 8px', textAlign: 'right', color: rucPct >= 0 ? '#1f7a36' : '#b14040', fontVariantNumeric: 'tabular-nums' }}>{pct(rucPct)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <p className="muted" style={{ fontSize: '0.72rem', marginTop: 8, lineHeight: 1.5 }}>
                ℹ️ <strong>NC</strong> = SPOT × g + Nabavni premium + Zav. troškovi ·{' '}
                {isMarketPremium ? (
                    <><strong>MPC {segLabel}</strong> = Web osnovica + Premium {segLabel} ({t('osnovica = bi_products.current_sale_price_eur, premium se sprema instant po artiklu', 'base = bi_products.current_sale_price_eur, premium saves per product')})</>
                ) : (
                    <><strong>MPC</strong> = SPOT × g + Marža (€)</>
                )} · <strong>Otkupna</strong> = MPC − Spread · <strong>RUC</strong> = MPC − NC ({t('marža iznad nabave', 'margin above cost')}).
                {! isMarketPremium ? t(' Edit polja se spremaju u draft — klikni "💾 Spremi reviziju cijena" ispod za pohranu.', ' Edited fields stay as draft — click "💾 Save pricing revision" below to commit.') : null}
            </p>
        </div>
    );
}


function KpiCard({ label, value, sublabel, accent, highlight }: { label: string; value: string; sublabel?: string; accent: string; highlight?: boolean }) {
    return (
        <div style={{
            background: highlight ? `linear-gradient(135deg, ${accent}1a, ${accent}0a)` : '#fff',
            border: '1px solid ' + (highlight ? accent + '66' : 'rgba(var(--ad-soft-rgb),0.3)'),
            borderRadius: 10, padding: '12px 14px',
        }}>
            <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(0,0,0,0.5)' }}>{label}</div>
            <strong style={{ fontSize: '1.2rem', color: accent }}>{value}</strong>
            {sublabel ? <div className="muted" style={{ fontSize: '0.74rem', marginTop: 2 }}>{sublabel}</div> : null}
        </div>
    );
}

function PlanIsporukaPlaceholderTab({ locale, token }: { locale: 'hr' | 'en'; token: string }) {
    return <DeliveryPlanTab locale={locale} token={token} />;
}

type ProposeResponse = {
    allocations: Array<{
        order_number: string; partner_name: string | null; item_code: string; item_name: string | null;
        qty: number; source_layer: 'L0' | 'L1' | 'L2' | 'L3'; source_warehouse_code: string | null;
        source_supplier_name: string | null; sale_price_eur: number; source_unit_cost_eur: number;
        delivery_cost_eur: number; ruc_estimate_eur: number; source_cost_layer_id: number | null;
        dest_branch_code?: string | null; dest_branch_name?: string | null;
        sla_days?: number; category_name?: string | null; lead_days?: number; sla_breach?: boolean; deliver_by_estimate?: string;
        phase?: number; is_manual_reservation?: boolean;
    }>;
    summary: { total_orders: number; l0_qty: number; l1_qty: number; l2_qty: number; l3_qty: number; total_sales_eur: number; total_cost_eur: number; total_ruc_eur: number; uncovered_qty: number; sla_breach_count?: number; anti_chaos_skips?: any; alloc_skips_by_rule?: any; filter_skips?: any; phase_counts?: Record<string, number>; sort_mode?: string };
    by_order: Array<{ order_number: string; partner_name: string | null; items_count: number; qty_total: number; sales: number; cost: number; ruc: number; layers: Record<string, number>; phase?: number; branch_code?: string | null }>;
    uncovered?: Array<{ order_number: string; partner_name: string | null; branch_code: string | null; branch_name: string | null; item_code?: string; item_name: string | null; qty_needed: number; fine_g?: number; category_name?: string | null; suggested_supplier?: string | null; reason: string }>;
    phases?: Record<string, { label: string; date: string; order_count: number }>;
    engine_version?: string;
};

type PlannerFilters = {
    order_numbers?: string[];
    excluded_order_numbers?: string[];
    sort_mode?: 'fifo_age' | 'cash_tight';
    engine?: 'phased' | 'legacy';
    min_age_days?: number; max_age_days?: number;
    sla_breach_only?: boolean; deliver_this_week?: boolean;
    only_full_orders?: boolean; min_coverage_pct?: number;
    only_paid_full?: boolean; min_advance_pct?: number;
    allowed_layers?: ('L0'|'L1'|'L2'|'L3')[];
    categories?: string[]; exclude_egzotika?: boolean;
    branches?: string[]; regions?: string[];
    min_eur?: number; max_eur?: number;
    min_ruc_pct?: number;
    min_transfer_eur?: number;
    prioritize_max_orders?: boolean; // strategija: zatvori max broj narudžbi unutar budžeta (jeftinije prvo)
    budget_eur?: number;             // ukupni budžet za prioritize_max_orders greedy
    mints?: string[];                // "naruči SAMO od ovih kovnica" (restrikcija; prazno = sve kovnice)
    mint_budgets?: Record<string, number>; // EUR limit po kovnici — reže nabavu te kovnice do iznosa (FIFO)
    mixed_only?: boolean;            // samo narudžbe koje sadrže BOTH Au i Ag
    delivery_by_date?: string;       // YYYY-MM-DD — samo narudžbe gdje je dogovoren_datum <= ovaj datum
    // Oznake iz m.40.3 (default ON kad undefined; eksplicitni false isključuje):
    respect_imam_ju?: boolean;       // izuzmi "imam ju" narudžbe iz preskladištenja
    prioritize_hitno?: boolean;      // hitne narudžbe prioritetne
    respect_agreed_date?: boolean;   // poštuj dogovoreni datum isporuke
    nacin_isporuke?: string[];       // filter: u_poslovnici|vozac|gls_posta|centralno
    mjesto_isporuke?: string;        // filter: mjesto isporuke (poslovnica ili CENTRALNE ISPORUKE RIJEKA)
};

export function DeliveryPlanTab({ locale, token }: { locale: 'hr' | 'en'; token: string }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = React.useState<ProposeResponse | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [committing, setCommitting] = React.useState(false);
    const [committedPlanId, setCommittedPlanId] = React.useState<number | null>(null);
    const [selectedPlanId, setSelectedPlanId] = React.useState<number | null>(null);
    const [minMarginPct, setMinMarginPct] = React.useState<number>(0); // 0 = ne forsiraj
    const [overrides, setOverrides] = React.useState<Record<number, 'force_l3' | 'exclude' | null>>({});
    const [overrideSearch, setOverrideSearch] = React.useState('');
    const [savedPlans, setSavedPlans] = React.useState<Array<{ id: number; plan_date: string; status: string; total_allocations: number; total_sales_eur: number; total_cost_eur: number; total_ruc_eur: number; created_at: string; created_by_name: string | null; notes: string | null }>>([]);
    const [drawerOrder, setDrawerOrder] = React.useState<string | null>(() => readUrlParam('order', '') || null);
    useUrlSync('order', drawerOrder, !drawerOrder);

    // Web preview za L3 narudžbe (Prijedlog narudžbi) — modal s Potvrdi flow
    const [proposalModal, setProposalModal] = React.useState<null | { planId: number; loading: boolean; data: any; sendHedge: boolean; hedgeEmail: string; potvrdjeno: boolean; potvrdResult: any }>(null);

    async function openOrderProposal(planId: number) {
        setProposalModal({ planId, loading: true, data: null, sendHedge: true, hedgeEmail: '', potvrdjeno: false, potvrdResult: null });
        try {
            const r = await fetch(`/api/gold-cm-planner/delivery-plan/${planId}/order-proposal`, { headers: { Authorization: `Bearer ${token}` } });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const json = await r.json();
            setProposalModal((s) => s ? { ...s, loading: false, data: json } : s);
        } catch (e) {
            setProposalModal((s) => s ? { ...s, loading: false, data: { error: e instanceof Error ? e.message : 'fail' } } : s);
        }
    }

    async function potvrdiNarudzbe() {
        if (!proposalModal?.data?.suppliers) return;
        const orders = proposalModal.data.suppliers.map((s: any) => ({
            supplier_name: s.supplier_name,
            commitment_eur: Number(s.totals.cost_eur.toFixed(2)),
            expected_sales_eur: Number(s.totals.sale_eur.toFixed(2)),
            expected_cost_eur: Number(s.totals.cost_eur.toFixed(2)),
            expected_ruc_eur: Number(s.totals.ruc_eur.toFixed(2)),
            order_reference: `Plan #${proposalModal.planId} · ${s.items.length} artikala · ${s.totals.order_count} kupaca`,
            recommendation_snapshot: { from_delivery_plan_id: proposalModal.planId, items: s.items },
        }));
        // Hedge email SAMO za zlato (XAU futures ne pokrivaju srebro)
        const goldFineG = proposalModal.data.grand_totals.fine_g_gold ?? proposalModal.data.grand_totals.fine_g ?? 0;
        const payload: any = { orders };
        if (proposalModal.sendHedge && goldFineG > 0) {
            payload.hedge = {
                send_email: true,
                fine_g: Number(goldFineG.toFixed(2)),
                note: `Iz Plan isporuka #${proposalModal.planId} — pokriva ${orders.length} dobavljača (samo Au, Ag se ne hedge-a).`,
            };
            if (proposalModal.hedgeEmail.trim()) payload.hedge.to_email = proposalModal.hedgeEmail.trim();
        }
        try {
            const r = await fetch('/api/gold-cm-planner/place-supplier-orders', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const json = await r.json();
            if (!r.ok) throw new Error(json?.message ?? `HTTP ${r.status}`);
            setProposalModal((s) => s ? { ...s, potvrdjeno: true, potvrdResult: json } : s);
        } catch (e) {
            alert(e instanceof Error ? e.message : 'Potvrdi fail');
        }
    }

    // Inline backlog preview state — useMemo (koji ovisi o `filters`) je niže, nakon filters deklaracije
    const [backlogData, setBacklogData] = React.useState<any>(null);
    const [backlogLoading, setBacklogLoading] = React.useState(false);
    const [previewSelected, setPreviewSelected] = React.useState<Set<string>>(new Set());
    const [previewSortKey, setPreviewSortKey] = React.useState<string>('remaining');
    const [previewSortDir, setPreviewSortDir] = React.useState<'asc' | 'desc'>('desc');
    const [previewActiveSuppliers, setPreviewActiveSuppliers] = React.useState<Set<string>>(new Set());

    // Filters state (sve A-G grupe) — declared early so loadBacklog može slati u POST body.
    const [filters, setFilters] = React.useState<PlannerFilters>({
        allowed_layers: ['L0', 'L1', 'L2', 'L3'],
    });

    const loadBacklog = React.useCallback(async () => {
        setBacklogLoading(true);
        try {
            const r = await fetch('/api/gold-cm-planner/backlog?include_ignored=0', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(filters),
            });
            if (r.ok) setBacklogData(await r.json());
        } catch { /* */ }
        finally { setBacklogLoading(false); }
    }, [token, filters]);
    React.useEffect(() => { void loadBacklog(); }, [loadBacklog]);

    // Kovnice prisutne u backlogu (za "budžet po kovnici" panel). Iz by_order[].mints (canonical, iz šifrarnika).
    const availableMints = React.useMemo<string[]>(() => {
        const set = new Set<string>();
        for (const o of (backlogData?.by_order ?? [])) {
            for (const m of (o.mints ?? [])) if (m) set.add(String(m));
        }
        return Array.from(set).sort();
    }, [backlogData]);

    function togglePreviewSort(k: string) {
        if (previewSortKey === k) setPreviewSortDir(previewSortDir === 'asc' ? 'desc' : 'asc');
        else { setPreviewSortKey(k); setPreviewSortDir(k === 'order' || k === 'partner' ? 'asc' : 'desc'); }
    }
    function togglePreviewSelect(orderNum: string) {
        setPreviewSelected((prev) => {
            const next = new Set(prev);
            if (next.has(orderNum)) next.delete(orderNum); else next.add(orderNum);
            return next;
        });
    }
    function togglePreviewSupplier(s: string) {
        setPreviewActiveSuppliers((prev) => {
            const next = new Set(prev);
            if (next.has(s)) next.delete(s); else next.add(s);
            return next;
        });
    }

    // Filters state moved up to be shared with loadBacklog. Ovdje samo UI helper state.
    const [showFilters, setShowFilters] = React.useState(true);
    const [presets, setPresets] = React.useState<Array<{ id: number; name: string; filters: any; is_default: boolean }>>([]);
    const [activePresetName, setActivePresetName] = React.useState<string>('');

    // Server-side filteri se već primjenjuju (POST backlog s `filters` body) — klijent samo
    // dodaje supplier-chip filter + sort. Layer feasibility, budget greedy, age/eur/payment
    // filteri rade na backendu (vidi GoldCmPlannerController::backlog()).
    const filteredPreview = React.useMemo(() => {
        if (!backlogData?.by_order) return [];
        let list: any[] = backlogData.by_order;
        if (previewActiveSuppliers.size > 0) {
            list = list.filter((o: any) => o.suppliers.some((s: string) => previewActiveSuppliers.has(s)));
        }
        const mul = previewSortDir === 'asc' ? 1 : -1;
        const sortFn: Record<string, (a: any, b: any) => number> = {
            date: (a, b) => mul * String(a.item_date ?? '').localeCompare(String(b.item_date ?? '')),
            order: (a, b) => mul * String(a.order_number).localeCompare(String(b.order_number)),
            partner: (a, b) => mul * String(a.partner_name ?? '').localeCompare(String(b.partner_name ?? '')),
            items: (a, b) => mul * (a.items_count - b.items_count),
            total: (a, b) => mul * (a.total_eur - b.total_eur),
            cost: (a, b) => mul * (a.cost_estimate_eur - b.cost_estimate_eur),
            ruc: (a, b) => mul * (a.ruc_estimate_eur - b.ruc_estimate_eur),
            paid: (a, b) => mul * (a.paid_eur - b.paid_eur),
            remaining: (a, b) => mul * (a.remaining_eur - b.remaining_eur),
            pct: (a, b) => mul * (a.paid_pct - b.paid_pct),
        };
        list = [...list].sort(sortFn[previewSortKey] ?? sortFn.remaining);
        return list;
    }, [backlogData, previewActiveSuppliers, previewSortKey, previewSortDir]);

    const loadPresets = React.useCallback(async () => {
        try {
            const r = await fetch('/api/gold-cm-planner/filter-presets', { headers: { Authorization: `Bearer ${token}` } });
            if (r.ok) {
                const j = await r.json();
                setPresets(j.presets ?? []);
                // Auto-apply default
                const def = (j.presets ?? []).find((p: any) => p.is_default);
                if (def) {
                    const f = typeof def.filters === 'string' ? JSON.parse(def.filters) : def.filters;
                    setFilters(f);
                    setActivePresetName(def.name);
                }
            }
        } catch { /* */ }
    }, [token]);
    React.useEffect(() => { void loadPresets(); }, [loadPresets]);

    // Učitaj filtere iz localStorage (postavlja se iz Backloga klikom "Generiraj plan iz filtriranog skupa")
    // i auto-pokreni propose ako je svjež zahtjev (< 60s).
    React.useEffect(() => {
        try {
            const raw = localStorage.getItem('bi.planner.plan_filters');
            if (raw) {
                const j = JSON.parse(raw);
                if (j._ts && Date.now() - j._ts < 60000) {
                    setFilters({ ...filters, order_numbers: j.order_numbers });
                    localStorage.removeItem('bi.planner.plan_filters');
                    // Auto-trigger propose nakon kratke pauze (da se filters state ažurira)
                    setTimeout(() => { void propose(); }, 100);
                }
            }
        } catch { /* */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function savePreset(name: string, isDefault: boolean) {
        const r = await fetch('/api/gold-cm-planner/filter-presets', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ name, filters, is_default: isDefault }),
        });
        if (r.ok) {
            await loadPresets();
            setActivePresetName(name);
            alert(t(`Preset "${name}" spremljen${isDefault ? ' (default)' : ''}.`, `Preset "${name}" saved${isDefault ? ' (default)' : ''}.`));
        }
    }
    async function deletePreset(id: number, name: string) {
        if (!confirm(t(`Obrisati preset "${name}"?`, `Delete preset "${name}"?`))) return;
        await fetch(`/api/gold-cm-planner/filter-presets/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        await loadPresets();
    }
    function loadPreset(name: string) {
        const p = presets.find((x) => x.name === name);
        if (!p) return;
        const f = typeof p.filters === 'string' ? JSON.parse(p.filters) : p.filters;
        setFilters(f);
        setActivePresetName(name);
    }

    const loadSavedPlans = React.useCallback(async () => {
        try {
            const r = await fetch('/api/gold-cm-planner/delivery-plan/list', { headers: { Authorization: `Bearer ${token}` } });
            if (r.ok) {
                const j = await r.json();
                setSavedPlans(j.rows ?? []);
            }
        } catch { /* ignore */ }
    }, [token]);
    React.useEffect(() => { void loadSavedPlans(); }, [loadSavedPlans, committedPlanId]);

    async function propose() {
        setLoading(true); setError(''); setCommittedPlanId(null);
        try {
            const r = await fetch('/api/gold-cm-planner/delivery-plan/propose', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(filters),
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const resp: ProposeResponse = await r.json();
            // Klijent-side: ako alokacija ima RUC% < minMarginPct i source je L0/L1/L2, force_l3 (override)
            if (minMarginPct > 0) {
                resp.allocations = resp.allocations.map((a) => {
                    const rucPct = a.sale_price_eur > 0 ? (a.ruc_estimate_eur / a.sale_price_eur) * 100 : 0;
                    if (rucPct < minMarginPct && a.source_layer !== 'L3') {
                        // force_l3 — postavi source_layer L3 i resetiraj cost na heuristiku
                        return { ...a, source_layer: 'L3' as const, source_warehouse_code: null, source_cost_layer_id: null };
                    }
                    return a;
                });
            }
            setData(resp);
            setOverrides({});
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setLoading(false);
        }
    }

    async function downloadPlanExport(planId: number, kind: 'pdf' | 'xlsx') {
        try {
            const res = await fetch(`/api/gold-cm-planner/delivery-plan/${planId}/export-${kind}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `delivery-plan-${planId}.${kind}`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            alert(e instanceof Error ? e.message : 'Download fail');
        }
    }

    // Oldschool download overlay s progress %
    const [downloadOverlay, setDownloadOverlay] = React.useState<null | { label: string; progress: number; phase: 'preparing' | 'downloading' | 'done' | 'error'; message?: string }>(null);

    async function downloadDeliverable(planId: number, kindName: 'isporuke' | 'preskladistenje' | 'narudzbe', format: 'pdf' | 'xlsx') {
        // Min transfer EUR sad dolazi iz globalnog filtera (G · Vrijednost) — ne više inline state
        const minTransferEur = filters.min_transfer_eur ?? 0;
        const labelMap: Record<string, string> = {
            isporuke: t('Plan isporuke', 'Delivery plan'),
            preskladistenje: t('Plan preskladištenja', 'Restocking plan'),
            narudzbe: t('Prijedlog narudžbi', 'Supplier proposal'),
        };
        const label = `${labelMap[kindName]} (${format.toUpperCase()})`;
        setDownloadOverlay({ label, progress: 0, phase: 'preparing', message: t('Pripremam izvještaj na serveru…', 'Preparing report on server…') });

        // Indeterminate ticker dok server generira (do prvih response bytes)
        let preparingPct = 0;
        const preparingTimer = setInterval(() => {
            preparingPct = Math.min(preparingPct + 3, 60);
            setDownloadOverlay((s) => s && s.phase === 'preparing' ? { ...s, progress: preparingPct } : s);
        }, 200);

        try {
            const qs = (kindName === 'preskladistenje' && minTransferEur > 0) ? `?min_transfer_eur=${minTransferEur}` : '';
            const res = await fetch(`/api/gold-cm-planner/delivery-plan/${planId}/export/${kindName}/${format}${qs}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            clearInterval(preparingTimer);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            // Stream s progressom
            const total = Number(res.headers.get('content-length') || 0);
            setDownloadOverlay({ label, progress: total > 0 ? 60 : 70, phase: 'downloading', message: t('Skidam datoteku…', 'Downloading file…') });

            let blob: Blob;
            if (res.body && total > 0) {
                const reader = res.body.getReader();
                const chunks: Uint8Array[] = [];
                let received = 0;
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    if (value) {
                        chunks.push(value);
                        received += value.length;
                        const pct = 60 + Math.min(38, Math.round((received / total) * 38));
                        setDownloadOverlay((s) => s ? { ...s, progress: pct } : s);
                    }
                }
                blob = new Blob(chunks as BlobPart[], { type: res.headers.get('content-type') ?? 'application/octet-stream' });
            } else {
                blob = await res.blob();
            }

            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `plan-${planId}-${kindName}.${format}`;
            a.click();
            URL.revokeObjectURL(url);
            setDownloadOverlay({ label, progress: 100, phase: 'done', message: t('Gotovo ✓', 'Done ✓') });
            setTimeout(() => setDownloadOverlay(null), 800);
        } catch (e) {
            clearInterval(preparingTimer);
            setDownloadOverlay({ label, progress: 0, phase: 'error', message: e instanceof Error ? e.message : 'Download fail' });
            setTimeout(() => setDownloadOverlay(null), 2500);
        }
    }

    // L3 budget per supplier — slider cutoff (FIFO age: starije ostaju, mlađe otpadaju kad spustimo budget)
    const [l3Budgets, setL3Budgets] = React.useState<Record<string, number>>({});

    // L3 grupiranje per supplier + cumulative cost po starosti
    const l3BySupplier = React.useMemo(() => {
        if (!data?.allocations) return [] as Array<{ supplier: string; orders: Array<{ order_number: string; partner_name: string | null; cost: number; age_days: number }>; max_cost: number; cumulative: Array<number> }>;
        const l3 = data.allocations.filter((a) => a.source_layer === 'L3');
        const byOrder: Record<string, any> = {};
        l3.forEach((a) => {
            if (!byOrder[a.order_number]) {
                const ord = data.by_order.find((o) => o.order_number === a.order_number);
                byOrder[a.order_number] = {
                    order_number: a.order_number,
                    partner_name: a.partner_name,
                    supplier: a.source_supplier_name ?? 'NERASPOREĐENO',
                    cost: 0,
                    age_days: 0,
                    item_date: null,
                };
            }
            byOrder[a.order_number].cost += a.source_unit_cost_eur * a.qty;
        });
        // Per supplier
        const bySupplier: Record<string, any[]> = {};
        Object.values(byOrder).forEach((o: any) => {
            if (!bySupplier[o.supplier]) bySupplier[o.supplier] = [];
            bySupplier[o.supplier].push(o);
        });
        return Object.entries(bySupplier).map(([supplier, orders]) => {
            // Sort: najstarije prve (FIFO age)
            const sorted = orders; // age_days už 0 jer nemamo iz API-ja; fallback by order_number string sort
            sorted.sort((a, b) => String(a.order_number).localeCompare(String(b.order_number)));
            let cumul = 0;
            const cumulative = sorted.map((o) => { cumul += o.cost; return cumul; });
            return { supplier, orders: sorted, max_cost: cumul, cumulative };
        }).sort((a, b) => b.max_cost - a.max_cost);
    }, [data]);

    // Order numbers koji su EXCLUDED zbog L3 budget cutoff
    const l3ExcludedOrders = React.useMemo(() => {
        const excluded = new Set<string>();
        l3BySupplier.forEach((sb) => {
            const budget = l3Budgets[sb.supplier] ?? sb.max_cost;
            // Sve narudžbe gdje cumulative > budget → excluded (FIFO: najstarije prve = ostaju, mlađe otpadaju)
            sb.orders.forEach((o, i) => {
                if (sb.cumulative[i] > budget) excluded.add(o.order_number);
            });
        });
        return excluded;
    }, [l3BySupplier, l3Budgets]);

    function applyOverrides(allocs: ProposeResponse['allocations']) {
        return allocs
            .map((a, idx) => {
                const ov = overrides[idx];
                if (ov === 'exclude') return null;
                if (l3ExcludedOrders.has(a.order_number) && a.source_layer === 'L3') return null;
                if (ov === 'force_l3') return { ...a, source_layer: 'L3' as const, source_warehouse_code: null, source_cost_layer_id: null };
                return a;
            })
            .filter((a): a is NonNullable<typeof a> => a !== null);
    }

    async function commit() {
        if (!data) return;
        const finalAllocs = applyOverrides(data.allocations);
        if (!confirm(t(`Potvrditi plan: ${finalAllocs.length} alokacija (${data.allocations.length - finalAllocs.length} excluded)? Cost layeri će biti decrementirani.`, `Approve plan: ${finalAllocs.length} allocations (${data.allocations.length - finalAllocs.length} excluded)? Cost layers will be decremented.`))) return;
        setCommitting(true);
        try {
            const r = await fetch('/api/gold-cm-planner/delivery-plan/commit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    allocations: finalAllocs,
                    summary: data.summary,
                    notes: `Generated from m.40.4 UI · minMargin=${minMarginPct}% · overrides=${Object.values(overrides).filter(Boolean).length}`,
                }),
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const result = await r.json();
            setCommittedPlanId(result.plan_id);
        } catch (e) {
            alert(e instanceof Error ? e.message : 'API');
        } finally {
            setCommitting(false);
        }
    }

    const layerMeta = {
        L0: { color: '#1f7a36', label: t('L0 lokalno', 'L0 local'), bg: 'rgba(31,122,54,0.15)' },
        L1: { color: '#1e40af', label: t('L1 HUB', 'L1 HUB'), bg: 'rgba(30,64,175,0.15)' },
        L2: { color: 'var(--ad-acc-3)', label: t('L2 cross', 'L2 cross'), bg: 'rgba(138,104,23,0.15)' },
        L3: { color: '#b14040', label: t('L3 supplier', 'L3 supplier'), bg: 'rgba(177,64,64,0.15)' },
    } as const;

    return (
        <section>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <h3 style={{ margin: 0, color: 'var(--ad-ink)' }}>🚚 {t('Plan isporuka — waterfall L0→L1→L2→L3', 'Delivery plan — waterfall L0→L1→L2→L3')}</h3>
                    <p className="muted" style={{ margin: '4px 0 0', fontSize: '0.82rem' }}>{t('FIFO po starosti narudžbe. L0 same-warehouse, L1 HUB stock, L2 cross-warehouse, L3 supplier order.', 'FIFO by order age. L0 same-warehouse, L1 HUB stock, L2 cross-warehouse, L3 supplier order.')}</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button type="button" onClick={() => setShowFilters(!showFilters)} className="secondary compact" style={{ fontSize: '0.82rem' }}>
                        {showFilters ? '▼' : '▶'} 🔧 {t('Filteri', 'Filters')}
                    </button>
                    <label style={{ fontSize: '0.78rem' }} title={t('Ako RUC% < threshold, forsiraj L3 supplier umjesto lokalne zalihe', 'If RUC% < threshold, force L3 supplier instead of local stock')}>
                        {t('Min RUC %', 'Min GM %')}:
                        <input type="number" step="0.5" value={minMarginPct} onChange={(e) => setMinMarginPct(parseFloat(e.target.value) || 0)} style={{ width: 60, padding: '3px 6px', marginLeft: 4 }} />
                    </label>
                    <button type="button" onClick={() => void propose()} disabled={loading}>
                        {loading ? '⏳ ' + t('Generiram…', 'Generating…') : '🎯 ' + t('Generiraj plan', 'Generate plan')}
                    </button>
                </div>
            </div>

            {showFilters ? (
                <PlannerFiltersPanel
                    filters={filters}
                    setFilters={setFilters}
                    presets={presets}
                    activePresetName={activePresetName}
                    onLoadPreset={loadPreset}
                    onSavePreset={savePreset}
                    onDeletePreset={deletePreset}
                    locale={locale}
                    availableMints={availableMints}
                    onExportCsv={async () => {
                        if (filteredPreview.length === 0) { alert(t('Nema rezultata za export.', 'No results to export.')); return; }
                        try {
                            const res = await fetch('/api/gold-cm-planner/backlog/export.xlsx', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                body: JSON.stringify(filters),
                            });
                            if (!res.ok) throw new Error(`HTTP ${res.status}`);
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            const ts = new Date().toISOString().slice(0, 19).replaceAll(':', '-');
                            a.href = url;
                            a.download = `Gold_CM_Backlog_${ts}.xlsx`;
                            a.click();
                            URL.revokeObjectURL(url);
                        } catch (e) {
                            alert(e instanceof Error ? e.message : 'Export fail');
                        }
                    }}
                />
            ) : null}

            {/* SMART PRIJEDLOZI — klikabilni, primjenjuju akciju na filtere */}
            {backlogData?.by_order ? (() => {
                type Suggestion = { icon: string; msg: string; color: string; apply: () => void };
                const sugg: Suggestion[] = [];
                const today = Date.now();

                // 1) Stare narudžbe bez paid_full → klik isključuje iz plana
                const oldUnpaid = backlogData.by_order.filter((o: any) => {
                    if (!o.item_date) return false;
                    const days = Math.floor((today - new Date(o.item_date).getTime()) / 86400000);
                    return days > 60 && o.payment_status !== 'paid_full';
                });
                if (oldUnpaid.length >= 5) {
                    sugg.push({
                        icon: '⚠',
                        color: '#b14040',
                        msg: t(`${oldUnpaid.length} narudžbi >60d bez paid_full — isključi iz plana`, `${oldUnpaid.length} orders >60d without paid_full — exclude from plan`),
                        apply: () => {
                            const ids = oldUnpaid.map((o: any) => o.order_number);
                            setFilters({ ...filters, excluded_order_numbers: [...(filters.excluded_order_numbers ?? []), ...ids] });
                        },
                    });
                }

                // 2) Top supplier — fokus → set explicit order_numbers samo na taj supplier
                if (backlogData.by_supplier?.length > 0) {
                    const top = [...backlogData.by_supplier].sort((a: any, b: any) => b.items_count - a.items_count)[0];
                    if (top && top.items_count >= 25) {
                        sugg.push({
                            icon: '📌',
                            color: '#1e40af',
                            msg: t(`${top.items_count} stavki za ${top.supplier} — fokusiraj plan samo na njega`, `${top.items_count} items for ${top.supplier} — focus plan only on it`),
                            apply: () => {
                                const ids = backlogData.by_order.filter((o: any) => o.suppliers.includes(top.supplier)).map((o: any) => o.order_number);
                                setFilters({ ...filters, order_numbers: ids });
                            },
                        });
                    }
                }

                // 3) Coverage >60% lokalno → skip L2
                if (backlogData.coverage_stats?.orders_covered_full) {
                    const total = (backlogData.coverage_stats.orders_covered_full + (backlogData.coverage_stats.orders_partial ?? 0) + (backlogData.coverage_stats.orders_uncovered ?? 0)) || 1;
                    const fullPct = Math.round(100 * backlogData.coverage_stats.orders_covered_full / total);
                    if (fullPct >= 60 && (filters.allowed_layers ?? []).includes('L2')) {
                        sugg.push({
                            icon: '💡',
                            color: '#1f7a36',
                            msg: t(`${fullPct}% narudžbi pokriveno lokalno — isključi L2 transfere (manje kaosa)`, `${fullPct}% orders covered locally — disable L2 transfers (less chaos)`),
                            apply: () => {
                                setFilters({ ...filters, allowed_layers: (filters.allowed_layers ?? []).filter((l) => l !== 'L2') });
                            },
                        });
                    }
                }

                // 4) Egzotika EGZOTIKA — predloži isključiti (dugi lead time)
                const egzCount = backlogData.by_order.filter((o: any) => o.has_egzotika).length;
                if (egzCount > 5 && !filters.exclude_egzotika) {
                    sugg.push({
                        icon: '🐢',
                        color: 'var(--ad-acc-3)',
                        msg: t(`${egzCount} narudžbi s EGZOTIKOM (sporiji 60d) — isključi iz brzog plana`, `${egzCount} orders with EGZOTIKA (slow 60d) — exclude from fast plan`),
                        apply: () => setFilters({ ...filters, exclude_egzotika: true }),
                    });
                }

                // 5) Many small unpaid → cash-tight sort
                const smallUnpaid = backlogData.by_order.filter((o: any) => o.paid_pct < 30 && o.total_eur > 500).length;
                if (smallUnpaid >= 10 && filters.sort_mode !== 'cash_tight') {
                    sugg.push({
                        icon: '💰',
                        color: '#1e40af',
                        msg: t(`${smallUnpaid} narudžbi s avansom <30% — koristi Cash-tight sort (priority na collection)`, `${smallUnpaid} orders with advance <30% — use Cash-tight sort (collection priority)`),
                        apply: () => setFilters({ ...filters, sort_mode: 'cash_tight' }),
                    });
                }

                // 6) HITNO narudžbe (djelatnik označio prioritet) → fokus filter na njih
                const hitnoOrders = backlogData.by_order.filter((o: any) => o.hitno);
                if (hitnoOrders.length > 0) {
                    sugg.push({
                        icon: '🚨',
                        color: '#b14040',
                        msg: t(`${hitnoOrders.length} HITNIH narudžbi (djelatnik označio prioritet) — fokusiraj plan na njih`, `${hitnoOrders.length} URGENT orders — focus plan on them`),
                        apply: () => setFilters({ ...filters, order_numbers: hitnoOrders.map((o: any) => o.order_number) }),
                    });
                }

                // 7) Dogovoreni datumi isporuke — prioritet po roku
                const agreedOrders = backlogData.by_order.filter((o: any) => o.agreed_delivery_date);
                if (agreedOrders.length > 0) {
                    // najbliži datum
                    const soonest = agreedOrders.map((o: any) => o.agreed_delivery_date).sort()[0];
                    const [y, m, d] = String(soonest).split('-');
                    sugg.push({
                        icon: '📅',
                        color: '#1f7a36',
                        msg: t(`${agreedOrders.length} narudžbi s dogovorenim datumom isporuke (najbliži ${d}.${m}.${y}) — plan ih prioritizira`, `${agreedOrders.length} orders with agreed delivery date — plan prioritizes them`),
                        apply: () => setFilters({ ...filters, order_numbers: agreedOrders.map((o: any) => o.order_number) }),
                    });
                }

                if (sugg.length === 0) return null;
                return (
                    <div style={{ marginBottom: 14, padding: '10px 14px', background: 'linear-gradient(135deg, rgba(var(--ad-acc-rgb),0.08), rgba(107,79,28,0.04))', border: '1px solid rgba(var(--ad-acc-rgb),0.4)', borderRadius: 8 }}>
                        <strong style={{ fontSize: '0.82rem', color: 'var(--ad-ink)', display: 'block', marginBottom: 6 }}>💡 {t('Smart prijedlozi — klik primjenjuje', 'Smart suggestions — click to apply')}</strong>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {sugg.map((s, i) => (
                                <button key={i} type="button" onClick={s.apply} style={{
                                    background: '#fff', border: `1px solid ${s.color}40`, padding: '6px 10px', borderRadius: 4,
                                    textAlign: 'left', cursor: 'pointer', fontSize: '0.82rem', color: s.color,
                                    transition: 'background 0.15s',
                                }} onMouseEnter={(e) => (e.currentTarget.style.background = '#fafaf5')} onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}>
                                    {s.icon} {s.msg} <span style={{ float: 'right', fontWeight: 700 }}>→ {t('primijeni', 'apply')}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                );
            })() : null}

            {filters.excluded_order_numbers && filters.excluded_order_numbers.length > 0 ? (
                <div style={{ marginBottom: 10, padding: '6px 10px', background: 'rgba(177,64,64,0.06)', borderRadius: 4, fontSize: '0.78rem' }}>
                    ⛔ {t('Isključeno', 'Excluded')}: <strong>{filters.excluded_order_numbers.length}</strong> {t('narudžbi', 'orders')}
                    <button type="button" onClick={() => setFilters({ ...filters, excluded_order_numbers: undefined })} className="secondary compact" style={{ marginLeft: 8, fontSize: '0.72rem' }}>✕ {t('Vrati', 'Restore')}</button>
                </div>
            ) : null}

            {/* INLINE BACKLOG PREVIEW — supplier kartice + tablica narudžbi (osvježava na filter klik) */}
            {backlogData ? (
                <div style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
                        <h4 style={{ margin: 0, fontSize: '0.84rem', color: 'var(--ad-ink)' }}>
                            📋 {t('Pregled narudžbi za filter', 'Order preview for filter')}
                            <span className="muted" style={{ fontWeight: 400, marginLeft: 8 }}>
                                {filteredPreview.length} / {backlogData.by_order?.length ?? 0}
                                {previewSelected.size > 0 ? ` · ${previewSelected.size} selected` : ''}
                            </span>
                        </h4>
                        <button type="button" onClick={() => { void loadBacklog(); }} className="secondary compact" style={{ fontSize: '0.74rem' }}>🔁 {t('Osvježi', 'Refresh')}</button>
                    </div>

                    {/* Banner — ručno alocirane narudžbe (imam_ju) — uvijek vidljiv ako postoje */}
                    {(backlogData.summary?.manual_allocated_count ?? 0) > 0 ? (() => {
                        const fmt = (n: number) => Number(n).toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { maximumFractionDigits: 0 });
                        return (
                            <div style={{ marginBottom: 8, padding: '6px 10px', background: 'rgba(31,122,54,0.10)', border: '1px solid rgba(31,122,54,0.4)', borderRadius: 6, fontSize: '0.78rem', color: '#1f7a36' }}>
                                ✋ <strong>{backlogData.summary.manual_allocated_count}</strong> {t('narudžbi izvan planera — ručno alocirano u m.40.3', 'orders outside planner — manually allocated in m.40.3')}
                                ({t('ukupno', 'total')}: <strong>{fmt(backlogData.summary.manual_allocated_total_eur)} €</strong>).
                                <button type="button" onClick={() => {
                                    const list = (backlogData.manual_allocated_orders ?? []).map((o: any) => `${o.order_number} · ${o.partner_name ?? '—'} · ${(o.total_eur ?? 0).toLocaleString('hr-HR')} €`).join('\n');
                                    alert((t('Ručno alocirane narudžbe (isključene iz plana):\n\n', 'Manually allocated orders (excluded from plan):\n\n')) + list);
                                }} className="secondary compact" style={{ marginLeft: 8, fontSize: '0.72rem' }}>📋 {t('Lista', 'List')}</button>
                            </div>
                        );
                    })() : null}

                    {/* Banner — što je filter ispustio (infeasible pod allowed_layers, budget overspend) */}
                    {backlogData.summary?.orders_infeasible_dropped > 0 || backlogData.budget_selection ? (
                        <div style={{ marginBottom: 8, padding: '6px 10px', background: 'rgba(var(--ad-soft-rgb),0.13)', border: '1px solid rgba(var(--ad-soft-rgb),0.45)', borderRadius: 6, fontSize: '0.78rem' }}>
                            {backlogData.summary?.orders_infeasible_dropped > 0 ? (
                                <div>
                                    🚫 <strong>{backlogData.summary.orders_infeasible_dropped}</strong> {t('narudžbi isključeno', 'orders excluded')} —
                                    {t(' ne mogu se zatvoriti pod ', ' cannot fulfill under ')}
                                    <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3 }}>{(backlogData.filters_applied?.allowed_layers ?? []).join('+')}</code>
                                    {t(' (treba i drugi layer koji je isključen).', ' (require an excluded layer).')}
                                </div>
                            ) : null}
                            {backlogData.budget_selection ? (() => {
                                const fmt = (n: number) => Number(n).toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { maximumFractionDigits: 0 });
                                const bs = backlogData.budget_selection;
                                if (bs.mode === 'per_mint') {
                                    return (
                                        <div>
                                            🏭 {t('Budžet po kovnici', 'Budget per mint')} → {t('uključeno', 'included')} <strong>{bs.orders_included}</strong> {t('narudžbi', 'orders')}, {t('preskočeno', 'skipped')} <strong>{bs.orders_skipped}</strong>.
                                            <div style={{ marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                {(bs.per_mint ?? []).map((pm: any) => (
                                                    <span key={pm.mint} style={{ fontSize: '0.74rem' }}>
                                                        <strong>{pm.mint}</strong>: {fmt(pm.spent_eur)}/{fmt(pm.budget_eur)} € ({t('ostalo', 'left')} {fmt(pm.remaining_eur)})
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                }
                                return (
                                    <div>
                                        💰 {t('Budžet', 'Budget')} <strong>{fmt(bs.budget_eur)} €</strong> →
                                        {t(' uključeno ', ' included ')}<strong>{bs.orders_included}</strong> {t('narudžbi', 'orders')}
                                        ({fmt(bs.spent_eur)} €), {t('preostalo', 'remaining')} <strong>{fmt(bs.remaining_eur)} €</strong>,
                                        {t(' preskočeno ', ' skipped ')}<strong>{bs.orders_skipped}</strong>.
                                    </div>
                                );
                            })() : null}
                            {(() => {
                                const lc = backlogData.summary?.layer_counts ?? {};
                                const parts: string[] = [];
                                for (const lay of ['L0','L1','L2','L3'] as const) {
                                    if ((lc[lay] ?? 0) > 0) parts.push(`${lay}:${lc[lay]}`);
                                }
                                if (parts.length === 0) return null;
                                return (
                                    <div style={{ fontSize: '0.74rem', color: 'var(--ad-ink)', marginTop: 4 }}>
                                        📊 {t('Narudžbi po potrebnom layeru', 'Orders by required layer')}: {parts.join(' · ')}
                                    </div>
                                );
                            })()}
                        </div>
                    ) : null}

                    {/* KPI mini strip iz filteredPreview */}
                    {(() => {
                        const totalOrders = filteredPreview.length;
                        const totalItems = filteredPreview.reduce((s, o) => s + (o.items_count ?? 0), 0);
                        const totalEur = filteredPreview.reduce((s, o) => s + (o.total_eur ?? 0), 0);
                        const totalRuc = filteredPreview.reduce((s, o) => s + (o.ruc_estimate_eur ?? 0), 0);
                        const fullCovered = filteredPreview.filter((o) => o.coverage === 'full').length;
                        const uncovered = filteredPreview.filter((o) => o.coverage === 'none').length;
                        const fmt = (n: number) => Number(n).toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { maximumFractionDigits: 0 });
                        return (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 6, marginBottom: 8 }}>
                                <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 4, padding: '4px 8px' }}>
                                    <div style={{ fontSize: '0.66rem', textTransform: 'uppercase', color: 'rgba(0,0,0,0.5)' }}>{t('Narudžbi', 'Orders')}</div>
                                    <strong>{totalOrders}</strong>
                                </div>
                                <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 4, padding: '4px 8px' }}>
                                    <div style={{ fontSize: '0.66rem', textTransform: 'uppercase', color: 'rgba(0,0,0,0.5)' }}>{t('Stavki', 'Items')}</div>
                                    <strong>{totalItems}</strong>
                                </div>
                                <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 4, padding: '4px 8px' }}>
                                    <div style={{ fontSize: '0.66rem', textTransform: 'uppercase', color: 'rgba(0,0,0,0.5)' }}>{t('Prodajno', 'Sales')}</div>
                                    <strong>{fmt(totalEur)} €</strong>
                                </div>
                                <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 4, padding: '4px 8px' }}>
                                    <div style={{ fontSize: '0.66rem', textTransform: 'uppercase', color: 'rgba(0,0,0,0.5)' }}>{t('RUC est.', 'GM est.')}</div>
                                    <strong style={{ color: totalRuc >= 0 ? '#1f7a36' : '#b14040' }}>{fmt(totalRuc)} €</strong>
                                </div>
                                <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 4, padding: '4px 8px' }}>
                                    <div style={{ fontSize: '0.66rem', textTransform: 'uppercase', color: 'rgba(0,0,0,0.5)' }}>✓ {t('Pokriveno', 'Covered')}</div>
                                    <strong style={{ color: '#1f7a36' }}>{fullCovered}</strong>
                                </div>
                                <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 4, padding: '4px 8px' }}>
                                    <div style={{ fontSize: '0.66rem', textTransform: 'uppercase', color: 'rgba(0,0,0,0.5)' }}>✗ {t('Treba naručit', 'Need order')}</div>
                                    <strong style={{ color: '#b14040' }}>{uncovered}</strong>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Supplier kartice — klikom filtriraju listu */}
                    {backlogData.by_supplier?.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                            {backlogData.by_supplier.map((s: any) => {
                                const active = previewActiveSuppliers.has(s.supplier);
                                return (
                                    <button key={s.supplier} type="button" onClick={() => togglePreviewSupplier(s.supplier)}
                                        style={{
                                            background: active ? 'rgba(31,122,54,0.13)' : '#fff',
                                            border: '1px solid ' + (active ? '#1f7a36' : 'rgba(var(--ad-soft-rgb),0.4)'),
                                            color: active ? '#1f7a36' : '#444',
                                            padding: '4px 10px', borderRadius: 6, fontSize: '0.78rem', cursor: 'pointer',
                                            fontWeight: active ? 700 : 400,
                                        }}
                                    >
                                        {active ? '✓ ' : ''}{s.supplier} · {s.items_count}
                                    </button>
                                );
                            })}
                            {previewActiveSuppliers.size > 0 ? (
                                <button type="button" onClick={() => setPreviewActiveSuppliers(new Set())} className="secondary compact" style={{ fontSize: '0.72rem' }}>✕ {t('Očisti', 'Clear')}</button>
                            ) : null}
                        </div>
                    ) : null}

                    {/* Tablica narudžbi — sortable + multi-select + click row = drawer */}
                    <div className="table-wrap" style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 6 }}>
                        <table className="admin-user-table" style={{ fontSize: '0.76rem' }}>
                            <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                                <tr>
                                    <th style={{ width: 24 }}>
                                        <input type="checkbox" checked={filteredPreview.length > 0 && filteredPreview.every((o) => previewSelected.has(o.order_number))} onChange={(e) => {
                                            if (e.target.checked) setPreviewSelected(new Set(filteredPreview.map((o) => o.order_number)));
                                            else setPreviewSelected(new Set());
                                        }} />
                                    </th>
                                    <th onClick={() => togglePreviewSort('date')} style={{ cursor: 'pointer' }}>{t('Datum', 'Date')} {previewSortKey === 'date' ? (previewSortDir === 'asc' ? '▲' : '▼') : ''}</th>
                                    <th onClick={() => togglePreviewSort('order')} style={{ cursor: 'pointer' }}>{t('Narudžba', 'Order')} {previewSortKey === 'order' ? (previewSortDir === 'asc' ? '▲' : '▼') : ''}</th>
                                    <th onClick={() => togglePreviewSort('partner')} style={{ cursor: 'pointer' }}>{t('Partner', 'Partner')} {previewSortKey === 'partner' ? (previewSortDir === 'asc' ? '▲' : '▼') : ''}</th>
                                    <th>{t('Posl.', 'Branch')}</th>
                                    <th onClick={() => togglePreviewSort('items')} style={{ cursor: 'pointer', textAlign: 'right' }}>{t('Stavki', 'Items')}</th>
                                    <th onClick={() => togglePreviewSort('total')} style={{ cursor: 'pointer', textAlign: 'right' }}>EUR</th>
                                    <th onClick={() => togglePreviewSort('pct')} style={{ cursor: 'pointer', textAlign: 'right' }}>Paid%</th>
                                    <th onClick={() => togglePreviewSort('ruc')} style={{ cursor: 'pointer', textAlign: 'right' }}>RUC</th>
                                    <th>Cov</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPreview.slice(0, 200).map((o: any) => {
                                    const covIcon = o.coverage === 'full' ? '✓' : o.coverage === 'partial' ? '◐' : o.coverage === 'none' ? '✗' : '—';
                                    const covColor = o.coverage === 'full' ? '#1f7a36' : o.coverage === 'partial' ? 'var(--ad-acc-3)' : o.coverage === 'none' ? '#b14040' : '#888';
                                    const selected = previewSelected.has(o.order_number);
                                    const transferred = !!o.transferred_elsewhere; // "prebačeno dalje" — sivo/neaktivno
                                    const rowBg = transferred ? 'rgba(0,0,0,0.04)' : (o.hitno ? 'rgba(177,64,64,0.07)' : (selected ? 'rgba(30,64,175,0.05)' : undefined));
                                    const nacinLbl: Record<string, string> = { u_poslovnici: t('U poslovnici', 'At branch'), vozac: t('Vozač', 'Driver'), gls_posta: 'GLS', centralno: t('Centralno', 'Central') };
                                    return (
                                        <tr key={o.order_number} onClick={() => setDrawerOrder(o.order_number)} title={transferred ? t('Prebačeno dalje — isporuka na: ', 'Transferred — delivery to: ') + (o.mjesto_isporuke ?? '') : undefined} style={{ cursor: 'pointer', background: rowBg, opacity: transferred ? 0.55 : 1, borderLeft: transferred ? '3px solid #9aa0a6' : (o.hitno ? '3px solid #b14040' : undefined) }}>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <input type="checkbox" checked={selected} onChange={() => togglePreviewSelect(o.order_number)} />
                                            </td>
                                            <td className="muted" style={{ fontSize: '0.72rem' }}>{o.item_date?.slice(0, 10) ?? '—'}</td>
                                            <td style={{ fontSize: '0.72rem' }}>
                                                {transferred ? <span title={t('Prebačeno dalje (isporuka drugdje)', 'Transferred elsewhere')} style={{ marginRight: 3 }}>🔀</span> : null}
                                                {o.hitno ? <span title={t('HITNO — prioritet', 'URGENT')} style={{ marginRight: 3 }}>🚨</span> : null}
                                                {o.agreed_delivery_date ? <span title={t('Dogovoren datum isporuke: ', 'Agreed delivery: ') + (() => { const [y,m,d] = String(o.agreed_delivery_date).split('-'); return `${d}.${m}.${y}`; })()} style={{ marginRight: 3 }}>📅</span> : null}
                                                {o.nacin_isporuke ? <span title={t('Način isporuke', 'Delivery method')} style={{ marginRight: 3, fontSize: '0.66rem', background: 'rgba(30,64,175,0.12)', color: '#1e40af', borderRadius: 6, padding: '0 4px' }}>🚚 {nacinLbl[o.nacin_isporuke] ?? o.nacin_isporuke}</span> : null}
                                                {o.order_number}
                                                {transferred && o.mjesto_isporuke ? <span style={{ marginLeft: 4, fontSize: '0.66rem', color: '#6b6b6b' }}>→ {o.mjesto_isporuke}</span> : null}
                                            </td>
                                            <td>{(o.partner_name ?? '—').slice(0, 30)}</td>
                                            <td style={{ fontSize: '0.72rem' }}>{o.branch ?? '—'}</td>
                                            <td style={{ textAlign: 'right' }}>{o.items_count}</td>
                                            <td style={{ textAlign: 'right', fontWeight: 600 }}>{Number(o.total_eur).toFixed(0)} €</td>
                                            <td style={{ textAlign: 'right' }}>{Math.round(o.paid_pct)}%</td>
                                            <td style={{ textAlign: 'right', color: o.ruc_estimate_eur >= 0 ? '#1f7a36' : '#b14040', fontWeight: 600 }}>{Number(o.ruc_estimate_eur).toFixed(0)} €</td>
                                            <td style={{ color: covColor, fontWeight: 700, fontSize: '0.86rem' }}>{covIcon}</td>
                                        </tr>
                                    );
                                })}
                                {filteredPreview.length === 0 ? (
                                    <tr><td colSpan={10} className="muted" style={{ textAlign: 'center', padding: 14 }}>{t('Nema narudžbi za trenutne filtere.', 'No orders match current filters.')}</td></tr>
                                ) : null}
                                {filteredPreview.length > 200 ? (
                                    <tr><td colSpan={10} className="muted" style={{ textAlign: 'center', padding: 8, fontSize: '0.72rem' }}>+{filteredPreview.length - 200} {t('više…', 'more…')}</td></tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>

                    {previewSelected.size > 0 ? (
                        <div style={{ marginTop: 8, padding: '6px 10px', background: 'rgba(30,64,175,0.06)', borderRadius: 4, fontSize: '0.78rem' }}>
                            📌 <strong>{previewSelected.size}</strong> {t('selektirano — Generiraj plan koristit će SAMO ove', 'selected — Generate plan will use ONLY these')}
                            <button type="button" onClick={() => {
                                setFilters({ ...filters, order_numbers: Array.from(previewSelected) });
                            }} className="secondary compact" style={{ marginLeft: 8, fontSize: '0.72rem' }}>{t('Primijeni u filter', 'Apply to filter')}</button>
                            <button type="button" onClick={() => setPreviewSelected(new Set())} className="secondary compact" style={{ marginLeft: 4, fontSize: '0.72rem' }}>✕</button>
                        </div>
                    ) : null}
                </div>
            ) : backlogLoading ? <p className="muted" style={{ fontSize: '0.84rem' }}>{t('Učitavam backlog preview…', 'Loading backlog preview…')}</p> : null}

            {error ? <p className="error">{error}</p> : null}

            {/* Pinned banner: aktivan plan (default = approved) + dropdown za izabrati drugi.
                Izabrani plan postaje aktivan view — sve PDF/XLSX akcije rade nad njim. */}
            {savedPlans.length > 0 ? (() => {
                const approved = savedPlans.find((p) => p.status === 'approved');
                const activeId = selectedPlanId ?? approved?.id ?? savedPlans[0]?.id;
                const active = savedPlans.find((p) => p.id === activeId);
                const others = savedPlans.filter((p) => p.id !== active?.id);
                return (
                    <div style={{ marginBottom: 12, padding: '10px 14px', background: 'linear-gradient(135deg, rgba(31,122,54,0.06), rgba(var(--ad-acc-rgb),0.04))', border: '1.5px solid var(--ad-acc)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        {active ? (
                            <>
                                <span style={{ background: 'rgba(31,122,54,0.18)', color: '#1f7a36', padding: '3px 10px', borderRadius: 4, fontSize: '0.74rem', fontWeight: 700 }}>✓ AKTIVAN</span>
                                <strong style={{ fontSize: '0.86rem' }}>Plan #{active.id}</strong>
                                <span className="muted" style={{ fontSize: '0.78rem' }}>
                                    {active.plan_date} · {active.total_allocations} alok. · RUC <strong style={{ color: Number(active.total_ruc_eur) >= 0 ? '#1f7a36' : '#b14040' }}>{Number(active.total_ruc_eur).toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { maximumFractionDigits: 0 })} €</strong>
                                </span>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginLeft: 'auto' }}>
                                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', background: 'rgba(31,122,54,0.06)', padding: '4px 8px', borderRadius: 5, border: '1px solid rgba(31,122,54,0.3)' }}>
                                        <strong style={{ minWidth: 145, fontSize: '0.78rem', color: '#1f7a36' }}>🚚 {t('Isporuke kupcu', 'Customer deliveries')}</strong>
                                        <button type="button" onClick={() => void downloadDeliverable(active.id, 'isporuke', 'pdf')} style={{ background: '#1f7a36', color: '#fff', padding: '4px 10px', borderRadius: 4, border: 'none', fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer' }}>📄 PDF</button>
                                        <button type="button" onClick={() => void downloadDeliverable(active.id, 'isporuke', 'xlsx')} style={{ background: '#1f7a36', color: '#fff', padding: '4px 10px', borderRadius: 4, border: 'none', fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer' }}>📊 XLSX</button>
                                    </div>
                                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', background: 'rgba(138,104,23,0.06)', padding: '4px 8px', borderRadius: 5, border: '1px solid rgba(138,104,23,0.3)' }}>
                                        <strong style={{ minWidth: 145, fontSize: '0.78rem', color: 'var(--ad-acc-3)' }}>📦 {t('Plan preskladištenja', 'Restocking plan')}</strong>
                                        {(filters.min_transfer_eur ?? 0) > 0 ? <span style={{ fontSize: '0.7rem', color: 'var(--ad-acc-3)' }} title="Filter G · Vrijednost">≥{filters.min_transfer_eur} €</span> : null}
                                        <button type="button" onClick={() => void downloadDeliverable(active.id, 'preskladistenje', 'pdf')} style={{ background: 'var(--ad-acc-3)', color: '#fff', padding: '4px 10px', borderRadius: 4, border: 'none', fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer' }}>📄 PDF</button>
                                        <button type="button" onClick={() => void downloadDeliverable(active.id, 'preskladistenje', 'xlsx')} style={{ background: 'var(--ad-acc-3)', color: '#fff', padding: '4px 10px', borderRadius: 4, border: 'none', fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer' }}>📊 XLSX</button>
                                    </div>
                                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', background: 'rgba(177,64,64,0.06)', padding: '4px 8px', borderRadius: 5, border: '1px solid rgba(177,64,64,0.3)' }}>
                                        <strong style={{ minWidth: 145, fontSize: '0.78rem', color: '#b14040' }}>📋 {t('Prijedlog narudžbe', 'Supplier order proposal')}</strong>
                                        <button type="button" onClick={() => void openOrderProposal(active.id)} style={{ background: '#fff', color: '#b14040', padding: '4px 10px', borderRadius: 4, border: '1.5px solid #b14040', fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer' }}>👁 {t('Pregled & Potvrdi', 'Preview & Confirm')}</button>
                                        <button type="button" onClick={() => void downloadDeliverable(active.id, 'narudzbe', 'pdf')} style={{ background: '#b14040', color: '#fff', padding: '4px 10px', borderRadius: 4, border: 'none', fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer' }}>📄 PDF</button>
                                        <button type="button" onClick={() => void downloadDeliverable(active.id, 'narudzbe', 'xlsx')} style={{ background: '#b14040', color: '#fff', padding: '4px 10px', borderRadius: 4, border: 'none', fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer' }}>📊 XLSX</button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <span className="muted" style={{ fontSize: '0.84rem' }}>{t('Nema aktivnog plana', 'No active plan')}</span>
                        )}
                        {savedPlans.length > 1 ? (
                            <select
                                value={active?.id ?? ''}
                                onChange={(e) => {
                                    const id = parseInt(e.target.value);
                                    setSelectedPlanId(Number.isFinite(id) ? id : null);
                                }}
                                title={t('Odaberi plan — svi PDF/XLSX buttoni rade nad odabranim planom.', 'Pick a plan — all PDF/XLSX buttons act on selected plan.')}
                                style={{ marginLeft: 'auto', padding: '4px 8px', fontSize: '0.78rem' }}
                            >
                                {savedPlans.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.id === approved?.id ? '✓ ' : ''}#{p.id} · {p.plan_date} · {p.status} · {p.total_allocations} alok.
                                    </option>
                                ))}
                            </select>
                        ) : null}
                    </div>
                );
            })() : null}

            {data ? (
                <>
                    {/* Phased view banner — kad engine v2 vraća phases */}
                    {data.phases ? (
                        <div style={{ marginBottom: 14, padding: '12px 16px', background: 'linear-gradient(135deg, rgba(31,122,54,0.08), rgba(var(--ad-acc-rgb),0.06))', border: '1.5px solid var(--ad-acc)', borderRadius: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <strong style={{ fontSize: '0.92rem', color: 'var(--ad-ink)' }}>🚚 {t('Phased plan — kad što stiže', 'Phased plan — what arrives when')}</strong>
                                <div style={{ display: 'flex', gap: 4 }}>
                                    <label style={{ fontSize: '0.78rem' }}>
                                        Sort:
                                        <select value={filters.sort_mode ?? 'fifo_age'} onChange={(e) => { setFilters({ ...filters, sort_mode: e.target.value as any }); void propose(); }} style={{ marginLeft: 4, padding: '2px 4px', fontSize: '0.78rem' }}>
                                            <option value="fifo_age">📅 {t('FIFO (najstarije)', 'FIFO (oldest)')}</option>
                                            <option value="cash_tight">💰 {t('Cash-tight (min avans)', 'Cash-tight (min advance)')}</option>
                                        </select>
                                    </label>
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                                {[1, 2, 3, 4].map((p) => {
                                    const ph = data.phases?.[String(p)];
                                    if (!ph) return null;
                                    const colors: any = { 1: '#1f7a36', 2: '#1e40af', 3: 'var(--ad-acc-3)', 4: '#b14040' };
                                    return (
                                        <div key={p} style={{ background: '#fff', border: `2px solid ${colors[p]}`, borderRadius: 6, padding: '8px 10px' }}>
                                            <div style={{ fontSize: '0.7rem', color: colors[p], fontWeight: 700, textTransform: 'uppercase' }}>{ph.label}</div>
                                            <strong style={{ fontSize: '1.3rem' }}>{ph.order_count}</strong>
                                            <div className="muted" style={{ fontSize: '0.72rem' }}>{t('narudžbi', 'orders')} · {ph.date}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}

                    {/* Summary KPIs */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 14 }}>
                        <KpiCard label={t('Narudžbi', 'Orders')} value={String(data.summary.total_orders)} sublabel={`${data.allocations.length} ${t('alokacija', 'allocations')}`} accent="var(--ad-ink)" />
                        <KpiCard label={t('Prodajno', 'Sales')} value={data.summary.total_sales_eur.toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { maximumFractionDigits: 0 }) + ' €'} accent="#1f7a36" />
                        <KpiCard label={t('Nabavno', 'Cost')} value={data.summary.total_cost_eur.toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { maximumFractionDigits: 0 }) + ' €'} accent="var(--ad-acc-3)" />
                        <KpiCard label={t('RUC', 'GM')} value={data.summary.total_ruc_eur.toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { maximumFractionDigits: 0 }) + ' €'} sublabel={data.summary.total_sales_eur > 0 ? (100 * data.summary.total_ruc_eur / data.summary.total_sales_eur).toFixed(1) + '%' : ''} accent={data.summary.total_ruc_eur >= 0 ? '#1f7a36' : '#b14040'} />
                    </div>

                    {/* Layer breakdown */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                        {(['L0', 'L1', 'L2', 'L3'] as const).map((l) => {
                            const m = layerMeta[l];
                            const qty = data.summary[`${l.toLowerCase()}_qty` as 'l0_qty'];
                            return (
                                <div key={l} style={{ flex: 1, minWidth: 140, background: m.bg, borderRadius: 8, padding: '10px 14px' }}>
                                    <div style={{ fontSize: '0.78rem', color: m.color, fontWeight: 700 }}>{m.label}</div>
                                    <strong style={{ fontSize: '1.2rem' }}>{qty} kom</strong>
                                </div>
                            );
                        })}
                    </div>

                    {/* Commit action */}
                    {!committedPlanId ? (
                        <div style={{
                            background: 'linear-gradient(135deg, #fffbeb 0%, rgba(254,243,199,0.4) 100%)',
                            border: '1.5px solid var(--ad-acc)', borderRadius: 10, padding: '12px 16px', marginBottom: 12,
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8,
                        }}>
                            <span style={{ color: 'var(--ad-ink)' }}>{t('Plan je u draft-u. Klikni "Potvrdi" da ga spremiš u DB i decrementiraš cost layere.', 'Plan is in draft. Click "Approve" to save to DB and decrement cost layers.')}</span>
                            <button type="button" onClick={() => void commit()} disabled={committing} style={{ background: 'linear-gradient(135deg, #1f7a36, #2f9a4a)', color: '#fff', border: 0, padding: '6px 16px', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
                                {committing ? '⏳ ' + t('Spremam…', 'Saving…') : '✓ ' + t('Potvrdi plan', 'Approve plan')}
                            </button>
                        </div>
                    ) : (
                        <div style={{ background: 'rgba(31,122,54,0.10)', border: '1px solid rgba(31,122,54,0.4)', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
                            <strong style={{ color: '#1f7a36' }}>✓ {t(`Plan #${committedPlanId} kreiran u DB-u`, `Plan #${committedPlanId} saved`)}</strong>
                        </div>
                    )}

                    {/* Per order summary */}
                    <h4 style={{ margin: '14px 0 6px', fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>{t('Po narudžbi (top 50)', 'Per order (top 50)')}</h4>
                    <div className="table-wrap">
                        <table className="admin-user-table" style={{ fontSize: '0.82rem' }}>
                            <thead>
                                <tr>
                                    <th>{t('Narudžba', 'Order')}</th>
                                    <th>{t('Partner', 'Partner')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Stavki', 'Items')}</th>
                                    <th>{t('Layeri', 'Layers')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Prodajno', 'Sales')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Nabavno', 'Cost')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('RUC', 'GM')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.by_order.slice(0, 50).map((o) => (
                                    <tr key={o.order_number} onClick={() => setDrawerOrder(o.order_number)} style={{ cursor: 'pointer' }} title={t('Klik za detalje alokacija', 'Click for allocation details')}>
                                        <td style={{ fontSize: '0.78rem', color: '#1e40af', textDecoration: 'underline' }}>{o.order_number}</td>
                                        <td>{o.partner_name ?? '—'}</td>
                                        <td style={{ textAlign: 'right' }}>{o.items_count}</td>
                                        <td>
                                            {Object.entries(o.layers).map(([l, c]) => (
                                                <span key={l} style={{ background: layerMeta[l as 'L0'].bg, color: layerMeta[l as 'L0'].color, padding: '1px 6px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700, marginRight: 3 }}>{l}×{c}</span>
                                            ))}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>{o.sales.toFixed(2)} €</td>
                                        <td style={{ textAlign: 'right' }}>{o.cost.toFixed(2)} €</td>
                                        <td style={{ textAlign: 'right', fontWeight: 700, color: o.ruc >= 0 ? '#1f7a36' : '#b14040' }}>{o.ruc.toFixed(2)} €</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* AKCIJSKA LISTA — Uncovered narudžbe (filter excluded ili layer L3 disabled) */}
                    {data.uncovered && data.uncovered.length > 0 ? (
                        <div style={{ marginTop: 14, marginBottom: 14, background: 'rgba(177,64,64,0.05)', border: '1.5px solid #b14040', borderRadius: 8, padding: '12px 16px' }}>
                            <h4 style={{ margin: '0 0 6px', fontSize: '0.84rem', color: '#b14040' }}>
                                ⚠ {t('AKCIJSKA LISTA — nepokrivene narudžbe', 'ACTION LIST — uncovered orders')} ({data.uncovered.length})
                            </h4>
                            <p className="muted" style={{ fontSize: '0.74rem', margin: '0 0 8px' }}>
                                {t('Ove narudžbe nisu pokrivene postojećim layerima (ili filter ih je odbio). Razmotri narudžbu od dobavljača preko Preporuke.',
                                  'These orders are not covered by current layers (or filter rejected them). Consider supplier order via Recommendation.')}
                            </p>
                            <div className="table-wrap" style={{ maxHeight: 280, overflowY: 'auto' }}>
                                <table className="admin-user-table" style={{ fontSize: '0.76rem' }}>
                                    <thead>
                                        <tr>
                                            <th>{t('Narudžba', 'Order')}</th>
                                            <th>{t('Partner', 'Partner')}</th>
                                            <th>{t('Poslovnica', 'Branch')}</th>
                                            <th>{t('Artikl', 'Item')}</th>
                                            <th style={{ textAlign: 'right' }}>{t('Treba', 'Need')}</th>
                                            <th>{t('Predloženi dobavljač', 'Suggested supplier')}</th>
                                            <th>{t('Razlog', 'Reason')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.uncovered.map((u, i) => (
                                            <tr key={i}>
                                                <td style={{ fontSize: '0.72rem' }}>{u.order_number}</td>
                                                <td>{u.partner_name?.slice(0, 24) ?? '—'}</td>
                                                <td style={{ fontSize: '0.72rem' }}>{u.branch_code ?? '—'}</td>
                                                <td>{u.item_name?.slice(0, 36) ?? '—'}</td>
                                                <td style={{ textAlign: 'right' }}>{u.qty_needed}</td>
                                                <td style={{ fontSize: '0.74rem', fontWeight: 600 }}>{u.suggested_supplier ?? '—'}</td>
                                                <td style={{ fontSize: '0.7rem', color: '#b14040' }}>{u.reason}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : null}

                    {/* L3 SUPPLIER SLIDERS — per dobavljač budget cutoff (FIFO age, najstarije ostaju) */}
                    {l3BySupplier.length > 0 ? (
                        <div style={{ marginBottom: 16, padding: '12px 16px', background: 'linear-gradient(135deg, rgba(177,64,64,0.06), rgba(var(--ad-acc-rgb),0.04))', border: '1.5px solid #b14040', borderRadius: 8 }}>
                            <h4 style={{ margin: '0 0 6px', fontSize: '0.86rem', color: '#b14040' }}>
                                🎚 {t('L3 budget per dobavljač — koliko želiš potrošiti?', 'L3 budget per supplier — how much to spend?')}
                            </h4>
                            <p className="muted" style={{ fontSize: '0.74rem', margin: '0 0 10px' }}>
                                {t('Slider od 0 do max. Najstarije narudžbe ostaju, mlađe otpadaju kad spustiš budget. Ukupno excluded: ', 'Slider 0 to max. Oldest orders stay, newer drop when you lower budget. Total excluded: ')}
                                <strong style={{ color: '#b14040' }}>{l3ExcludedOrders.size}</strong>
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {l3BySupplier.map((sb) => {
                                    const budget = l3Budgets[sb.supplier] ?? sb.max_cost;
                                    const covered = sb.orders.filter((_, i) => sb.cumulative[i] <= budget).length;
                                    const fmt = (n: number) => Number(n).toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { maximumFractionDigits: 0 });
                                    return (
                                        <div key={sb.supplier} style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 6, padding: '8px 12px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, gap: 8, flexWrap: 'wrap' }}>
                                                <strong style={{ fontSize: '0.86rem' }}>{sb.supplier}</strong>
                                                <span style={{ fontSize: '0.78rem' }}>
                                                    <strong style={{ color: '#1f7a36' }}>{covered}</strong>/{sb.orders.length} {t('narudžbi', 'orders')} ·
                                                    <strong> {fmt(budget)} €</strong> / {fmt(sb.max_cost)} €
                                                </span>
                                            </div>
                                            <input type="range" min="0" max={Math.ceil(sb.max_cost)} step={Math.max(100, Math.floor(sb.max_cost / 100))}
                                                value={budget}
                                                onChange={(e) => setL3Budgets({ ...l3Budgets, [sb.supplier]: parseFloat(e.target.value) })}
                                                style={{ width: '100%', accentColor: '#b14040' }}
                                            />
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: 'rgba(0,0,0,0.5)' }}>
                                                <span>0 €</span>
                                                <button type="button" onClick={() => setL3Budgets({ ...l3Budgets, [sb.supplier]: sb.max_cost })} className="secondary compact" style={{ fontSize: '0.7rem', padding: '1px 6px' }}>Max</button>
                                                <span>{fmt(sb.max_cost)} €</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}

                    {/* Per-allocation override panel + commit (B1 Operator override) */}
                    <h4 style={{ margin: '14px 0 6px', fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>
                        🔧 {t('Operator override per alokacija', 'Operator override per allocation')}
                        <span style={{ fontSize: '0.7rem', color: 'var(--ad-acc-3)', marginLeft: 8 }}>
                            ({Object.values(overrides).filter(Boolean).length} {t('overrides', 'overrides')})
                        </span>
                    </h4>

                    <div style={{ marginBottom: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input
                            type="text"
                            value={overrideSearch}
                            onChange={(e) => setOverrideSearch(e.target.value)}
                            placeholder={t('🔍 Pretraga: korisnik / partner / broj ponude / artikl / skladište / supplier…', '🔍 Search: customer / partner / offer # / item / warehouse / supplier…')}
                            style={{ flex: 1, minWidth: 320, padding: '6px 10px', fontSize: '0.84rem', border: '1px solid #ddd4c1', borderRadius: 4 }}
                        />
                        {overrideSearch ? (
                            <button type="button" onClick={() => setOverrideSearch('')} className="secondary compact" style={{ fontSize: '0.74rem' }}>
                                ✕ {t('Očisti', 'Clear')}
                            </button>
                        ) : null}
                        <span className="muted" style={{ fontSize: '0.74rem' }}>
                            {(() => {
                                const q = overrideSearch.toLowerCase().trim();
                                const cnt = q ? data.allocations.filter((a) => `${a.order_number} ${a.partner_name ?? ''} ${a.item_name ?? ''} ${a.source_warehouse_code ?? ''} ${a.source_supplier_name ?? ''}`.toLowerCase().includes(q)).length : data.allocations.length;
                                return q ? `${cnt} / ${data.allocations.length} ${t('match', 'match')}` : `${data.allocations.length} ${t('alokacija', 'allocations')}`;
                            })()}
                        </span>
                    </div>
                    <div className="table-wrap" style={{ maxHeight: 480, overflowY: 'auto', marginBottom: 14 }}>
                        <table className="admin-user-table" style={{ fontSize: '0.74rem' }}>
                            <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                                <tr>
                                    <th>{t('Narudžba', 'Order')}</th>
                                    <th>{t('Artikl', 'Item')}</th>
                                    <th>L</th>
                                    <th>SLA</th>
                                    <th>{t('Skladište', 'WH')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Kol.', 'Qty')}</th>
                                    <th style={{ textAlign: 'right' }}>RUC</th>
                                    <th style={{ textAlign: 'right' }}>RUC%</th>
                                    <th>{t('Override', 'Override')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.allocations.map((a, idx) => {
                                    const rucPct = a.sale_price_eur > 0 ? (a.ruc_estimate_eur / a.sale_price_eur) * 100 : 0;
                                    const ov = overrides[idx];
                                    const lm = layerMeta[a.source_layer];
                                    const q = overrideSearch.toLowerCase().trim();
                                    if (q) {
                                        const hay = `${a.order_number} ${a.partner_name ?? ''} ${a.item_name ?? ''} ${a.source_warehouse_code ?? ''} ${a.source_supplier_name ?? ''}`.toLowerCase();
                                        if (!hay.includes(q)) return null;
                                    }
                                    return (
                                        <tr key={idx} style={ov === 'exclude' ? { opacity: 0.4, textDecoration: 'line-through' } : ov === 'force_l3' ? { background: 'rgba(177,64,64,0.05)' } : undefined}>
                                            <td style={{ fontSize: '0.7rem' }}>{a.order_number}</td>
                                            <td>{a.item_name?.slice(0, 30)}</td>
                                            <td>
                                                <span style={{ background: lm.bg, color: lm.color, padding: '1px 5px', borderRadius: 3, fontSize: '0.7rem', fontWeight: 700 }}>{a.source_layer}</span>
                                                {a.is_manual_reservation ? <span style={{ marginLeft: 4, background: 'rgba(31,122,54,0.18)', color: '#1f7a36', padding: '1px 4px', borderRadius: 3, fontSize: '0.62rem', fontWeight: 700 }} title="Poslovnica označila — ručna alokacija">🔒 RUČNO</span> : null}
                                            </td>
                                            <td style={{ fontSize: '0.7rem' }}>
                                                {a.sla_days ?? '—'}d
                                                {a.sla_breach ? <span style={{ marginLeft: 3, color: '#b14040', fontWeight: 700 }}>⚠</span> : null}
                                            </td>
                                            <td style={{ fontSize: '0.7rem' }}>{a.source_warehouse_code ?? a.source_supplier_name ?? '—'}</td>
                                            <td style={{ textAlign: 'right' }}>{a.qty}</td>
                                            <td style={{ textAlign: 'right' }}>{a.ruc_estimate_eur.toFixed(2)} €</td>
                                            <td style={{ textAlign: 'right', color: rucPct >= (minMarginPct || 0) ? '#1f7a36' : '#b14040', fontWeight: 700 }}>{rucPct.toFixed(1)}%</td>
                                            <td style={{ display: 'flex', gap: 3 }}>
                                                {a.source_layer !== 'L3' ? (
                                                    <button type="button" className={ov === 'force_l3' ? 'compact' : 'secondary compact'} style={{ fontSize: '0.7rem', padding: '2px 5px' }}
                                                        onClick={() => setOverrides({ ...overrides, [idx]: ov === 'force_l3' ? null : 'force_l3' })}
                                                    >→L3</button>
                                                ) : null}
                                                <button type="button" className={ov === 'exclude' ? 'compact' : 'secondary compact'} style={{ fontSize: '0.7rem', padding: '2px 5px', color: ov === 'exclude' ? '#fff' : '#b14040' }}
                                                    onClick={() => setOverrides({ ...overrides, [idx]: ov === 'exclude' ? null : 'exclude' })}
                                                >✗</button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, padding: '10px 14px', background: 'rgba(31,122,54,0.06)', borderRadius: 8, flexWrap: 'wrap' }}>
                        <button type="button" onClick={() => void commit()} disabled={committing} style={{ background: '#1f7a36', color: '#fff', padding: '8px 16px', borderRadius: 6, border: 'none', fontWeight: 700 }}>
                            {committing ? '⏳ ' + t('Spremam…', 'Saving…') : '✓ ' + t('Potvrdi plan (snapshot, supersede stare)', 'Approve plan (snapshot, supersede old)')}
                        </button>
                        {committedPlanId ? (
                            <span style={{ color: '#1f7a36', fontWeight: 700, fontSize: '0.86rem' }}>
                                ✓ Plan #{committedPlanId} {t('snapshot-iran — preuzmi dokumente iz pinned bannera gore (3 dokumenta × PDF/XLSX).', 'snapshotted — download documents from the pinned banner above (3 docs × PDF/XLSX).')}
                            </span>
                        ) : null}
                        {data.summary.sla_breach_count ? <span style={{ color: '#b14040', fontWeight: 700 }}>⚠ {data.summary.sla_breach_count} SLA breach</span> : null}
                    </div>
                </>
            ) : (
                <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                    <p className="muted">{t('Klikni "Generiraj plan" da pokreneš waterfall algoritam (FIFO po starosti narudžbe iz Faros open_orders, ne uračunava ignorirane).', 'Click "Generate plan" to run the waterfall algorithm (FIFO by order age from Faros open_orders, excludes ignored).')}</p>
                </div>
            )}

            {/* Drawer s detaljima alokacija odabrane narudžbe */}
            {drawerOrder && data ? (() => {
                const allocs = data.allocations.filter((a) => a.order_number === drawerOrder);
                if (allocs.length === 0) return null;
                const first = allocs[0];
                const totalSale = allocs.reduce((s, a) => s + a.sale_price_eur, 0);
                const totalCost = allocs.reduce((s, a) => s + a.source_unit_cost_eur * a.qty, 0);
                const totalRuc = allocs.reduce((s, a) => s + a.ruc_estimate_eur, 0);
                const totalQty = allocs.reduce((s, a) => s + a.qty, 0);
                return (
                    <div
                        role="dialog"
                        aria-modal="true"
                        onClick={() => setDrawerOrder(null)}
                        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}
                    >
                        <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(720px, 95vw)', background: '#fff', height: '100vh', overflowY: 'auto', boxShadow: '-4px 0 16px rgba(0,0,0,0.2)' }}>
                            <div style={{ padding: '16px 22px', borderBottom: '1px solid #ddd4c1', position: 'sticky', top: 0, background: '#fff', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <h3 style={{ margin: 0, color: 'var(--ad-ink)' }}>📦 {drawerOrder}</h3>
                                    <p className="muted" style={{ margin: '2px 0 0', fontSize: '0.78rem' }}>{first.partner_name ?? '—'}</p>
                                </div>
                                <button type="button" onClick={() => setDrawerOrder(null)} className="secondary compact" style={{ fontSize: '0.84rem' }}>✕</button>
                            </div>

                            <div style={{ padding: '14px 22px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 14, fontSize: '0.82rem' }}>
                                    <div><span className="muted">{t('Stavki', 'Items')}:</span> <strong>{allocs.length}</strong></div>
                                    <div><span className="muted">{t('Ukupno kom', 'Total qty')}:</span> <strong>{totalQty}</strong></div>
                                    <div><span className="muted">{t('Prodajno', 'Sales')}:</span> <strong>{totalSale.toFixed(2)} €</strong></div>
                                    <div><span className="muted">{t('Nabavno', 'Cost')}:</span> <strong>{totalCost.toFixed(2)} €</strong></div>
                                    <div><span className="muted">{t('RUC', 'GM')}:</span> <strong style={{ color: totalRuc >= 0 ? '#1f7a36' : '#b14040' }}>{totalRuc.toFixed(2)} €</strong></div>
                                    <div><span className="muted">{t('RUC %', 'GM %')}:</span> <strong>{totalSale > 0 ? ((totalRuc / totalSale) * 100).toFixed(1) : '—'}%</strong></div>
                                </div>

                                <h4 style={{ margin: '10px 0 6px', fontSize: '0.78rem', color: 'var(--ad-acc-3)' }}>{t('Alokacije po artiklu', 'Allocations per item')}</h4>
                                <table className="admin-user-table" style={{ fontSize: '0.78rem', width: '100%' }}>
                                    <thead>
                                        <tr>
                                            <th>{t('Artikl', 'Item')}</th>
                                            <th>L</th>
                                            <th>{t('Izvor', 'Source')}</th>
                                            <th style={{ textAlign: 'right' }}>{t('Kol.', 'Qty')}</th>
                                            <th style={{ textAlign: 'right' }}>{t('Unit cost', 'Unit cost')}</th>
                                            <th style={{ textAlign: 'right' }}>RUC</th>
                                            <th>SLA</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allocs.map((a, i) => {
                                            const lm = layerMeta[a.source_layer];
                                            return (
                                                <tr key={i}>
                                                    <td>{a.item_name?.slice(0, 32)}</td>
                                                    <td><span style={{ background: lm.bg, color: lm.color, padding: '1px 6px', borderRadius: 3, fontSize: '0.7rem', fontWeight: 700 }}>{a.source_layer}</span></td>
                                                    <td style={{ fontSize: '0.74rem' }}>{a.source_warehouse_code ?? a.source_supplier_name ?? '—'}</td>
                                                    <td style={{ textAlign: 'right' }}>{a.qty}</td>
                                                    <td style={{ textAlign: 'right' }}>{a.source_unit_cost_eur.toFixed(2)} €</td>
                                                    <td style={{ textAlign: 'right', fontWeight: 700, color: a.ruc_estimate_eur >= 0 ? '#1f7a36' : '#b14040' }}>{a.ruc_estimate_eur.toFixed(2)} €</td>
                                                    <td style={{ fontSize: '0.72rem' }}>
                                                        {a.sla_days ?? '—'}d
                                                        {a.sla_breach ? <span style={{ color: '#b14040', fontWeight: 700, marginLeft: 3 }}>⚠</span> : null}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                <h4 style={{ margin: '14px 0 6px', fontSize: '0.78rem', color: 'var(--ad-acc-3)' }}>{t('Destinacija (poslovnica koja isporučuje)', 'Destination (delivering branch)')}</h4>
                                <p style={{ fontSize: '0.84rem', margin: 0 }}>
                                    {(first as any).dest_branch_code ? `${(first as any).dest_branch_code} · ${(first as any).dest_branch_name ?? ''}` : t('Nije popunjeno (regeneriraj plan).', 'Not populated (regenerate plan).')}
                                </p>
                            </div>
                        </div>
                    </div>
                );
            })() : null}

            {/* Oldschool download overlay s progress bar % */}
            {downloadOverlay ? (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: '#1a1a1a', color: 'var(--ad-acc)', fontFamily: 'DejaVu Sans Mono, Courier New, monospace', padding: '24px 32px', borderRadius: 8, border: '2px solid var(--ad-acc)', minWidth: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
                        <div style={{ fontSize: '0.92rem', fontWeight: 700, marginBottom: 12, letterSpacing: 0.5 }}>
                            {downloadOverlay.phase === 'error' ? '✗ FAIL' : downloadOverlay.phase === 'done' ? '✓ OK' : '⟳'} · {downloadOverlay.label}
                        </div>
                        <div style={{ background: '#000', height: 22, border: '1px solid var(--ad-acc)', position: 'relative', overflow: 'hidden' }}>
                            <div style={{
                                background: downloadOverlay.phase === 'error' ? '#b14040' : downloadOverlay.phase === 'done' ? '#1f7a36' : 'linear-gradient(90deg, var(--ad-acc-3), var(--ad-acc))',
                                height: '100%',
                                width: `${downloadOverlay.progress}%`,
                                transition: 'width 0.2s linear',
                            }} />
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.78rem', color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.8)', mixBlendMode: 'difference' }}>
                                {downloadOverlay.progress}%
                            </div>
                        </div>
                        <div style={{ marginTop: 10, fontSize: '0.78rem', color: '#e4d9b8' }}>
                            {/* ASCII spinner */}
                            {downloadOverlay.phase !== 'done' && downloadOverlay.phase !== 'error' ? (
                                <span style={{ marginRight: 8 }}>{'['.padEnd(Math.floor(downloadOverlay.progress / 5) + 1, '#').padEnd(21, ' ')+']'}</span>
                            ) : null}
                            {downloadOverlay.message}
                        </div>
                    </div>
                </div>
            ) : null}

            {/* WEB PREVIEW: Prijedlog narudžbe per artikl + Potvrdi → place-supplier-orders + hedge email */}
            {proposalModal ? (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', justifyContent: 'flex-end' }} onClick={() => setProposalModal(null)}>
                    <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', width: 'min(900px, 95vw)', height: '100vh', overflow: 'auto', padding: '18px 22px', boxShadow: '-8px 0 24px rgba(0,0,0,0.25)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, position: 'sticky', top: 0, background: '#fff', paddingBottom: 8, borderBottom: '1px solid #eee', zIndex: 1 }}>
                            <h3 style={{ margin: 0, color: '#b14040' }}>📋 {t('Prijedlog narudžbi dobavljačima', 'Supplier order proposal')} — Plan #{proposalModal.planId}</h3>
                            <button type="button" onClick={() => setProposalModal(null)} className="secondary compact">✕ {t('Zatvori', 'Close')}</button>
                        </div>

                        {proposalModal.loading ? (
                            <p className="muted">{t('Učitavam prijedlog…', 'Loading proposal…')}</p>
                        ) : proposalModal.data?.error ? (
                            <p className="error">{proposalModal.data.error}</p>
                        ) : proposalModal.potvrdjeno ? (
                            <div style={{ padding: '14px 18px', background: 'rgba(31,122,54,0.08)', border: '1.5px solid #1f7a36', borderRadius: 6 }}>
                                <h4 style={{ margin: '0 0 8px', color: '#1f7a36' }}>✅ {t('Narudžbe potvrđene', 'Orders confirmed')}</h4>
                                <p style={{ margin: '4px 0', fontSize: '0.86rem' }}>
                                    {t('Kreirano', 'Created')}: <strong>{proposalModal.potvrdResult?.created_count}</strong> {t('narudžbi u Cash Schedule (m.40 cash-schedule).', 'orders in Cash Schedule (m.40 cash-schedule).')}
                                </p>
                                {proposalModal.potvrdResult?.hedge?.sent ? (
                                    <p style={{ margin: '4px 0', fontSize: '0.86rem' }}>📧 {t('Hedge email poslan na', 'Hedge email sent to')} <strong>{proposalModal.potvrdResult.hedge.to}</strong>.</p>
                                ) : proposalModal.potvrdResult?.hedge?.message ? (
                                    <p style={{ margin: '4px 0', fontSize: '0.84rem', color: 'var(--ad-acc-3)' }}>⚠ {proposalModal.potvrdResult.hedge.message}</p>
                                ) : null}
                                <ul style={{ marginTop: 10, fontSize: '0.82rem' }}>
                                    {(proposalModal.potvrdResult?.created ?? []).map((c: any) => (
                                        <li key={c.id}>
                                            <strong>{c.supplier_name}</strong> · {Number(c.commitment_eur).toLocaleString('hr-HR', { minimumFractionDigits: 2 })} € · #{c.id}
                                            {' · '}<a href={c.pdf_url} target="_blank" rel="noreferrer">PDF</a>
                                            {' / '}<a href={c.xlsx_url} target="_blank" rel="noreferrer">XLSX</a>
                                        </li>
                                    ))}
                                </ul>
                                <button type="button" onClick={() => setProposalModal(null)} style={{ marginTop: 12, background: '#1f7a36', color: '#fff', padding: '8px 18px', borderRadius: 4, border: 'none', fontWeight: 700, cursor: 'pointer' }}>{t('Gotovo', 'Done')}</button>
                            </div>
                        ) : proposalModal.data ? (
                            <>
                                <div style={{ padding: '10px 14px', background: '#fff8e7', border: '1px solid var(--ad-acc)', borderRadius: 6, marginBottom: 12, fontSize: '0.84rem' }}>
                                    <div>
                                        <strong>Σ {t('Ukupno', 'Total')}</strong>:
                                        {' '}{proposalModal.data.grand_totals.suppliers} {t('dobavljača', 'suppliers')}
                                        {' · '}<strong>{Number(proposalModal.data.grand_totals.cost_eur).toLocaleString('hr-HR', { maximumFractionDigits: 0 })} €</strong> nabavno
                                        {' · '}RUC <strong style={{ color: proposalModal.data.grand_totals.ruc_eur >= 0 ? '#1f7a36' : '#b14040' }}>{Number(proposalModal.data.grand_totals.ruc_eur).toLocaleString('hr-HR', { maximumFractionDigits: 0 })} €</strong>
                                    </div>
                                    {(proposalModal.data.grand_totals.fine_g_gold ?? 0) > 0 ? (
                                        <div style={{ color: 'var(--ad-acc)', marginTop: 4 }}>🥇 <strong>Au: {Number(proposalModal.data.grand_totals.fine_g_gold).toLocaleString('hr-HR', { maximumFractionDigits: 2 })} g fine</strong> ({(proposalModal.data.grand_totals.fine_g_gold / 31.1034768).toFixed(2)} oz)</div>
                                    ) : null}
                                    {(proposalModal.data.grand_totals.fine_g_silver ?? 0) > 0 ? (
                                        <div style={{ color: '#7a7a7a', marginTop: 2 }}>🥈 <strong>Ag: {Number(proposalModal.data.grand_totals.fine_g_silver).toLocaleString('hr-HR', { maximumFractionDigits: 2 })} g fine</strong> ({(proposalModal.data.grand_totals.fine_g_silver / 31.1034768).toFixed(2)} oz)</div>
                                    ) : null}
                                </div>

                                {(proposalModal.data.suppliers ?? []).map((s: any) => (
                                    <div key={s.supplier_name} style={{ marginBottom: 14, border: '1px solid #ddd', borderRadius: 6 }}>
                                        <div style={{ background: 'linear-gradient(135deg, #fff8e7, #f5ecd5)', padding: '8px 12px', borderBottom: '1px solid var(--ad-acc)' }}>
                                            <strong style={{ color: 'var(--ad-ink)' }}>🏭 {s.supplier_name}</strong>
                                            <span className="muted" style={{ fontSize: '0.78rem', marginLeft: 8 }}>
                                                {s.items.length} {t('artikala', 'items')} · <strong>{Number(s.totals.fine_g).toLocaleString('hr-HR', { maximumFractionDigits: 2 })} g fine</strong> · <strong>{Number(s.totals.cost_eur).toLocaleString('hr-HR', { maximumFractionDigits: 0 })} €</strong> · {s.totals.order_count} {t('kupaca', 'customers')}
                                            </span>
                                        </div>
                                        <table style={{ width: '100%', fontSize: '0.82rem' }}>
                                            <thead>
                                                <tr style={{ background: '#fafafa' }}>
                                                    <th style={{ textAlign: 'left', padding: '4px 8px' }}>SKU</th>
                                                    <th style={{ textAlign: 'left', padding: '4px 8px' }}>{t('Artikl', 'Item')}</th>
                                                    <th style={{ textAlign: 'right', padding: '4px 8px' }}>{t('Kom.', 'Qty')}</th>
                                                    <th style={{ textAlign: 'right', padding: '4px 8px' }}>Fine g</th>
                                                    <th style={{ textAlign: 'right', padding: '4px 8px' }}>Unit €</th>
                                                    <th style={{ textAlign: 'right', padding: '4px 8px' }}>Total €</th>
                                                    <th style={{ textAlign: 'left', padding: '4px 8px' }}>{t('Pokriva narudžbe', 'Covers orders')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {s.items.map((a: any, i: number) => {
                                                    const isSilver = (a.metal_type ?? 'gold') === 'silver';
                                                    return (
                                                    <tr key={i} style={{ borderTop: '1px solid #eee', background: isSilver ? 'rgba(122,122,122,0.04)' : undefined }}>
                                                        <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>
                                                            <span style={{ color: isSilver ? '#7a7a7a' : 'var(--ad-acc)', fontWeight: 700, marginRight: 4 }} title={isSilver ? 'Srebro' : 'Zlato'}>{isSilver ? '🥈' : '🥇'}</span>
                                                            {a.sku ?? '—'}
                                                        </td>
                                                        <td style={{ padding: '4px 8px' }}>{a.item_name ?? '—'}</td>
                                                        <td style={{ padding: '4px 8px', textAlign: 'right' }}><strong>{a.qty}</strong></td>
                                                        <td style={{ padding: '4px 8px', textAlign: 'right' }}>{Number(a.fine_g_total).toLocaleString('hr-HR', { maximumFractionDigits: 2 })}</td>
                                                        <td style={{ padding: '4px 8px', textAlign: 'right' }}>{Number(a.unit_cost_eur).toLocaleString('hr-HR', { maximumFractionDigits: 2 })}</td>
                                                        <td style={{ padding: '4px 8px', textAlign: 'right' }}><strong>{Number(a.cost_eur_total).toLocaleString('hr-HR', { maximumFractionDigits: 0 })}</strong></td>
                                                        <td style={{ padding: '4px 8px', fontSize: '0.76rem', color: '#666' }}>
                                                            {a.order_count}× ({a.order_numbers.slice(0, 3).join(', ')}{a.order_numbers.length > 3 ? '…' : ''})
                                                        </td>
                                                    </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                ))}

                                <div style={{ padding: 12, background: 'rgba(30,64,175,0.05)', border: '1.5px solid #1e40af', borderRadius: 6, marginTop: 14 }}>
                                    <h4 style={{ margin: '0 0 8px', color: '#1e40af' }}>📧 {t('Hedging', 'Hedging')}</h4>
                                    <label style={{ display: 'block', fontSize: '0.84rem', marginBottom: 6 }}>
                                        <input type="checkbox" checked={proposalModal.sendHedge} onChange={(e) => setProposalModal((s) => s ? { ...s, sendHedge: e.target.checked } : s)} />
                                        {' '}{t('Pošalji hedge email automatski (otpusti samo Au:', 'Send hedge email (release Au only:')}
                                        {' '}<strong>{Number(proposalModal.data.grand_totals.fine_g_gold ?? proposalModal.data.grand_totals.fine_g).toLocaleString('hr-HR', { maximumFractionDigits: 2 })} g fine</strong>
                                        {' '}/ {((proposalModal.data.grand_totals.fine_g_gold ?? proposalModal.data.grand_totals.fine_g) / 31.1034768).toFixed(3)} oz)
                                    </label>
                                    {(proposalModal.data.grand_totals.fine_g_silver ?? 0) > 0 ? (
                                        <div style={{ fontSize: '0.76rem', color: '#7a7a7a', marginBottom: 6 }}>
                                            ⓘ {t('Srebro', 'Silver')} ({Number(proposalModal.data.grand_totals.fine_g_silver).toLocaleString('hr-HR', { maximumFractionDigits: 2 })} g) {t('se ne hedge-a XAU futursima — ručno ako treba.', 'is not hedged via XAU futures — handle manually if needed.')}
                                        </div>
                                    ) : null}
                                    {proposalModal.sendHedge ? (
                                        <label style={{ display: 'block', fontSize: '0.82rem' }}>
                                            {t('Hedge email (ostavi prazno za default iz config-a)', 'Hedge email (leave empty for config default)')}:
                                            <input type="email" value={proposalModal.hedgeEmail} onChange={(e) => setProposalModal((s) => s ? { ...s, hedgeEmail: e.target.value } : s)} placeholder="hedging@example.com" style={{ marginLeft: 6, padding: '3px 6px', fontSize: '0.82rem', width: 260 }} />
                                        </label>
                                    ) : null}
                                </div>

                                <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
                                    <button type="button" onClick={() => setProposalModal(null)} className="secondary">{t('Odustani', 'Cancel')}</button>
                                    <button type="button" onClick={() => void potvrdiNarudzbe()} style={{ background: '#1f7a36', color: '#fff', padding: '10px 22px', borderRadius: 4, border: 'none', fontSize: '0.92rem', fontWeight: 700, cursor: 'pointer' }}>
                                        ✓ {t('Potvrdi narudžbe + pošalji hedge', 'Confirm orders + send hedge')}
                                    </button>
                                </div>
                            </>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </section>
    );
}

export function GoldCmModule({ locale, token, onBack, onOpenPlanGenerate }: { locale: 'hr' | 'en'; token: string; onBack?: () => void; onOpenPlanGenerate?: () => void }) {
    const [tab, setTab] = useState<'prices' | 'pravila' | 'planner' | 'plan_isporuka' | 'cash_schedule' | 'ruc_drift' | 'sim'>(() => {
        const t = readUrlParam('tab', 'planner');
        const valid = ['prices', 'pravila', 'planner', 'plan_isporuka', 'cash_schedule', 'ruc_drift', 'sim'];
        return (valid.includes(t) ? t : 'planner') as any;
    });
    useUrlSync('tab', tab, tab === 'planner');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [spot, setSpot] = useState<{ spot_date: string; price_eur_per_troy_oz: number; source: string } | null>(null);
    const [segmentKeys, setSegmentKeys] = useState<string[]>([...CGM_FALLBACK_SEGMENTS]);
    const [activeSegment, setActiveSegment] = useState<string>('poslovnice');
    const [rows, setRows] = useState<IndexRow[]>([]);
    const [payload, setPayload] = useState<CgmPayload>({ products: {} });
    const [recent10, setRecent10] = useState<Array<{ id: number; effective_from: string }>>([]);
    const [simResult, setSimResult] = useState<{
        total_delta_eur: Record<string, number>;
        per_product_delta_eur: Record<string, Record<string, number>>;
        by_segment_delta_eur?: Record<string, Record<string, number>>;
        per_product_segment_delta_eur?: Record<string, Record<string, Record<string, number>>>;
    } | null>(null);
    const [busy, setBusy] = useState(false);
    /** Expanded product row — nabava / spread / iznimke panel */
    const [expandedProductId, setExpandedProductId] = useState<number | null>(null);

    const load = useCallback(async (opts?: { preserveError?: boolean }) => {
        setLoading(true);
        if (!opts?.preserveError) {
            setError('');
        }
        try {
            const data = await api<{
                spot: typeof spot;
                revision: { payload: { products?: Record<string, unknown> } } | null;
                rows: IndexRow[];
                segments?: string[];
            }>('/gold-cm', token);
            setSpot(data.spot);
            setRows(data.rows ?? []);
            const order = data.segments?.length ? data.segments : [...CGM_FALLBACK_SEGMENTS];
            setSegmentKeys(order);
            setActiveSegment((prev) => (order.includes(prev) ? prev : order[0] ?? 'poslovnice'));
            setPayload(buildPayloadFromRows(data.rows ?? [], data.revision?.payload, order));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Error');
            setLoading(false);

            return;
        }
        try {
            const rev = await api<{ recent_10: Array<{ id: number; effective_from: string }> }>('/gold-cm/revisions', token);
            setRecent10(rev.recent_10 ?? []);
        } catch {
            setRecent10([]);
        }
        setSimResult(null);
        setLoading(false);
    }, [token]);

    useEffect(() => {
        void load();
    }, [load]);

    function draftSegmentRule(productId: number): CgmSegmentRule {
        const key = String(productId);
        const row = rows.find((x) => x.product.id === productId);
        const cur = payload.products[key]?.segments?.[activeSegment];
        if (cur) {
            return cur;
        }

        return cloneSegmentRule((row?.rules_by_segment?.[activeSegment] as CgmSegmentRule | undefined) ?? emptyRule());
    }

    function draftPurchase(productId: number): PurchaseInputs {
        const key = String(productId);

        return payload.products[key]?.purchase ?? defaultPurchase();
    }

    const [populateMsg, setPopulateMsg] = useState('');
    function populatePremiumEstimates(onlyEmpty: boolean) {
        let touched = 0;
        setPayload((prev) => {
            const products = { ...prev.products };
            rows.forEach((r) => {
                const key = String(r.product.id);
                const cur = products[key]?.purchase ?? defaultPurchase();
                const est = premiumEstimateEur(r.product.fine_weight_g);
                if (est <= 0) return;
                if (onlyEmpty && (cur.premium_eur ?? 0) > 0) return;
                products[key] = {
                    purchase: { ...cur, premium_eur: est },
                    segments: products[key]?.segments ?? {},
                };
                touched++;
            });
            return { ...prev, products };
        });
        setPopulateMsg(locale === 'en'
            ? `Populated premium for ${touched} products. Click "Save pricing revision" to commit.`
            : `Populiran premium na ${touched} proizvoda. Klikni "Spremi reviziju cijena" za pohranu.`);
        setTimeout(() => setPopulateMsg(''), 6000);
    }

    function updatePurchase(productId: number, patch: Partial<PurchaseInputs>) {
        const key = String(productId);
        setPayload((prev) => {
            const row = prev.products[key];
            const cur = row?.purchase ?? defaultPurchase();

            return {
                products: {
                    ...prev.products,
                    [key]: {
                        purchase: {
                            premium_pct_on_metal: patch.premium_pct_on_metal ?? cur.premium_pct_on_metal,
                            premium_eur: patch.premium_eur ?? cur.premium_eur,
                            dependent_costs_eur: patch.dependent_costs_eur ?? cur.dependent_costs_eur,
                        },
                        segments: row?.segments ?? {},
                    },
                },
            };
        });
    }

    function updateSegmentRule(productId: number, patch: Partial<CgmSegmentRule>) {
        const key = String(productId);
        setPayload((prev) => {
            const row = prev.products[key];
            const curSeg = row?.segments?.[activeSegment] ?? draftSegmentRule(productId);
            const next: CgmSegmentRule = {
                ...curSeg,
                ...patch,
                exceptions: patch.exceptions ?? curSeg.exceptions ?? [],
            };

            return {
                products: {
                    ...prev.products,
                    [key]: {
                        purchase: row?.purchase ?? defaultPurchase(),
                        segments: {
                            ...(row?.segments ?? {}),
                            [activeSegment]: next,
                        },
                    },
                },
            };
        });
    }

    async function refreshSpot() {
        setBusy(true);
        try {
            const data = await mutate<{ ok: boolean; message?: string | null; spot: typeof spot }>(
                '/gold-cm/refresh-spot',
                token,
                'POST',
                {},
            );
            if (data.ok) {
                setError('');
                if (data.spot) {
                    setSpot(data.spot);
                }
            } else {
                setError(data.message ?? (locale === 'en' ? 'Spot fetch failed.' : 'Dohvat spota nije uspio — provjeri .env na serveru.'));
            }
            await load({ preserveError: !data.ok });
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Error');
        } finally {
            setBusy(false);
        }
    }

    async function runSimulate() {
        setBusy(true);
        try {
            const res = await mutate<{
                total_delta_eur: Record<string, number>;
                per_product_delta_eur: Record<string, Record<string, number>>;
                by_segment_delta_eur?: Record<string, Record<string, number>>;
                per_product_segment_delta_eur?: Record<string, Record<string, Record<string, number>>>;
            }>('/gold-cm/simulate', token, 'POST', { payload });
            setSimResult({
                total_delta_eur: res.total_delta_eur,
                per_product_delta_eur: res.per_product_delta_eur,
                by_segment_delta_eur: res.by_segment_delta_eur,
                per_product_segment_delta_eur: res.per_product_segment_delta_eur,
            });
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Error');
        } finally {
            setBusy(false);
        }
    }

    async function applyRevision() {
        setBusy(true);
        try {
            await mutate('/gold-cm/apply', token, 'POST', { payload });
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Error');
        } finally {
            setBusy(false);
        }
    }

    const labels = useMemo(
        () => ({
            prices: locale === 'en' ? 'Prices' : 'Cijene',
            sim: locale === 'en' ? 'Simulation' : 'Simulacija',
            spotHead: locale === 'en' ? 'London spot · fine gold' : 'London spot · fino zlato',
            spotPerGram: locale === 'en' ? 'EUR/g' : 'EUR/g',
            spotPerOzHint: locale === 'en' ? 'EUR / troy oz' : 'EUR / tr. oz',
            pct: locale === 'en' ? '% on spot' : '% na spot',
            eur: locale === 'en' ? '€ on spot' : '€ na spot',
            refresh: locale === 'en' ? 'Refresh spot' : 'Osvježi spot',
            simulate: locale === 'en' ? 'Run simulation' : 'Pokreni simulaciju',
            apply: locale === 'en' ? 'Apply simulation' : 'Primijeni simulaciju',
            recent: locale === 'en' ? 'Last 10 revisions' : 'Zadnjih 10 revizija',
            segTitle: locale === 'en' ? 'Price channel' : 'Prodajni kanal',
            purchase: locale === 'en' ? 'Purchase chain (same as XLS)' : 'Nabava (kao u XLS-u)',
            sale: locale === 'en' ? 'Sale €' : 'Prodaja €',
            buy: locale === 'en' ? 'Buyback €' : 'Otkup €',
            spreadEur: locale === 'en' ? 'Spread € (sale − buyback)' : 'Spread € (prodaja − otkup)',
            spreadPct: locale === 'en' ? '% below sale' : '% ispod prodaje',
            productExpandHint:
                locale === 'en'
                    ? 'Click the product row to open purchase, spread & exceptions'
                    : 'Klik na red proizvoda otvara nabavu, spread i iznimke',
        }),
        [locale],
    );

    const priceColCount = 6;

    const supplierGroups = useMemo(() => {
        const unk = locale === 'en' ? 'No supplier' : 'Bez dobavljača';
        const m = new Map<string, IndexRow[]>();
        for (const r of rows) {
            const key = r.product.cgm_supplier?.trim() || unk;
            const arr = m.get(key);
            if (arr) {
                arr.push(r);
            } else {
                m.set(key, [r]);
            }
        }
        const ordered: Array<[string, IndexRow[]]> = [];
        const keys = [...m.keys()].filter((k) => k !== unk);
        keys.sort((a, b) => a.localeCompare(b, locale === 'en' ? 'en-GB' : 'hr-HR'));
        for (const k of keys) {
            ordered.push([k, m.get(k)!]);
        }
        if (m.has(unk)) {
            ordered.push([unk, m.get(unk)!]);
        }

        return ordered;
    }, [rows, locale]);

    if (loading) {
        return <section className="panel">Gold Category Manager…</section>;
    }

    return (
        <section className="panel">
            <Breadcrumb items={[
                { label: locale === 'en' ? '🏭 40 · Warehouse' : '🏭 40 · Skladišno poslovanje', onClick: onBack },
                { label: '40.1 · Gold Category Manager' },
            ]} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <p className="eyebrow">40.1 · Gold Category Manager</p>
                    <h2>{locale === 'en' ? 'Category Manager — pricing' : 'Category Manager — cijene'}</h2>
                </div>
                <CopyLinkButton locale={locale} />
            </div>
            {error ? <p className="error">{error}</p> : null}

            {/* 4-sekcijska workflow struktura — SETUP → REAL-TIME → OPERATIVA → STRATEGIJA */}
            <div style={{ marginBottom: 14, padding: '8px 10px', background: 'linear-gradient(135deg, #fffbeb 0%, rgba(254,243,199,0.4) 100%)', border: '1px solid rgba(var(--ad-soft-rgb),0.25)', borderRadius: 10 }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--ad-ink)', fontWeight: 700, marginRight: 4 }}>Setup</span>
                    <button type="button" className={tab === 'prices' ? '' : 'secondary compact'} onClick={() => setTab('prices')} style={{ fontSize: '0.84rem' }}>⚙ {labels.prices}</button>
                    <button type="button" className={tab === 'pravila' ? '' : 'secondary compact'} onClick={() => setTab('pravila')} style={{ fontSize: '0.84rem' }}>📐 {locale === 'en' ? 'Rules' : 'Pravila'}</button>

                    <span style={{ width: 1, height: 22, background: 'rgba(var(--ad-soft-rgb),0.35)', margin: '0 6px' }} />
                    <span style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--ad-ink)', fontWeight: 700, marginRight: 4 }}>Operativa</span>
                    <button type="button" className={tab === 'planner' ? '' : 'secondary compact'} onClick={() => setTab('planner')} style={{ fontSize: '0.84rem' }}>📦 {locale === 'en' ? 'Planer' : 'Planer'}</button>
                    {/* "Plan & generate" je izdvojen u zaseban modul 40.4 (warehouse_plan_generate). */}
                    <button type="button" className={tab === 'cash_schedule' ? '' : 'secondary compact'} onClick={() => setTab('cash_schedule')} style={{ fontSize: '0.84rem' }}>💵 {locale === 'en' ? 'Cash schedule' : 'Cash schedule'}</button>
                    <button type="button" className={tab === 'ruc_drift' ? '' : 'secondary compact'} onClick={() => setTab('ruc_drift')} style={{ fontSize: '0.84rem' }}>💰 RUC Drift</button>

                    <span style={{ width: 1, height: 22, background: 'rgba(var(--ad-soft-rgb),0.35)', margin: '0 6px' }} />
                    <span style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--ad-ink)', fontWeight: 700, marginRight: 4 }}>Strategija</span>
                    <button type="button" className={tab === 'sim' ? '' : 'secondary compact'} onClick={() => setTab('sim')} style={{ fontSize: '0.84rem' }}>🔬 {labels.sim}</button>
                </div>
            </div>

            {tab === 'planner' ? (
                <GoldCmPlannerPanel token={token} locale={locale} onOpenPlanGenerate={onOpenPlanGenerate} />
            ) : tab === 'pravila' ? (
                <PravilaPlaceholderTab locale={locale} token={token} />
            ) : tab === 'plan_isporuka' ? (
                <PlanIsporukaPlaceholderTab locale={locale} token={token} />
            ) : tab === 'cash_schedule' ? (
                <section>
                    <h3 style={{ margin: '0 0 4px', color: 'var(--ad-ink)' }}>💵 Cash Schedule</h3>
                    <p className="muted" style={{ margin: '0 0 12px', fontSize: '0.82rem' }}>
                        {locale === 'en'
                            ? 'Mirror of m.6 Cashflow → Cash Schedule. Same data, same actions. Edits here reflect in m.6.'
                            : 'Mirror modula 6 Cashflow → Cash Schedule. Iste tablice, iste akcije. Izmjene su sinkrone s m.6.'}
                    </p>
                    <CashflowScheduleTab token={token} locale={locale} />
                </section>
            ) : tab === 'ruc_drift' ? (
                <RucDriftTab locale={locale} token={token} />
            ) : (
            <><div className="card" style={{ padding: '10px 14px', marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <strong style={{ marginRight: 8 }}>{labels.segTitle}:</strong>
                {segmentKeys.map((s) => (
                    <button
                        key={s}
                        type="button"
                        className={s === activeSegment ? 'compact' : 'secondary compact'}
                        onClick={() => setActiveSegment(s)}
                    >
                        {segmentLabel(s, locale)}
                    </button>
                ))}
            </div>

            <div
                className="card"
                style={{
                    padding: '14px 16px',
                    marginBottom: 12,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 14,
                    border: '1px solid rgba(var(--ad-soft-rgb), 0.28)',
                    boxShadow: '0 1px 4px rgba(58, 47, 28, 0.06)',
                    background: 'linear-gradient(165deg, rgba(255, 252, 245, 0.95) 0%, rgba(255, 250, 238, 0.85) 100%)',
                }}
            >
                <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                    <p
                        style={{
                            margin: 0,
                            fontSize: '0.68rem',
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            fontWeight: 600,
                            color: 'rgba(90, 82, 70, 0.85)',
                        }}
                    >
                        {labels.spotHead}
                    </p>
                    {spot ? (
                        <>
                            <p style={{ margin: '8px 0 6px', fontSize: '1.55rem', fontWeight: 700, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}>
                                {(spot.price_eur_per_troy_oz / GRAMS_PER_TROY_OZ).toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}{' '}
                                <span style={{ fontSize: '1rem', fontWeight: 600 }}>{labels.spotPerGram}</span>
                            </p>
                            <p className="muted" style={{ margin: 0, fontSize: '0.82rem', lineHeight: 1.4 }}>
                                ≈{' '}
                                {spot.price_eur_per_troy_oz.toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}{' '}
                                {labels.spotPerOzHint} · {spot.spot_date} · {spot.source}
                            </p>
                        </>
                    ) : (
                        <p className="muted" style={{ margin: '8px 0 0', fontSize: '0.88rem', lineHeight: 1.45 }}>
                            {locale === 'en'
                                ? 'No spot row — refresh or set CGM_METALPRICEAPI_KEY (preferred), CGM_SPOT_FIX_EUR_PER_OZ, or CGM_GOLDAPI_IO_TOKEN.'
                                : 'Nema spot zapisa — osvježi ili postavi CGM_METALPRICEAPI_KEY (preporuka), CGM_SPOT_FIX_EUR_PER_OZ ili CGM_GOLDAPI_IO_TOKEN.'}
                        </p>
                    )}
                </div>
                <button type="button" className="secondary compact" style={{ flexShrink: 0 }} disabled={busy} onClick={() => void refreshSpot()}>
                    {labels.refresh}
                </button>
            </div>

            <CijeneFlatTable
                rows={rows}
                payload={payload}
                activeSegment={activeSegment}
                spotPerOz={spot?.price_eur_per_troy_oz}
                locale={locale}
                onUpdatePurchase={updatePurchase}
                onUpdateSegment={updateSegmentRule}
                token={token}
                onProductPremiumChanged={() => { void load(); }}
            />

            {false && <div>
                {supplierGroups.map(([supplier, list]) => {
                    const sortedList = sortRowsBySalePriceAsc(list, {
                        allRows: rows,
                        payload,
                        activeSegment,
                        spotPx: spot?.price_eur_per_troy_oz,
                        locale,
                    });

                    return (
                    <details key={supplier} style={{ marginBottom: 14, border: '1px solid rgba(var(--ad-soft-rgb),0.35)', borderRadius: 8, padding: '4px 10px 10px' }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 700, padding: '10px 4px' }}>
                            {locale === 'en' ? 'Supplier' : 'Dobavljač'}: {supplier}{' '}
                            <span className="muted" style={{ fontWeight: 400 }}>
                                ({list.length})
                            </span>
                        </summary>
                        <div style={{ overflowX: 'auto' }}>
                            <table className="table-like" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                <thead>
                        <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd4c1' }}>
                            <th>{locale === 'en' ? 'Product' : 'Proizvod'}</th>
                            <th>
                                {labels.pct} · {segmentLabel(activeSegment, locale)}
                            </th>
                            <th>
                                {labels.eur} · {segmentLabel(activeSegment, locale)}
                            </th>
                            {tab === 'sim' ? (
                                <>
                                    <th style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>Δ 30d</th>
                                    <th style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>Δ 90d</th>
                                    <th style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>Δ 180d</th>
                                </>
                            ) : (
                                <>
                                    <th style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>EUR/g</th>
                                    <th style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{labels.sale}</th>
                                    <th style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{labels.buy}</th>
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedList.map((r) => {
                            const key = String(r.product.id);
                            const draft = draftSegmentRule(r.product.id);
                            const purch = draftPurchase(r.product.id);
                            const simAll = simResult?.per_product_delta_eur[key];
                            const simSeg = simResult?.per_product_segment_delta_eur?.[key]?.[activeSegment];
                            const sim = simSeg ?? simAll;
                            const spotPx = spot?.price_eur_per_troy_oz;
                            const saleLive =
                                spotPx !== undefined ? computeSaleEur(spotPx, r.product.fine_weight_g, draft) : null;
                            const buyLive = saleLive !== null ? computeBuybackEur(saleLive, draft) : null;

                            const panelOpen = expandedProductId === r.product.id;

                            function toggleProductPanel(e: React.MouseEvent): void {
                                if ((e.target as HTMLElement).closest('input, button, a, textarea, label')) {
                                    return;
                                }
                                setExpandedProductId((id) => (id === r.product.id ? null : r.product.id));
                            }

                            return (
                                <React.Fragment key={r.product.id}>
                                    <tr
                                        style={{
                                            borderBottom: '1px dashed rgba(var(--ad-soft-rgb),0.25)',
                                            cursor: 'pointer',
                                            background: panelOpen ? 'rgba(255, 248, 230, 0.45)' : undefined,
                                        }}
                                        onClick={toggleProductPanel}
                                        title={labels.productExpandHint}
                                        aria-expanded={panelOpen}
                                    >
                                        <td>
                                            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                                <span
                                                    aria-hidden
                                                    style={{
                                                        flexShrink: 0,
                                                        marginTop: 2,
                                                        fontSize: '0.7rem',
                                                        color: 'rgba(120, 108, 90, 0.9)',
                                                    }}
                                                >
                                                    {panelOpen ? '▼' : '▶'}
                                                </span>
                                                <div style={{ minWidth: 0 }}>
                                            <strong>{r.product.name_hr}</strong>
                                            <span className="muted" style={{ fontSize: '0.75rem' }}>
                                                {' '}
                                                ({r.product.slug})
                                            </span>
                                            {(() => {
                                                const unk = locale === 'en' ? 'No supplier' : 'Bez dobavljača';
                                                const bits = [
                                                    r.product.cgm_category,
                                                    r.product.cgm_warehouse,
                                                    r.product.cgm_delivery_type,
                                                    r.product.cgm_mint,
                                                ];
                                                if (supplier === unk) {
                                                    bits.push(r.product.cgm_supplier);
                                                }
                                                const line = bits.filter(Boolean).join(' · ');

                                                return line ? (
                                                    <div className="muted" style={{ fontSize: '0.72rem', marginTop: 2 }}>
                                                        {line}
                                                    </div>
                                                ) : null;
                                            })()}
                                                </div>
                                            </div>
                                        </td>
                                        <td
                                            onClick={(e) => {
                                                e.stopPropagation();
                                            }}
                                        >
                                            <input
                                                className="bi-input"
                                                style={{ width: 88 }}
                                                type="number"
                                                step="0.01"
                                                value={draft.pct_on_spot}
                                                onChange={(e) => updateSegmentRule(r.product.id, { pct_on_spot: Number(e.target.value) })}
                                            />
                                        </td>
                                        <td
                                            onClick={(e) => {
                                                e.stopPropagation();
                                            }}
                                        >
                                            <input
                                                className="bi-input"
                                                style={{ width: 88 }}
                                                type="number"
                                                step="0.01"
                                                value={draft.eur_on_spot}
                                                onChange={(e) => updateSegmentRule(r.product.id, { eur_on_spot: Number(e.target.value) })}
                                            />
                                        </td>
                                        {tab === 'sim' ? (
                                            <>
                                                <td style={moneyCellStyle} onClick={(e) => e.stopPropagation()}>
                                                    {sim?.['30'] !== undefined ? formatEurAmount(sim['30'], locale) : '—'}
                                                </td>
                                                <td style={moneyCellStyle} onClick={(e) => e.stopPropagation()}>
                                                    {sim?.['90'] !== undefined ? formatEurAmount(sim['90'], locale) : '—'}
                                                </td>
                                                <td style={moneyCellStyle} onClick={(e) => e.stopPropagation()}>
                                                    {sim?.['180'] !== undefined ? formatEurAmount(sim['180'], locale) : '—'}
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td style={moneyCellStyle} onClick={(e) => e.stopPropagation()}>
                                                    {saleLive !== null &&
                                                    r.product.fine_weight_g !== null &&
                                                    r.product.fine_weight_g > 0 ? (
                                                        <span>
                                                            {formatEurAmount(saleLive / r.product.fine_weight_g, locale)}{' '}
                                                            <span className="muted" style={{ fontSize: '0.85em' }}>
                                                                / g
                                                            </span>
                                                        </span>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                                <td style={moneyCellStyle} onClick={(e) => e.stopPropagation()}>
                                                    {formatEurMoneyCell(saleLive, locale)}
                                                </td>
                                                <td style={moneyCellStyle} onClick={(e) => e.stopPropagation()}>
                                                    {formatEurMoneyCell(buyLive, locale)}
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                    {panelOpen ? (
                                    <tr>
                                        <td colSpan={priceColCount} style={{ background: 'rgba(255,250,240,0.6)', padding: '8px 12px', fontSize: '0.78rem' }}>
                                            <details key={`${r.product.id}-purchase`} open={false}>
                                                <summary style={{ cursor: 'pointer', fontWeight: 700 }}>{labels.purchase}</summary>
                                                <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                                                    <label className="muted">
                                                        {locale === 'en' ? 'Premium % on metal' : 'Nabavni premium % na metal'}
                                                        <input
                                                            className="bi-input"
                                                            type="number"
                                                            step="0.01"
                                                            style={{ display: 'block', marginTop: 4, width: '100%' }}
                                                            value={purch.premium_pct_on_metal}
                                                            onChange={(e) => updatePurchase(r.product.id, { premium_pct_on_metal: Number(e.target.value) })}
                                                        />
                                                    </label>
                                                    <label className="muted">
                                                        {locale === 'en' ? 'Premium €' : 'Nabavni premium €'}
                                                        <input
                                                            className="bi-input"
                                                            type="number"
                                                            step="0.01"
                                                            style={{ display: 'block', marginTop: 4, width: '100%' }}
                                                            value={purch.premium_eur}
                                                            onChange={(e) => updatePurchase(r.product.id, { premium_eur: Number(e.target.value) })}
                                                        />
                                                    </label>
                                                    <label className="muted">
                                                        {locale === 'en' ? 'Dependent purchase costs €' : 'Zavisni troškovi nabave €'}
                                                        <input
                                                            className="bi-input"
                                                            type="number"
                                                            step="0.01"
                                                            style={{ display: 'block', marginTop: 4, width: '100%' }}
                                                            value={purch.dependent_costs_eur}
                                                            onChange={(e) => updatePurchase(r.product.id, { dependent_costs_eur: Number(e.target.value) })}
                                                        />
                                                    </label>
                                                </div>
                                                {spotPx !== undefined && r.product.fine_weight_g ? (
                                                    <p className="muted" style={{ marginTop: 8 }}>
                                                        metal: {computePurchaseLadder(spotPx, r.product.fine_weight_g, purch)?.metal_value_eur.toFixed(2)} · Σ nabava:{' '}
                                                        {computePurchaseLadder(spotPx, r.product.fine_weight_g, purch)?.total_purchase_cost_eur.toFixed(2)} · nabavna:{' '}
                                                        {computePurchaseLadder(spotPx, r.product.fine_weight_g, purch)?.nabavna_cijena_eur.toFixed(2)} EUR
                                                    </p>
                                                ) : (
                                                    <p className="muted" style={{ marginTop: 8 }}>{locale === 'en' ? 'Set spot + fine weight for ladder.' : 'Postavi spot i finu masu za lanac.'}</p>
                                                )}
                                            </details>

                                            <details style={{ marginTop: 12 }} open={false}>
                                                <summary style={{ cursor: 'pointer', fontWeight: 700 }}>
                                                    {labels.spreadEur} / {labels.spreadPct} · {segmentLabel(activeSegment, locale)}
                                                </summary>
                                                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                                                    <label className="muted">
                                                        {labels.spreadEur}
                                                        <input
                                                            className="bi-input"
                                                            type="number"
                                                            step="0.01"
                                                            style={{ display: 'block', marginTop: 4, width: 120 }}
                                                            value={draft.buyback_spread_eur ?? ''}
                                                            onChange={(e) =>
                                                                updateSegmentRule(r.product.id, {
                                                                    buyback_spread_eur: e.target.value === '' ? undefined : Number(e.target.value),
                                                                    buyback_pct_below_sale: undefined,
                                                                })
                                                            }
                                                        />
                                                    </label>
                                                    <label className="muted">
                                                        {labels.spreadPct}
                                                        <input
                                                            className="bi-input"
                                                            type="number"
                                                            step="0.01"
                                                            style={{ display: 'block', marginTop: 4, width: 120 }}
                                                            value={draft.buyback_pct_below_sale ?? ''}
                                                            onChange={(e) =>
                                                                updateSegmentRule(r.product.id, {
                                                                    buyback_pct_below_sale: e.target.value === '' ? undefined : Number(e.target.value),
                                                                    buyback_spread_eur: undefined,
                                                                })
                                                            }
                                                        />
                                                    </label>
                                                    <span className="muted">
                                                        {locale === 'en' ? 'Use either € spread or % — not both.' : 'Koristi ili € spread ili % — ne oba.'}
                                                    </span>
                                                </div>
                                            </details>

                                            <details style={{ marginTop: 12 }} open={false}>
                                                <summary style={{ cursor: 'pointer', fontWeight: 700 }}>
                                                    {locale === 'en' ? 'Date exceptions' : 'Iznimke po datumima'} · {segmentLabel(activeSegment, locale)} (
                                                    {(draft.exceptions ?? []).length})
                                                </summary>
                                                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                    {(draft.exceptions ?? []).map((ex, idx) => (
                                                        <div key={idx} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                                                            <label className="muted">
                                                                od
                                                                <DateInput
                                                                    className="bi-input"
                                                                    style={{ marginLeft: 4 }}
                                                                    value={ex.valid_from}
                                                                    onChange={(iso) => {
                                                                        const next = [...(draft.exceptions ?? [])];
                                                                        next[idx] = { ...ex, valid_from: iso };
                                                                        updateSegmentRule(r.product.id, { exceptions: next });
                                                                    }}
                                                                />
                                                            </label>
                                                            <label className="muted">
                                                                do
                                                                <DateInput
                                                                    className="bi-input"
                                                                    style={{ marginLeft: 4 }}
                                                                    value={ex.valid_to ?? ''}
                                                                    onChange={(iso) => {
                                                                        const next = [...(draft.exceptions ?? [])];
                                                                        next[idx] = { ...ex, valid_to: iso === '' ? null : iso };
                                                                        updateSegmentRule(r.product.id, { exceptions: next });
                                                                    }}
                                                                />
                                                            </label>
                                                            <input
                                                                className="bi-input"
                                                                type="number"
                                                                step="0.01"
                                                                placeholder="%"
                                                                style={{ width: 72 }}
                                                                value={ex.pct_on_spot ?? ''}
                                                                onChange={(e) => {
                                                                    const next = [...(draft.exceptions ?? [])];
                                                                    next[idx] = { ...ex, pct_on_spot: e.target.value === '' ? undefined : Number(e.target.value) };
                                                                    updateSegmentRule(r.product.id, { exceptions: next });
                                                                }}
                                                            />
                                                            <input
                                                                className="bi-input"
                                                                type="number"
                                                                step="0.01"
                                                                placeholder="€"
                                                                style={{ width: 72 }}
                                                                value={ex.eur_on_spot ?? ''}
                                                                onChange={(e) => {
                                                                    const next = [...(draft.exceptions ?? [])];
                                                                    next[idx] = { ...ex, eur_on_spot: e.target.value === '' ? undefined : Number(e.target.value) };
                                                                    updateSegmentRule(r.product.id, { exceptions: next });
                                                                }}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="bi-link bi-link--quiet"
                                                                onClick={() => {
                                                                    const next = (draft.exceptions ?? []).filter((_, j) => j !== idx);
                                                                    updateSegmentRule(r.product.id, { exceptions: next });
                                                                }}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        className="bi-link"
                                                        onClick={() => {
                                                            const next = [
                                                                ...(draft.exceptions ?? []),
                                                                { valid_from: new Date().toISOString().slice(0, 10), valid_to: null, pct_on_spot: 0, eur_on_spot: 0 },
                                                            ];
                                                            updateSegmentRule(r.product.id, { exceptions: next });
                                                        }}
                                                    >
                                                        + {locale === 'en' ? 'exception' : 'iznimka'}
                                                    </button>
                                                </div>
                                            </details>

                                            <details key={`${r.product.id}-lager`} open={false} style={{ marginTop: 10 }}>
                                                <summary style={{ cursor: 'pointer', fontWeight: 700 }}>
                                                    📦 {locale === 'en' ? 'Min stock per warehouse (Lager)' : 'Min lager po skladištu'}
                                                </summary>
                                                <MinStockEditor productId={r.product.id} token={token} locale={locale} />
                                            </details>
                                        </td>
                                    </tr>
                                    ) : null}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
                        </div>
                    </details>
                    );
                })}
            </div>}

            {tab === 'sim' ? (
                <div style={{ marginTop: 16 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                        <button type="button" className="compact" disabled={busy} onClick={() => void runSimulate()}>
                            {labels.simulate}
                        </button>
                        <button type="button" className="secondary compact" disabled={busy} onClick={() => void applyRevision()}>
                            {labels.apply}
                        </button>
                    </div>
                    {simResult ? (
                        <div style={{ fontSize: '0.85rem' }}>
                            <p className="muted" style={{ marginBottom: 8 }}>
                                Σ {locale === 'en' ? 'all channels' : 'svi kanali'} — 30d: {simResult.total_delta_eur['30']?.toFixed(2) ?? '0'} · 90d:{' '}
                                {simResult.total_delta_eur['90']?.toFixed(2) ?? '0'} · 180d: {simResult.total_delta_eur['180']?.toFixed(2) ?? '0'} EUR
                            </p>
                            {simResult.by_segment_delta_eur ? (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                                    {segmentKeys.map((s) => {
                                        const bySeg = simResult.by_segment_delta_eur!;

                                        return (
                                            <div key={s} className="card" style={{ padding: '10px 12px' }}>
                                                <strong>{segmentLabel(s, locale)}</strong>
                                                <div className="muted" style={{ marginTop: 6 }}>
                                                    30d: {bySeg[s]?.['30']?.toFixed(2) ?? '0'}
                                                    <br />
                                                    90d: {bySeg[s]?.['90']?.toFixed(2) ?? '0'}
                                                    <br />
                                                    180d: {bySeg[s]?.['180']?.toFixed(2) ?? '0'} EUR
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : null}
                            <p className="muted" style={{ marginTop: 10 }}>
                                {locale === 'en'
                                    ? `Δ columns: retail impact for "${segmentLabel(activeSegment, locale)}" only. Buyback ladder is informational.`
                                    : `Δ stupci: utjecaj na prodajnu cijenu samo za kanal "${segmentLabel(activeSegment, locale)}". Otkup je informativan.`}
                            </p>
                        </div>
                    ) : null}
                </div>
            ) : (
                <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <button type="button" className="compact" disabled={busy} onClick={() => void applyRevision()}>
                        {locale === 'en' ? '💾 Save pricing revision' : '💾 Spremi reviziju cijena'}
                    </button>
                    <button
                        type="button"
                        className="secondary compact"
                        onClick={() => { if (token) void downloadFile('/gold-cm/cjenik/export.xlsx', token, `cjenik-${new Date().toISOString().slice(0,10)}.xlsx`); }}
                        title={locale === 'en' ? 'Full XLS — Parameters sheet (spot) + per-segment sheets with formulas. Change spot in Parameters → all sheets recalc.' : 'Pun XLS — sheet Parametri (spot) + sheet po segmentu s formulama. Promijeniš spot u Parametrima → sve recalculira.'}
                    >
                        ⬇ {locale === 'en' ? 'XLS export cjenika' : 'XLS export cjenika'}
                    </button>
                    <button
                        type="button"
                        className="secondary compact"
                        onClick={() => populatePremiumEstimates(true)}
                        title={locale === 'en' ? 'Fill empty premium_eur per product with weight-based heuristic estimate (skips manually-set)' : 'Popuni prazan premium_eur per produkt s heuristikom po težini (preskače ručno postavljene)'}
                    >
                        ✨ {locale === 'en' ? 'Populate premium (empty only)' : 'Populiraj premium (samo prazne)'}
                    </button>
                    <button
                        type="button"
                        className="secondary compact"
                        onClick={() => populatePremiumEstimates(false)}
                        style={{ color: '#b14040' }}
                        title={locale === 'en' ? 'Overwrite ALL premium_eur with heuristic estimate' : 'Prepiši SVE premium_eur s heuristikom'}
                    >
                        🔁 {locale === 'en' ? 'Overwrite all premiums' : 'Prepiši sve premije'}
                    </button>
                    {populateMsg ? <span style={{ color: '#1f7a36', fontSize: '0.86rem', fontWeight: 600 }}>{populateMsg}</span> : null}
                </div>
            )}

            <div style={{ marginTop: 20 }}>
                <p className="eyebrow">{labels.recent}</p>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {recent10.map((r) => (
                        <li key={r.id}>
                            #{r.id} · {r.effective_from}
                        </li>
                    ))}
                </ul>
            </div>
            </>
            )}
        </section>
    );
}
